import React, { useState, useEffect, useRef, useMemo } from 'react';
import { BrowserRouter, useNavigate, useLocation } from 'react-router-dom';
import { UIProvider, useUI } from './context/UIContext';
import chartiqLogo from "./chartiq-logo.png";
import chartiqLogoLight from "./chartiq-logo-light.png";
import sutherlandLogo from "./output-onlinepngtools__1_-removebg-preview.png";
import symbol from "./symbol-removebg-preview.png";
import symbolLight from "./symbol_light-removebg-preview.png";

import {
  MessageSquare,
  BarChart3,
  Layout,
  Send,
  Plus,
  Save,
  Share2,
  Palette,
  X,
  Eye,
  Edit3,
  User,
  FileText,
  Download,
  Copy,
  LogOut,
  Sparkles,
  MoreHorizontal,
  PieChart,
  TrendingUp,
  Trash2,
  Upload,
  ChevronLeft,
  Layers,
  Search,
  Settings,
  Sun,
  Moon,
  Home,
  Database,
  FolderOpen,
  ChevronDown,
  ChevronUp,
  Table,
  Check,
  ChevronRight,
  Folder,
  Paperclip,
  Lightbulb,
  CheckCircle2,
  Shield,
  PanelLeftClose,
  PanelLeft,
  RefreshCw,
  Filter,
  Combine,
  Scissors,
  Shuffle,
  Globe,
  Zap,
  Bell,
  Clock,
  MessageCircle,
  Loader,
  Link,
  Image,
  ToggleLeft,
  ToggleRight
} from 'lucide-react';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  PointElement,
  ArcElement,
  RadialLinearScale,
  Filler,
  Title,
  Tooltip,
  Legend
} from 'chart.js';
import { Bar, Line, Pie, Doughnut, Scatter, Bubble, Radar, PolarArea } from 'react-chartjs-2';

import GridLayout, { Responsive } from 'react-grid-layout';
import 'react-grid-layout/css/styles.css';
import 'react-resizable/css/styles.css';

import './App.css';

// Auth imports
import { AuthProvider, useAuth } from './context/AuthContext';
import Login from './components/Login';
import ReviewSidebar from './components/ReviewSidebar';
import StatusBadge from './components/StatusBadge';
import AdminPanel from './components/AdminPanel';
import PublicDashboard from './components/PublicDashboard';
import StyleChartModal from './components/StyleChartModal';
import ViewDataModal from './components/ViewDataModal';
import DataCombineModal from './components/DataCombineModal';
import DataTransformModal from './components/DataTransformModal';
import DataRestructureModal from './components/DataRestructureModal';

// Register ChartJS components
import html2canvas from 'html2canvas';
import ChartDataLabels from 'chartjs-plugin-datalabels';
ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  PointElement,
  ArcElement,
  RadialLinearScale,
  Filler,
  Title,
  Tooltip,
  Legend,
  ChartDataLabels
);

ChartJS.defaults.plugins.datalabels = {
  display: false
};

// Custom WidthProvider to handle container resizing reliably
function WidthProvider(ComposedComponent) {
  return function WidthProviderWrapper(props) {
    const [width, setWidth] = useState(0);
    const elementRef = useRef(null);

    useEffect(() => {
      const element = elementRef.current;
      if (!element) return;

      const resizeObserver = new ResizeObserver((entries) => {
        if (entries[0] && entries[0].contentRect) {
          const newWidth = Math.floor(entries[0].contentRect.width);
          if (newWidth > 0) {
            setWidth(newWidth);
          }
        }
      });

      resizeObserver.observe(element);

      const syncWidth = () => {
        if (element && element.offsetWidth > 0) {
          setWidth(element.offsetWidth);
        }
      };

      syncWidth();
      const timer = setTimeout(syncWidth, 200);

      return () => {
        resizeObserver.disconnect();
        clearTimeout(timer);
      };
    }, []);

    const containerStyle = {
      ...props.style,
      width: '100%',
      minHeight: '1px',
      position: 'relative'
    };

    return (
      <div ref={elementRef} className={props.className} style={containerStyle}>
        {width > 0 ? <ComposedComponent {...props} width={width} /> : null}
      </div>
    );
  };
}

const ResponsiveGridLayout = WidthProvider(GridLayout);

const isProd = process.env.NODE_ENV === 'production';
const API_BASE = process.env.REACT_APP_API_BASE || (isProd ? '' : 'http://localhost:4000/api');
if (isProd && !API_BASE) {
  throw new Error('REACT_APP_API_BASE must be set in production');
}


// Main App Component (requires authentication)
// Map URL paths to tab names and vice versa
const TAB_ROUTES = {
  '/': 'overview',
  '/overview': 'overview',
  '/dashboard': 'dashboard',
  '/dashboards': 'dashboard',
  '/inventory': 'inventory',
  '/charts': 'inventory',
  '/explore': 'explore',
  '/chat': 'chat',
  '/settings': 'settings',
  '/profile': 'profile',
};

const ROUTE_FOR_TAB = {
  'overview': '/overview',
  'dashboard': '/dashboards',
  'inventory': '/charts',
  'explore': '/explore',
  'chat': '/chat',
  'settings': '/settings',
  'profile': '/profile',
};

function MainApp() {
  const { user, logout, loading, updateProfile } = useAuth();
  const { showToast, showConfirm } = useUI();
  const navigate = useNavigate();
  const location = useLocation();

  // Derive initial tab from URL, unless just logged in (then use role-based default)
  const initialTab = useMemo(() => {
    const justLoggedIn = sessionStorage.getItem('just_logged_in');
    if (justLoggedIn) {
      sessionStorage.removeItem('just_logged_in');
      const role = user?.role || 'user';
      if (role === 'viewer' || role === 'builder') {
        return 'dashboard';
      }
      return 'overview';
    }
    return TAB_ROUTES[location.pathname] || 'overview';
  }, []); // Only on mount

  const [activeTab, setActiveTabState] = useState(initialTab);

  // On mount after fresh login, update URL to match the role-based default tab
  useEffect(() => {
    const route = ROUTE_FOR_TAB[initialTab];
    if (route && location.pathname !== route) {
      navigate(route, { replace: true });
    }
  }, []); // Only on mount

  // Wrapper that also updates the URL
  const setActiveTab = (tab) => {
    setActiveTabState(tab);
    const route = ROUTE_FOR_TAB[tab];
    if (route && location.pathname !== route) {
      navigate(route, { replace: false });
    }
  };

  // Sync tab when browser back/forward is used
  useEffect(() => {
    const tab = TAB_ROUTES[location.pathname];
    if (tab && tab !== activeTab) {
      setActiveTabState(tab);
    }
  }, [location.pathname]);

  // Logout handler: navigate to root first, then logout
  const handleLogout = async () => {
    navigate('/', { replace: true });
    await logout();
  };

  const [stats, setStats] = useState(null);
  const chartRef = React.useRef(null);
  const chatViewportRef = useRef(null);
  const copyMenuRef = useRef(null);
  const isLayoutDraggingRef = useRef(false);

  // Profile Editing State
  const [isEditingName, setIsEditingName] = useState(false);
  const [tempUserName, setTempUserName] = useState('');
  const [message, setMessage] = useState('');
  const [messages, setMessages] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [currentChart, setCurrentChart] = useState(null);
  const [savedCharts, setSavedCharts] = useState([]);
  const [dashboards, setDashboards] = useState([]);
  const [selectedDashboard, setSelectedDashboard] = useState(null);
  const [dashboardLayout, setDashboardLayout] = useState([]); // Array of layout objects {i, x, y, w, h}
  const [showStyleEditor, setShowStyleEditor] = useState(false);
  const [showDashboardModal, setShowDashboardModal] = useState(false);
  const [newDashboardTitle, setNewDashboardTitle] = useState('');
  const [newDashboardDesc, setNewDashboardDesc] = useState('');
  const [dashboardMode, setDashboardMode] = useState('view'); // 'view' or 'edit'
  const [selectedInventoryChart, setSelectedInventoryChart] = useState(null);
  const [showAddFromInventoryModal, setShowAddFromInventoryModal] = useState(false);
  const [showSummaryModal, setShowSummaryModal] = useState(false);
  const [dashboardSummary, setDashboardSummary] = useState(null);
  const [isGeneratingSummary, setIsGeneratingSummary] = useState(false);
  const [showAdminPanel, setShowAdminPanel] = useState(false);
  const [showReviewSidebar, setShowReviewSidebar] = useState(false);
  const [reviewAsset, setReviewAsset] = useState(null); // { id: 1, type: 'dashboard' }
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [targetWorkspaceId, setTargetWorkspaceId] = useState('');

  // Reviewer Selection State
  const [showReviewerSelectModal, setShowReviewerSelectModal] = useState(false);
  const [potentialReviewers, setPotentialReviewers] = useState([]);
  const [selectedReviewerId, setSelectedReviewerId] = useState('');

  // Password Modal State (after publish)
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [dashboardPassword, setDashboardPassword] = useState(null); // Current password to show owner
  const [passwordCopied, setPasswordCopied] = useState(false);
  const [isRegeneratingPassword, setIsRegeneratingPassword] = useState(false);

  // Dashboard Interactive Filters (Power BI style)
  const [dashboardFilters, setDashboardFilters] = useState({}); // Global Slicers { "Region": "East" }
  const [interactiveFilters, setInteractiveFilters] = useState({}); // Cross-filters from clicks { "Region": "East" }

  // Notifications
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [showNotificationPanel, setShowNotificationPanel] = useState(false);

  // Inventory Filters
  const [inventorySearch, setInventorySearch] = useState('');
  const [inventoryFilterType, setInventoryFilterType] = useState('all');
  const [inventorySortOrder, setInventorySortOrder] = useState('newest');

  // Explore Data state
  const [availableTables, setAvailableTables] = useState([]);
  const [selectedTables, setSelectedTables] = useState([]);
  const [exploredData, setExploredData] = useState(null);
  const [dataSearch, setDataSearch] = useState('');
  const [isLoadingData, setIsLoadingData] = useState(false);
  const [dataFilters, setDataFilters] = useState({});
  const [selectedColumns, setSelectedColumns] = useState([]);
  const [joinConfig, setJoinConfig] = useState([]);
  const [showCreateChartModal, setShowCreateChartModal] = useState(false);
  const [showUploadPanel, setShowUploadPanel] = useState(false);
  const [dashboardSearch, setDashboardSearch] = useState('');

  // Viewer Dashboard View State - persist selection in localStorage
  const [viewerSelectedDashboardId, setViewerSelectedDashboardId] = useState(() => {
    try {
      const stored = localStorage.getItem(`viewer_dashboard_${user?.id}`);
      return stored ? Number(stored) : null;
    } catch { return null; }
  });
  const [viewerDashboardData, setViewerDashboardData] = useState(null);
  const [viewerDashboardLoading, setViewerDashboardLoading] = useState(false);
  const [viewerDashboardLayout, setViewerDashboardLayout] = useState([]);

  // Data Table Sorting & Pagination
  const [dataSortConfig, setDataSortConfig] = useState({ key: null, direction: 'asc' });
  const [dataCurrentPage, setDataCurrentPage] = useState(1);
  const [dataRowsPerPage, setDataRowsPerPage] = useState(100);
  const [columnFilters, setColumnFilters] = useState({}); // { colName: { type: 'contains', value: '' } }
  const [activeFilterColumn, setActiveFilterColumn] = useState(null);

  // Sidebar Expansion
  const [isInventoryExpanded, setIsInventoryExpanded] = useState(false);
  const [isDatabaseExpanded, setIsDatabaseExpanded] = useState(false);
  const [expandedSources, setExpandedSources] = useState({ main: true }); // { sourceId: bool }
  const [activeConnectionId, setActiveConnectionId] = useState(null); // null = main DB
  const [viewOnlyChart, setViewOnlyChart] = useState(null);
  const [dataLabelsMap, setDataLabelsMap] = useState(() => {
    try { const stored = localStorage.getItem('dataLabelsMap'); return stored ? JSON.parse(stored) : {}; } catch { return {}; }
  });
  const toggleDataLabel = (chartId) => {
    setDataLabelsMap(prev => {
      const updated = { ...prev, [chartId]: !prev[chartId] };
      try { localStorage.setItem('dataLabelsMap', JSON.stringify(updated)); } catch { }
      return updated;
    });
  };
  const [showViewDataModal, setShowViewDataModal] = useState(false);
  const [viewDataContent, setViewDataContent] = useState(null);

  // Sharing State
  const [chartViewTab, setChartViewTab] = useState('my'); // 'my' or 'shared'
  const [dashboardViewTab, setDashboardViewTab] = useState('my'); // 'my' or 'shared'
  const [sharedCharts, setSharedCharts] = useState([]);
  const [sharedDashboards, setSharedDashboards] = useState([]);
  const [showShareModal, setShowShareModal] = useState(null); // { type: 'chart'|'dashboard', item: {...} }
  const [shareSearchQuery, setShareSearchQuery] = useState('');
  const [shareSearchResults, setShareSearchResults] = useState([]);
  const [sharedWithUsers, setSharedWithUsers] = useState([]);
  const [isShareSearching, setIsShareSearching] = useState(false);

  // Data Combine State
  const [showCombineModal, setShowCombineModal] = useState(false);
  const [showTransformModal, setShowTransformModal] = useState(false);
  const [showRestructureModal, setShowRestructureModal] = useState(false);

  // Data Chat State (Explore tab conversational AI)
  const [dataChatOpen, setDataChatOpen] = useState(false);
  const [dataChatMessage, setDataChatMessage] = useState('');
  const [dataChatMessages, setDataChatMessages] = useState([]);
  const [isDataChatLoading, setIsDataChatLoading] = useState(false);

  // Sorting State
  const [isEditingTitle, setIsEditingTitle] = useState(false);
  const [tempChartTitle, setTempChartTitle] = useState('');
  // Dashboard title editing
  const [isEditingDashTitle, setIsEditingDashTitle] = useState(false);
  const [tempDashTitle, setTempDashTitle] = useState('');
  const [activeChartTypeMenu, setActiveChartTypeMenu] = useState(null); // Chart ID for open menu
  const [showCopyMenu, setShowCopyMenu] = useState(false);
  // Handle click outside for Menus (Chart Type & Copy Menu)
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (activeChartTypeMenu) {
        setActiveChartTypeMenu(null);
      }
      // Check if click is outside the copy menu (and not on the toggle button itself)
      if (showCopyMenu && copyMenuRef.current && !copyMenuRef.current.contains(e.target)) {
        setShowCopyMenu(false);
      }
    };

    if (activeChartTypeMenu || showCopyMenu) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [activeChartTypeMenu, showCopyMenu]);

  // Initialize dashboard layout
  useEffect(() => {
    if (selectedDashboard) {
      // Ensure charts have string IDs for react-grid-layout
      // Check if existing layout is valid and matches current charts
      // If charts were added/removed, we might need to reconcile
      const currentChartIds = (selectedDashboard.charts || []).map(c => c.id.toString());

      let initialLayout = [];
      if (selectedDashboard.layout && Array.isArray(selectedDashboard.layout) && selectedDashboard.layout.length > 0) {
        // Filter layout items that still exist in charts
        initialLayout = selectedDashboard.layout.filter(l => currentChartIds.includes(l.i));

        // Add new charts that aren't in layout yet
        const existingLayoutIds = initialLayout.map(l => l.i);
        const newCharts = selectedDashboard.charts.filter(c => !existingLayoutIds.includes(c.id.toString()));

        newCharts.forEach((chart, i) => {
          initialLayout.push({
            i: chart.id.toString(),
            x: (existingLayoutIds.length + i) % 2 * 6,
            y: 1000, // Puts it at the bottom fallback
            w: 6,
            h: 10,
            minW: 3,
            minH: 6
          });
        });
      } else if (selectedDashboard.charts && selectedDashboard.charts.length > 0) {
        // Create default layout from scratch
        initialLayout = selectedDashboard.charts.map((chart, i) => ({
          i: chart.id.toString(),
          x: (i % 2) * 6,
          y: Math.floor(i / 2) * 10,
          w: 6,
          h: 10,
          minW: 3,
          minH: 6
        }));
      }
      setDashboardLayout(initialLayout);
    }
  }, [selectedDashboard?.id, selectedDashboard?.charts?.length]); // removed selectedDashboard.layout dep to prevent loops if we update it locally

  const onLayoutChange = (layout) => {
    // Only update state if we are in edit mode to prevent auto-reflows from persisting
    if (dashboardMode === 'edit') {
      setDashboardLayout(layout);
    }
  };

  const saveLayout = async () => {
    if (!selectedDashboard) return;
    try {
      const token = localStorage.getItem('authToken');
      const res = await fetch(`${API_BASE}/dashboards/${selectedDashboard.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ layout: dashboardLayout })
      });
      if (res.ok) {
        showToast('Layout saved successfully!', 'success');
        // Update local state to reflect saved layout
        setSelectedDashboard(prev => ({ ...prev, layout: dashboardLayout }));
      } else {
        showToast('Failed to save layout', 'error');
      }
    } catch (error) {
      console.error('Save layout error:', error);
      showToast('Error saving layout', 'error');
    }
  };

  const cancelLayoutEdit = () => {
    if (selectedDashboard) {
      const currentChartIds = (selectedDashboard.charts || []).map(c => c.id.toString());
      let initialLayout = [];
      if (selectedDashboard.layout && Array.isArray(selectedDashboard.layout) && selectedDashboard.layout.length > 0) {
        initialLayout = selectedDashboard.layout.filter(l => currentChartIds.includes(l.i));
        const existingLayoutIds = initialLayout.map(l => l.i);
        const newCharts = selectedDashboard.charts.filter(c => !existingLayoutIds.includes(c.id.toString()));
        newCharts.forEach((chart, i) => {
          initialLayout.push({
            i: chart.id.toString(),
            x: (existingLayoutIds.length + i) % 2 * 6,
            y: 1000,
            w: 6,
            h: 10,
            minW: 3,
            minH: 6
          });
        });
      } else if (selectedDashboard.charts && selectedDashboard.charts.length > 0) {
        initialLayout = selectedDashboard.charts.map((chart, i) => ({
          i: chart.id.toString(),
          x: (i % 2) * 6,
          y: Math.floor(i / 2) * 10,
          w: 6,
          h: 10,
          minW: 3,
          minH: 6
        }));
      }
      setDashboardLayout(initialLayout);
    }
    setDashboardMode('view');
  };

  const handleDataSort = (key) => {
    let direction = 'asc';
    if (dataSortConfig.key === key && dataSortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setDataSortConfig({ key, direction });
  };

  const handleExportData = async () => {
    if (!exploredData || !exploredData.data || selectedTables.length === 0) return;

    showToast('Exporting all data... please wait', 'info');

    try {
      // Fetch ALL data from server (no row limit) for a complete export
      const res = await fetch(`${API_BASE}/explore/data`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          tables: selectedTables,
          joins: joinConfig,
          filters: dataFilters,
          columns: selectedColumns.length > 0 ? selectedColumns : null,
          limit: 'all'
        })
      });

      const fullData = await res.json();
      if (!res.ok || !fullData.data) {
        showToast('Export failed', 'error');
        return;
      }

      const headers = fullData.columns.map(c => `"${c.name}"`).join(',');
      const rows = fullData.data.map(row =>
        fullData.columns.map(col => {
          const val = row[col.name];
          if (val === null || val === undefined) return '""';
          const strVal = typeof val === 'object' ? JSON.stringify(val) : String(val);
          return `"${strVal.replace(/"/g, '""')}"`;
        }).join(',')
      ).join('\n');

      const blob = new Blob([headers + '\n' + rows], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${(selectedTables[0] || 'data')}_export.csv`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

      showToast(`Exported ${fullData.rowCount.toLocaleString()} rows`, 'success');
    } catch (error) {
      console.error('Export error:', error);
      showToast('Error exporting data', 'error');
    }
  };

  // Theme state
  const [theme, setTheme] = useState(() => {
    return localStorage.getItem('chartiq-theme') || 'light';
  });

  // Toggle theme function
  const toggleTheme = () => {
    const newTheme = theme === 'light' ? 'dark' : 'light';
    setTheme(newTheme);
    localStorage.setItem('chartiq-theme', newTheme);
    document.documentElement.setAttribute('data-theme', newTheme);
  };

  // Apply theme on mount
  useEffect(() => {
    document.documentElement.setAttribute('data-theme', theme);
  }, [theme]);

  // RBAC Permissions
  const userRole = user?.role || 'user';
  const isSuperAdmin = userRole === 'super_admin' || userRole === 'admin';
  const isBuilder = userRole === 'builder' || userRole === 'workspace_admin' || isSuperAdmin;
  const isViewer = userRole === 'viewer';

  // Redirect viewer/builder to dashboard if they try to access restricted tabs
  useEffect(() => {
    if (isViewer) {
      if (activeTab === 'overview' || activeTab === 'chat' || activeTab === 'explore') {
        setActiveTab('dashboard');
      }
      if (dashboardViewTab !== 'shared') {
        setDashboardViewTab('shared');
      }
      if (chartViewTab !== 'shared') {
        setChartViewTab('shared');
      }
    } else if (userRole === 'builder') {
      if (activeTab === 'overview') {
        setActiveTab('dashboard');
      }
    }
  }, [userRole, activeTab, isViewer, dashboardViewTab, chartViewTab]);

  // Viewer: persist selected dashboard to localStorage
  useEffect(() => {
    if (isViewer && viewerSelectedDashboardId && user?.id) {
      localStorage.setItem(`viewer_dashboard_${user.id}`, viewerSelectedDashboardId);
    }
  }, [isViewer, viewerSelectedDashboardId, user?.id]);

  // Viewer: auto-load saved or first shared dashboard
  useEffect(() => {
    if (isViewer && sharedDashboards.length > 0 && !viewerSelectedDashboardId) {
      // Check if saved dashboard still exists in shared list
      const savedId = localStorage.getItem(`viewer_dashboard_${user?.id}`);
      if (savedId && sharedDashboards.some(d => d.id === Number(savedId))) {
        setViewerSelectedDashboardId(Number(savedId));
      } else {
        setViewerSelectedDashboardId(sharedDashboards[0].id);
      }
    }
  }, [isViewer, sharedDashboards]);

  // Viewer: load dashboard data when selection changes
  useEffect(() => {
    if (!isViewer || !viewerSelectedDashboardId) return;
    const loadViewerDashboard = async () => {
      try {
        setViewerDashboardLoading(true);
        const res = await fetch(`${API_BASE}/dashboards/${viewerSelectedDashboardId}`, { credentials: 'include' });
        const data = await res.json();
        // Fetch chart details
        if (data.chart_ids && data.chart_ids.length > 0) {
          let chartIds = typeof data.chart_ids === 'string' ? JSON.parse(data.chart_ids) : data.chart_ids;
          chartIds = [...new Set(chartIds)];
          const charts = [];
          for (const cid of chartIds) {
            try {
              const cRes = await fetch(`${API_BASE}/charts/${cid}`, { credentials: 'include' });
              if (cRes.ok) {
                const cData = await cRes.json();
                charts.push(cData);
              }
            } catch (e) { console.error('Error loading chart:', cid); }
          }
          data.charts = charts;
        } else {
          data.charts = [];
        }
        setViewerDashboardData(data);
        // Calculate layout
        if (data.charts && data.charts.length > 0) {
          let layout = [];
          const currentChartIds = data.charts.map(c => c.id.toString());
          if (data.layout && Array.isArray(data.layout) && data.layout.length > 0) {
            layout = data.layout.filter(l => currentChartIds.includes(l.i));
            const existingIds = layout.map(l => l.i);
            const newCharts = data.charts.filter(c => !existingIds.includes(c.id.toString()));
            newCharts.forEach((chart, i) => {
              layout.push({ i: chart.id.toString(), x: (existingIds.length + i) % 2 * 6, y: Infinity, w: 6, h: 10, minW: 3, minH: 6 });
            });
          } else {
            layout = data.charts.map((chart, i) => ({ i: chart.id.toString(), x: (i % 2) * 6, y: Math.floor(i / 2) * 10, w: 6, h: 10, minW: 3, minH: 6 }));
          }
          setViewerDashboardLayout(layout);
        } else {
          setViewerDashboardLayout([]);
        }
      } catch (error) {
        console.error('Error loading viewer dashboard:', error);
      } finally {
        setViewerDashboardLoading(false);
      }
    };
    loadViewerDashboard();
  }, [isViewer, viewerSelectedDashboardId]);

  // Chart style state
  const [chartStyle, setChartStyle] = useState({
    type: 'bar',
    title: '',
    colors: ['#3B82F6', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6'],
    theme: 'professional',
    individualColors: {}, // For custom colors per data point
    highlightIndices: []
  });


  // Load initial data
  useEffect(() => {
    loadCharts('my');
    loadCharts('shared');
    loadDashboards('my');
    loadDashboards('shared');
    if (!isViewer) {
      loadAvailableTables();
    }
  }, [userRole]);

  // Auto-scroll chat to bottom when new messages arrive
  useEffect(() => {
    if (chatViewportRef.current) {
      chatViewportRef.current.scrollTop = chatViewportRef.current.scrollHeight;
    }
  }, [messages]);










  // Color presets
  const colorPresets = {
    professional: ['#2563eb', '#10b981', '#f59e0b', '#8b5cf6', '#ec4899'],
    vibrant: ['#ef4444', '#f59e0b', '#10b981', '#3b82f6', '#8b5cf6'],
    pastel: ['#93c5fd', '#86efac', '#fde68a', '#c4b5fd', '#fbcfe8'],
    dark: ['#1e40af', '#065f46', '#92400e', '#5b21b6', '#9f1239'],
    ocean: ['#0ea5e9', '#06b6d4', '#14b8a6', '#10b981', '#22c55e'],
    sunset: ['#f97316', '#fb923c', '#fdba74', '#fcd34d', '#fde047'],
    forest: ['#065f46', '#047857', '#059669', '#10b981', '#34d399'],
    purple: ['#581c87', '#6b21a8', '#7c3aed', '#8b5cf6', '#a78bfa']
  };

  useEffect(() => {
    loadStats();
    loadCharts('my');
    loadDashboards('my');
    loadAvailableTables();
  }, []);

  const loadStats = async () => {
    try {
      const res = await fetch(`${API_BASE}/stats`);
      const data = await res.json();
      setStats(data);
    } catch (error) {
      console.error('Error loading stats:', error);
    }
  };

  const [isRefreshing, setIsRefreshing] = useState(false);

  const loadAvailableTables = async (forceRefresh = false) => {
    try {
      if (forceRefresh) setIsRefreshing(true);
      const url = forceRefresh ? `${API_BASE}/explore/tables?refresh=true` : `${API_BASE}/explore/tables`;
      const res = await fetch(url, { credentials: 'include' });
      const data = await res.json();
      setAvailableTables(data.tables || []);
    } catch (error) {
      console.error('Error loading tables:', error);
    } finally {
      if (forceRefresh) setIsRefreshing(false);
    }
  };

  const fetchNotifications = async () => {
    try {
      const token = localStorage.getItem('authToken');
      // If token not found, rely on cookie credentials via fetch below
      const headers = {};
      if (token) headers['Authorization'] = `Bearer ${token}`;

      const res = await fetch(`${API_BASE}/notifications`, {
        headers,
        credentials: 'include'
      });
      if (res.ok) {
        const data = await res.json();
        if (Array.isArray(data)) {
          setNotifications(data);
          setUnreadCount(data.filter(n => !n.is_read).length);
        }
      }
    } catch (e) {
      console.error("Notification fetch error", e);
    }
  };

  // Notification Polling
  useEffect(() => {
    if (user) {
      fetchNotifications();
      const interval = setInterval(fetchNotifications, 30000); // 30s
      return () => clearInterval(interval);
    }
  }, [user]);

  const handleNotificationClick = async (notif) => {
    // Mark Read
    try {
      const token = localStorage.getItem('authToken');
      const headers = { 'Content-Type': 'application/json' };
      if (token) headers['Authorization'] = `Bearer ${token}`;

      await fetch(`${API_BASE}/notifications/${notif.id}/read`, {
        method: 'POST',
        headers,
        credentials: 'include'
      });
      fetchNotifications(); // Refresh count
    } catch (e) { }

    // Navigate
    if (notif.link) {
      if (notif.link.startsWith('/dashboard/')) {
        const id = notif.link.split('/')[2];
        if (isViewer) {
          setViewerSelectedDashboardId(Number(id));
          setSelectedDashboard(null);
          setActiveTab('dashboard');
        } else {
          loadDashboard(id);
        }
      }
    }
    setShowNotificationPanel(false);
  };

  const handleMarkAllRead = async () => {
    try {
      const token = localStorage.getItem('authToken');
      const headers = { 'Content-Type': 'application/json' };
      if (token) headers['Authorization'] = `Bearer ${token}`;

      await fetch(`${API_BASE}/notifications/mark-all-read`, {
        method: 'POST',
        headers,
        credentials: 'include'
      });
      fetchNotifications();
      showToast('All notifications marked as read', 'success');
    } catch (e) {
      console.error("Error marking all read:", e);
    }
  };

  const handleFileUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    const formData = new FormData();
    formData.append('file', file);

    // Optional: prompt for table name
    // const tableName = prompt("Enter a name for the table (optional):");
    // if (tableName) formData.append('tableName', tableName);

    try {
      setIsLoadingData(true);
      const res = await fetch(`${API_BASE}/upload-excel`, {
        method: 'POST',
        credentials: 'include',
        body: formData
      });

      const data = await res.json();

      if (res.ok) {
        showToast(`Successfully uploaded and created table: ${data.tableName}`, 'success');
        loadAvailableTables(); // Refresh list
      } else {
        showToast(data.error || 'Upload failed', 'error');
      }
    } catch (error) {
      console.error('Error uploading file:', error);
      showToast('Error uploading file', 'error');
    } finally {
      setIsLoadingData(false);
      // Reset input
      event.target.value = '';
    }
  };

  const deleteTable = async (tableName, e) => {
    e.stopPropagation(); // Prevent toggling selection
    e.preventDefault();

    const confirmed = await showConfirm({
      title: 'Delete Table',
      message: `Are you sure you want to delete the table "${tableName}"? This action cannot be undone.`,
      confirmText: 'Delete',
      cancelText: 'Keep'
    });

    if (!confirmed) {
      return;
    }

    try {
      const res = await fetch(`${API_BASE}/tables/${tableName}`, {
        method: 'DELETE',
        credentials: 'include'
      });

      if (res.ok) {
        showToast(`Table "${tableName}" deleted successfully`, 'success');
        // Remove from selected if it was selected
        if (selectedTables.includes(tableName)) {
          setSelectedTables(selectedTables.filter(t => t !== tableName));
        }
        loadAvailableTables();
      } else {
        const data = await res.json();
        showToast(data.error || 'Failed to delete table', 'error');
      }
    } catch (error) {
      console.error('Error deleting table:', error);
      showToast('Error deleting table', 'error');
    }
  };

  const exploreData = async (tablesOverride = null, joinsOverride = null, filtersOverride = null, colsOverride = null) => {
    const tablesToUse = Array.isArray(tablesOverride) ? tablesOverride : selectedTables;
    const joinsToUse = joinsOverride !== null ? joinsOverride : joinConfig;
    const filtersToUse = filtersOverride !== null ? filtersOverride : dataFilters;
    const colsToUse = colsOverride !== null ? colsOverride : (selectedColumns.length > 0 ? selectedColumns : null);

    if (tablesToUse.length === 0) {
      showToast('Please select at least one table', 'warning');
      return;
    }

    setIsLoadingData(true);

    try {
      const res = await fetch(`${API_BASE}/explore/data`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          tables: tablesToUse,
          joins: joinsToUse,
          filters: filtersToUse,
          columns: colsToUse,
          limit: 'all',
          connectionId: activeConnectionId
        })
      });

      const data = await res.json();

      if (res.ok) {
        setExploredData(data);
      } else {
        showToast(data.error || 'Failed to explore data', 'error');
      }
    } catch (error) {
      console.error('Error exploring data:', error);
      showToast(error.message || 'Error exploring data', 'error');
    } finally {
      setIsLoadingData(false);
    }
  };

  const createChartFromExplore = async (chartConfig) => {
    try {
      const res = await fetch(`${API_BASE}/explore/create-chart`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          data: exploredData.data,
          ...chartConfig
        })
      });

      const data = await res.json();

      if (res.ok) {
        setCurrentChart({
          data: data.data,
          type: data.chartType,
          title: data.title,
          dataMapping: data.dataMapping,
          sql: exploredData.sql
        });

        setChartStyle({
          type: data.chartType,
          title: data.title,
          colors: colorPresets.professional,
          theme: 'professional',
          individualColors: {},
          highlightIndices: []
        });

        setShowCreateChartModal(false);
        setActiveTab('chat'); // Switch to chart view
      } else {
        showToast(`Error: ${data.error}`, 'error');
      }
    } catch (error) {
      console.error('Error creating chart:', error);
      showToast(error.message || 'Error creating chart', 'error');
    }
  };

  const loadCharts = async (filter) => {
    try {
      const f = filter || 'my';
      const res = await fetch(`${API_BASE}/charts?filter=${f}`, { credentials: 'include' });
      const charts = await res.json();

      if (!Array.isArray(charts)) {
        console.error('Expected array for charts, got:', charts);
        if (f === 'shared') setSharedCharts([]); else setSavedCharts([]);
        return;
      }

      // Fetch data for each chart for preview
      const chartsWithData = await Promise.all(
        charts.map(async (chart) => {
          try {
            const dataRes = await fetch(`${API_BASE}/charts/${chart.id}`, { credentials: 'include' });
            const data = await dataRes.json();

            if (dataRes.ok) {
              return { ...data, is_shared: chart.is_shared, owner_name: chart.owner_name, owner_email: chart.owner_email };
            } else {
              console.warn(`Data load failed for chart ${chart.id}:`, data.error);
              return { ...chart, dataError: data.error };
            }
          } catch (error) {
            console.error(`Error loading data for chart ${chart.id}:`, error);
            return chart;
          }
        })
      );

      if (f === 'shared') {
        setSharedCharts(chartsWithData);
      } else {
        setSavedCharts(chartsWithData);
      }
    } catch (error) {
      console.error('Error loading charts:', error);
      if (filter === 'shared') setSharedCharts([]); else setSavedCharts([]);
    }
  };

  const loadDashboards = async (filter) => {
    try {
      const f = filter || 'my';
      const res = await fetch(`${API_BASE}/dashboards?filter=${f}`, { credentials: 'include' });
      const data = await res.json();
      if (Array.isArray(data)) {
        if (f === 'shared') {
          setSharedDashboards(data);
        } else {
          setDashboards(data);
        }
      } else {
        console.error('Expected array for dashboards, got:', data);
        if (f === 'shared') setSharedDashboards([]); else setDashboards([]);
      }
    } catch (error) {
      console.error('Error loading dashboards:', error);
      if (filter === 'shared') setSharedDashboards([]); else setDashboards([]);
    }
  };

  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (!message.trim() || isLoading) return;

    const userMsg = { role: 'user', content: message };
    setMessages([...messages, userMsg]);
    const userMessage = message;
    setMessage('');
    setIsLoading(true);

    try {
      // Build request body - include chart context for modifications
      const requestBody = { message: userMessage };
      if (currentChart && currentChart.sql) {
        requestBody.context = {
          mode: 'modify',
          currentChart: {
            sql: currentChart.sql,
            chartType: chartStyle.type || currentChart.type || currentChart.chart_type || 'bar',
            title: chartStyle.title || currentChart.title || '',
            dataMapping: currentChart.dataMapping || currentChart.data_mapping || {}
          }
        };
      }

      const res = await fetch(`${API_BASE}/chat`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(requestBody)
      });

      const data = await res.json();

      if (!res.ok || data.error) {
        throw new Error(data.error || 'Server error');
      }

      const hasData = data.data && data.data.length > 0;

      setMessages(prev => [...prev, {
        role: 'assistant',
        content: hasData
          ? data.explanation
          : (data.explanation || '') + '\n\n⚠️ The query returned no results. The data might not exist for this filter/condition. Try a different query.',
        sql: data.sql,
        suggestedFollowUps: hasData ? (data.suggestedFollowUps || []) : []
      }]);

      if (hasData) {
        setCurrentChart(prev => ({
          ...(prev || {}),
          data: data.data,
          type: data.chartType,
          title: data.title,
          dataMapping: data.dataMapping,
          sql: data.sql
        }));
      } else {
        // Keep previous chart visible but update SQL for debugging
        setCurrentChart(prev => prev ? ({ ...prev, sql: data.sql }) : null);
      }

      if (hasData) {
        // Apply style preferences from AI or use defaults
        const stylePrefs = data.stylePreferences || {};
        const selectedTheme = stylePrefs.theme || 'professional';
        const themeColors = stylePrefs.colors || colorPresets[selectedTheme] || colorPresets.professional;

        setChartStyle({
          type: data.chartType,
          title: data.title,
          colors: themeColors,
          theme: selectedTheme,
          individualColors: stylePrefs.customColors || {},
          highlightIndices: stylePrefs.highlightIndices || []
        });
      }

    } catch (error) {
      console.error('Error:', error);
      setMessages(prev => [...prev, {
        role: 'assistant',
        content: `Error: ${error.message}`
      }]);
    } finally {
      setIsLoading(false);
    }
  };

  // Data Chat - Conversational data exploration in Explore tab
  const handleDataChatSend = async (e, quickMsg) => {
    if (e) e.preventDefault();
    const msg = quickMsg || dataChatMessage;
    if (!msg.trim() || isDataChatLoading) return;

    const userMsg = { role: 'user', content: msg };
    setDataChatMessages(prev => [...prev, userMsg]);
    setDataChatMessage('');
    setIsDataChatLoading(true);

    try {
      const res = await fetch(`${API_BASE}/data/chat`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          message: msg,
          tables: selectedTables,
          currentColumns: exploredData?.columns?.map(c => c.name) || [],
          currentFilters: columnFilters,
          connectionId: activeConnectionId
        })
      });

      const data = await res.json();

      if (!res.ok || data.error) {
        throw new Error(data.error || 'Server error');
      }

      setDataChatMessages(prev => [...prev, {
        role: 'assistant',
        content: data.explanation,
        sql: data.sql,
        rowCount: data.rowCount,
        suggestedFollowUps: data.suggestedFollowUps || []
      }]);

      // Update the explored data table with AI query results
      if (data.data && data.columns) {
        setExploredData({
          data: data.data,
          columns: data.columns,
          sql: data.sql,
          rowCount: data.rowCount
        });
        setDataCurrentPage(1);
        setColumnFilters({});
        setDataSortConfig({ key: null, direction: 'asc' });
      }

    } catch (error) {
      console.error('Data chat error:', error);
      setDataChatMessages(prev => [...prev, {
        role: 'assistant',
        content: `Error: ${error.message}`
      }]);
    } finally {
      setIsDataChatLoading(false);
    }
  };

  const saveChart = async () => {
    if (!currentChart) return;

    const isUpdating = !!currentChart.id;
    const url = isUpdating ? `${API_BASE}/charts/${currentChart.id}` : `${API_BASE}/charts`;
    const method = isUpdating ? 'PUT' : 'POST';

    try {
      const res = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          title: chartStyle.title || currentChart.title,
          sql: currentChart.sql,
          chart_type: chartStyle.type,
          data_mapping: currentChart.dataMapping || currentChart.data_mapping,
          colors: chartStyle.colors,
          filters: {}
        })
      });

      if (res.ok) {
        const savedData = await res.json();
        showToast(isUpdating ? 'Chart updated successfully!' : 'Chart saved to inventory!', 'success');

        // Update current chart with the returned data (includes ID if it's a new save)
        // This ensures subsequent saves are UPDATES
        setCurrentChart(prev => ({ ...prev, ...savedData }));

        loadCharts('my');
      } else {
        const data = await res.json();
        showToast(`Error: ${data.error || 'Unknown error'}`, 'error');
      }
    } catch (error) {
      console.error('Error saving chart:', error);
      showToast(error.message || 'Error saving chart', 'error');
    }
  };

  const createDashboard = async () => {
    // Validation
    if (!newDashboardTitle || !newDashboardTitle.trim()) {
      showToast('Please enter a dashboard name', 'error');
      return;
    }

    try {
      const res = await fetch(`${API_BASE}/dashboards`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          title: newDashboardTitle.trim(),
          description: newDashboardDesc.trim(),
          workspaceId: targetWorkspaceId
        })
      });

      if (res.ok) {
        const newDashboard = await res.json();
        console.log('Dashboard created:', newDashboard);

        // Success actions
        showToast(`Dashboard "${newDashboardTitle}" created!`, 'success');
        await loadDashboards('my');

        // Reset and close modal
        setNewDashboardTitle('');
        setNewDashboardDesc('');
        setShowDashboardModal(false);
      } else {
        const error = await res.json();
        console.error('Failed to create dashboard:', error);
        showToast(`Failed to create dashboard: ${error.error || 'Unknown error'}`, 'error');
      }
    } catch (error) {
      console.error('Error creating dashboard:', error);
      showToast(`Error creating dashboard: ${error.message}`, 'error');
    }
  };

  const addChartToDashboard = async (dashboardId) => {
    if (!currentChart) {
      showToast('Please create a chart first', 'error');
      return;
    }

    console.log('Adding chart to dashboard:', dashboardId);
    console.log('Current chart:', currentChart);

    try {
      // First save the chart
      const chartRes = await fetch(`${API_BASE}/charts`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          title: chartStyle.title || 'Untitled Chart',
          sql: currentChart.sql,
          chart_type: chartStyle.type,
          data_mapping: currentChart.dataMapping,
          colors: chartStyle.colors,
          filters: {}
        })
      });

      if (!chartRes.ok) {
        const error = await chartRes.json();
        throw new Error(`Failed to save chart: ${error.error || 'Unknown error'}`);
      }

      const newChart = await chartRes.json();
      console.log('Chart saved:', newChart);

      // Get current dashboard
      const dashRes = await fetch(`${API_BASE}/dashboards/${dashboardId}`);
      if (!dashRes.ok) {
        throw new Error('Failed to fetch dashboard');
      }

      const dashboard = await dashRes.json();
      console.log('Current dashboard:', dashboard);

      // Update dashboard with new chart
      const chartIds = Array.isArray(dashboard.chart_ids) ? dashboard.chart_ids : [];
      chartIds.push(newChart.id);

      const updateRes = await fetch(`${API_BASE}/dashboards/${dashboardId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          title: dashboard.title,
          description: dashboard.description || '',
          chart_ids: chartIds,
          layout: dashboard.layout || {}
        })
      });

      if (!updateRes.ok) {
        const error = await updateRes.json();
        throw new Error(`Failed to update dashboard: ${error.error || 'Unknown error'}`);
      }

      console.log('Dashboard updated successfully');
      showToast(`Chart "${newChart.title}" added to dashboard!`, 'success');
      setShowDashboardModal(false);

      // Reload dashboards to reflect changes
      await loadDashboards('my');
    } catch (error) {
      console.error('Error adding chart to dashboard:', error);
      showToast(`Error: ${error.message}`, 'error');
    }
  };

  const addInventoryChartToDashboard = async (dashboardId) => {
    if (!selectedInventoryChart) return;

    try {
      const dashRes = await fetch(`${API_BASE}/dashboards/${dashboardId}`);
      if (!dashRes.ok) throw new Error('Dashboard not found');
      const dashboard = await dashRes.json();

      let existingIds = dashboard.chart_ids || [];
      // Handle potental string format from DB
      if (typeof existingIds === 'string') {
        try { existingIds = JSON.parse(existingIds); } catch (e) { existingIds = []; }
      }

      // Check if already exists to avoid redundant updates
      if (existingIds.includes(selectedInventoryChart.id)) {
        showToast('Chart is already in this dashboard', 'info');
        setShowAddFromInventoryModal(false);
        return;
      }

      // Ensure unique IDs
      const updatedChartIds = [...new Set([...existingIds, selectedInventoryChart.id])];

      const updateRes = await fetch(`${API_BASE}/dashboards/${dashboardId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...dashboard,
          chart_ids: updatedChartIds
        })
      });

      if (!updateRes.ok) throw new Error('Failed to update dashboard');

      showToast('Chart added to dashboard!', 'success');
      setShowAddFromInventoryModal(false);

      // Refresh both the list and the active dashboard view
      await loadDashboards('my');
      if (selectedDashboard && selectedDashboard.id === dashboardId) {
        await loadDashboard(dashboardId);
      }
    } catch (error) {
      console.error('Error adding chart:', error);
      showToast(error.message, 'error');
    }
  };

  const loadDashboard = async (id) => {
    try {
      const res = await fetch(`${API_BASE}/dashboards/${id}`);
      const data = await res.json();

      // Fetch charts details if we have IDs
      if (data.chart_ids && data.chart_ids.length > 0) {
        let chartIds = typeof data.chart_ids === 'string' ? JSON.parse(data.chart_ids) : data.chart_ids;
        // Deduplicate chart IDs to prevent multiple instances
        chartIds = [...new Set(chartIds)];

        const charts = [];
        for (const cid of chartIds) {
          try {
            const cRes = await fetch(`${API_BASE}/charts/${cid}`);
            if (cRes.ok) {
              const cData = await cRes.json();
              charts.push(cData);
            }
          } catch (e) { console.error('Error loading chart details:', cid); }
        }
        data.charts = charts;
      } else {
        data.charts = [];
      }

      setSelectedDashboard(data);
      setActiveTab('dashboard');
    } catch (error) {
      console.error('Error loading dashboard:', error);
    }
  };

  const addExistingChartToDashboard = async (dashboardId, chartId) => {
    console.log('Adding existing chart to dashboard:', { dashboardId, chartId });

    try {
      // Get current dashboard
      const dashRes = await fetch(`${API_BASE}/dashboards/${dashboardId}`);
      if (!dashRes.ok) {
        throw new Error('Failed to fetch dashboard');
      }

      const dashboard = await dashRes.json();
      console.log('Current dashboard:', dashboard);

      // Check if chart already in dashboard
      const chartIds = Array.isArray(dashboard.chart_ids) ? dashboard.chart_ids : [];
      if (chartIds.includes(chartId)) {
        showToast('This chart is already in the dashboard!', 'warning');
        return;
      }

      // Add chart to dashboard
      chartIds.push(chartId);

      const updateRes = await fetch(`${API_BASE}/dashboards/${dashboardId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          title: dashboard.title,
          description: dashboard.description || '',
          chart_ids: chartIds,
          layout: dashboard.layout || {}
        })
      });

      if (!updateRes.ok) {
        const error = await updateRes.json();
        throw new Error(`Failed to update dashboard: ${error.error || 'Unknown error'}`);
      }

      console.log('Dashboard updated successfully');
      showToast('Chart added to dashboard!', 'success');
      setShowAddFromInventoryModal(false);

      // Reload dashboards
      await loadDashboards('my');
    } catch (error) {
      console.error('Error adding chart to dashboard:', error);
      showToast(`Error: ${error.message}`, 'error');
    }

  };

  const handleUpdateChartTitle = async () => {
    if (!viewOnlyChart || !tempChartTitle.trim()) return;

    try {
      const response = await fetch(`${API_BASE}/charts/${viewOnlyChart.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...viewOnlyChart,
          title: tempChartTitle
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to update chart title');
      }

      const updatedChart = await response.json();

      // Update state
      setViewOnlyChart(updatedChart);
      setSavedCharts(prev => prev.map(c => c.id === updatedChart.id ? updatedChart : c));
      setIsEditingTitle(false);

    } catch (error) {
      console.error('Error updating chart title:', error);
      showToast(error.message || 'Error updating title', 'error');
    }
  };

  const handleUpdateName = async () => {
    if (!tempUserName.trim()) return;

    try {
      const res = await updateProfile({ name: tempUserName });
      if (res.success) {
        setIsEditingName(false);
        // User state is updated by updateProfile internal call to setUser
      } else {
        showToast(res.error || 'Failed to update name', 'error');
      }
    } catch (error) {
      console.error('Update name error:', error);
      showToast('Failed to update name', 'error');
    }
  };

  const deleteChart = async (chartId) => {
    const confirmed = await showConfirm({
      title: 'Delete Chart',
      message: 'Are you sure you want to delete this chart?',
      confirmText: 'Delete',
      cancelText: 'Cancel'
    });
    if (!confirmed) return;

    try {
      const res = await fetch(`${API_BASE}/charts/${chartId}`, {
        method: 'DELETE'
      });

      if (res.ok) {
        showToast('Chart deleted!', 'success');
        await loadCharts('my');
      } else {
        const error = await res.json();
        showToast(error.error || 'Failed to delete chart', 'error');
      }
    } catch (error) {
      console.error('Error deleting chart:', error);
      showToast(error.message || 'Error deleting chart', 'error');
    }
  };

  const publishDashboard = async () => {
    if (!selectedDashboard) return;

    try {
      const res = await fetch(`${API_BASE}/dashboards/${selectedDashboard.id}/publish`, {
        method: 'PUT',
        credentials: 'include'
      });

      if (!res.ok) {
        const error = await res.json();
        showToast(error.error || 'Failed to publish', 'error', 'Publish Error');
        return;
      }

      const data = await res.json();

      // Show password modal instead of just copying link
      if (data.share_password) {
        setDashboardPassword(data.share_password);
        setShowPasswordModal(true);
      } else {
        // Fallback: copy link
        if (data.share_url) await navigator.clipboard.writeText(data.share_url).catch(() => { });
        showToast('Dashboard published!', 'success', 'Published');
      }

      setSelectedDashboard(prev => ({ ...prev, ...data }));
      loadDashboards('my');
    } catch (error) {
      console.error('Error publishing dashboard:', error);
      showToast('Failed to publish dashboard. Please try again.', 'error');
    }
  };

  // Fetch current password for already-published dashboard
  const fetchDashboardPassword = async (dashId) => {
    try {
      const res = await fetch(`${API_BASE}/dashboards/${dashId}/share-password`, {
        credentials: 'include'
      });
      if (res.ok) {
        const data = await res.json();
        setDashboardPassword(data.share_password);
      }
    } catch (e) {
      console.error('Failed to fetch dashboard password', e);
    }
  };

  // Regenerate password for dashboard
  const regenerateDashboardPassword = async () => {
    if (!selectedDashboard) return;
    setIsRegeneratingPassword(true);
    try {
      const res = await fetch(`${API_BASE}/dashboards/${selectedDashboard.id}/regenerate-password`, {
        method: 'POST',
        credentials: 'include'
      });
      if (res.ok) {
        const data = await res.json();
        setDashboardPassword(data.share_password);
        showToast('Password regenerated! Old password is now invalid.', 'success');
      } else {
        const err = await res.json();
        showToast(err.error || 'Failed to regenerate password', 'error');
      }
    } catch (e) {
      showToast('Failed to regenerate password', 'error');
    } finally {
      setIsRegeneratingPassword(false);
    }
  };

  // Copy password to clipboard
  const copyPasswordToClipboard = async () => {
    if (!dashboardPassword) return;
    try {
      await navigator.clipboard.writeText(dashboardPassword);
      setPasswordCopied(true);
      setTimeout(() => setPasswordCopied(false), 2000);
    } catch (e) {
      showToast('Failed to copy', 'error');
    }
  };


  const unpublishDashboard = async () => {
    if (!selectedDashboard) return;

    const confirmed = await showConfirm({
      title: 'Unpublish Dashboard',
      message: 'Are you sure you want to unpublish this dashboard? The share link will stop working.',
      confirmText: 'Unpublish',
      cancelText: 'Keep'
    });

    if (!confirmed) {
      return;
    }

    try {
      const res = await fetch(`${API_BASE}/dashboards/${selectedDashboard.id}/unpublish`, {
        method: 'PUT',
        credentials: 'include'
      });

      if (!res.ok) {
        const error = await res.json();
        showToast(error.error || 'Failed to unpublish', 'error');
        return;
      }

      const data = await res.json();
      showToast('Dashboard unpublished successfully.', 'success');

      setSelectedDashboard(prev => ({ ...prev, ...data }));
      loadDashboards('my');
    } catch (error) {
      console.error('Error unpublishing dashboard:', error);
      showToast('Failed to unpublish dashboard. Please try again.', 'error');
    }
  };

  const submitForReview = async () => {
    if (!selectedDashboard) return;

    // improved: If workspace dashboard or global dashboard, let user select a reviewer
    const fetchUrl = selectedDashboard.workspace_id
      ? `${API_BASE}/workspaces/${selectedDashboard.workspace_id}/potential_reviewers`
      : `${API_BASE}/organizations/${user.org_id}/potential_reviewers`;

    try {
      const token = localStorage.getItem('authToken');
      const res = await fetch(fetchUrl, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (res.ok) {
        const data = await res.json();
        // Filter out self just in case backend didn't
        setPotentialReviewers(data.filter(u => u.id !== user.id));
        setSelectedReviewerId(''); // Reset selection
        setShowReviewerSelectModal(true);
        return; // Stop here and wait for modal interaction
      } else {
        console.error("Failed to fetch potential reviewers", await res.text());
      }
    } catch (e) {
      console.error("Failed to fetch potential reviewers", e);
    }

    // Fallback logic if we couldn't get a reviewer list
    const msg = "Submit this dashboard for admin review? You won't be able to edit it while it's in review.";
    if (window.confirm(msg)) {
      confirmReviewSubmission(null);
    }
  };

  const confirmReviewSubmission = async (reviewerId) => {
    try {
      const token = localStorage.getItem('authToken');
      const headers = { 'Content-Type': 'application/json' };
      if (token) headers['Authorization'] = `Bearer ${token}`;

      const payload = {
        status: reviewerId ? 'in_review_stakeholder' : 'pending_review'
      };
      if (reviewerId) payload.reviewerId = reviewerId;

      // Use the workflow status endpoint instead of generic PUT to trigger notifications in backend
      const res = await fetch(`${API_BASE}/workflow/dashboard/${selectedDashboard.id}/status`, {
        method: 'POST',
        headers,
        credentials: 'include',
        body: JSON.stringify(payload)
      });

      if (res.ok) {
        showToast('Dashboard submitted for review!', 'success');
        // If we have a modal, closing it
        if (typeof setShowReviewerSelectModal === 'function') setShowReviewerSelectModal(false);
        await loadDashboard(selectedDashboard.id);
        await loadDashboards('my');
      } else {
        const errorData = await res.json().catch(() => ({}));
        showToast(errorData.error || 'Failed to submit dashboard', 'error');
      }
    } catch (error) {
      console.error('Error submitting dashboard:', error);
      showToast(error.message || 'Error submitting dashboard', 'error');
    }
  };

  const deleteDashboard = async () => {
    if (!selectedDashboard) return;

    const confirmed = await showConfirm({
      title: 'Delete Dashboard',
      message: `Are you sure you want to delete "${selectedDashboard.title}"? This action cannot be undone.`,
      confirmText: 'Delete',
      cancelText: 'Cancel'
    });

    if (!confirmed) {
      return;
    }

    try {
      const res = await fetch(`${API_BASE}/dashboards/${selectedDashboard.id}`, {
        method: 'DELETE',
        credentials: 'include'
      });

      if (!res.ok) {
        const errData = await res.json().catch(() => ({}));
        throw new Error(errData.error || 'Failed to delete dashboard');
      }

      showToast('Dashboard deleted successfully', 'success');
      setSelectedDashboard(null); // Clear selection
      setDashboardMode('view');
      loadDashboards('my'); // Refresh list
    } catch (error) {
      console.error('Error deleting dashboard:', error);
      showToast(error.message || 'Failed to delete dashboard', 'error');
    }
  };

  const copyShareLink = async () => {
    if (!selectedDashboard?.share_url) return;

    try {
      await navigator.clipboard.writeText(selectedDashboard.share_url);
      showToast('Share link copied to clipboard!', 'success');
    } catch (error) {
      console.error('Failed to copy:', error);
    }
  };

  // ============ SHARING FUNCTIONS ============

  const searchUsersForSharing = async (query) => {
    if (!query || query.trim().length < 2) {
      setShareSearchResults([]);
      return;
    }
    setIsShareSearching(true);
    try {
      const res = await fetch(`${API_BASE}/users/search?q=${encodeURIComponent(query)}`, { credentials: 'include' });
      const data = await res.json();
      setShareSearchResults(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('Error searching users:', error);
      setShareSearchResults([]);
    } finally {
      setIsShareSearching(false);
    }
  };

  const shareItem = async (itemType, itemId, userId) => {
    try {
      const res = await fetch(`${API_BASE}/share`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ item_type: itemType, item_id: itemId, shared_with_id: userId })
      });
      const data = await res.json();
      if (res.ok) {
        showToast('Shared successfully!', 'success');
        loadSharedWithUsers(itemType, itemId);
        setShareSearchQuery('');
        setShareSearchResults([]);
      } else {
        showToast(data.error || 'Failed to share', 'error');
      }
    } catch (error) {
      showToast('Error sharing item', 'error');
    }
  };

  const unshareItem = async (itemType, itemId, userId) => {
    try {
      const res = await fetch(`${API_BASE}/share`, {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ item_type: itemType, item_id: itemId, shared_with_id: userId })
      });
      if (res.ok) {
        showToast('Share removed', 'success');
        loadSharedWithUsers(itemType, itemId);
      }
    } catch (error) {
      showToast('Error removing share', 'error');
    }
  };

  const loadSharedWithUsers = async (itemType, itemId) => {
    try {
      const res = await fetch(`${API_BASE}/share/${itemType}/${itemId}`, { credentials: 'include' });
      const data = await res.json();
      setSharedWithUsers(Array.isArray(data) ? data : []);
    } catch (error) {
      setSharedWithUsers([]);
    }
  };

  const openShareModal = (type, item) => {
    setShowShareModal({ type, item });
    setShareSearchQuery('');
    setShareSearchResults([]);
    loadSharedWithUsers(type, item.id);
  };

  const generateDashboardSummary = async () => {
    if (!selectedDashboard) return;

    setIsGeneratingSummary(true);
    setShowSummaryModal(true);

    try {
      const res = await fetch(`${API_BASE}/dashboards/${selectedDashboard.id}/summary`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' }
      });

      if (!res.ok) {
        const error = await res.json();
        throw new Error(error.error || 'Failed to generate summary');
      }

      const data = await res.json();
      setDashboardSummary(data.summary);
      showToast('Successfully generated dashboard summary', 'success');
    } catch (error) {
      console.error('Error generating summary:', error);
      showToast(error.message || 'Error generating summary', 'error');
      setShowSummaryModal(false);
    } finally {
      setIsGeneratingSummary(false);
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text).then(() => {
      showToast('Copied to clipboard!', 'success');
    }).catch(err => {
      console.error('Failed to copy:', err);
    });
  };

  const downloadSummaryAsPDF = () => {
    // Create a simple text version for download
    if (!dashboardSummary) return;

    const text = `
DASHBOARD SUMMARY
${selectedDashboard.title}
Generated: ${new Date(dashboardSummary.generatedAt).toLocaleString()}

KEY INSIGHTS
${dashboardSummary.keyInsights.map((insight, i) => `${i + 1}. ${insight}`).join('\n')}

HIGHLIGHTS
${Object.entries(dashboardSummary.highlights).map(([key, value]) => `• ${key}: ${value}`).join('\n')}

RECOMMENDATIONS
${dashboardSummary.recommendations.map((rec, i) => `${i + 1}. ${rec}`).join('\n')}

EXECUTIVE SUMMARY
${dashboardSummary.executiveSummary}
`;

    const blob = new Blob([text], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${selectedDashboard.title.replace(/\s+/g, '_')}_Summary.txt`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const downloadDashboardImage = async () => {
    // Select the dashboard grid container
    const dashboardElement = document.querySelector('.dashboard-charts-grid-premium');
    if (!dashboardElement) return;

    try {
      showToast('Generating image...', 'info');
      // Use html2canvas to capture the element
      const canvas = await html2canvas(dashboardElement, {
        scale: 2, // Higher resolution for better quality
        useCORS: true, // Allow cross-origin images
        logging: false,
        backgroundColor: theme === 'dark' ? '#0f172a' : '#f8fafc',
        ignoreElements: (element) => element.classList.contains('do-not-capture')
      });

      const link = document.createElement('a');
      link.download = `${selectedDashboard.title.replace(/\s+/g, '_')}_Dashboard.png`;
      link.href = canvas.toDataURL('image/png');
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      showToast('Dashboard image downloaded!', 'success');
    } catch (error) {
      console.error('Error capturing dashboard:', error);
      showToast('Failed to generate image', 'error');
    }
  };

  const handleCopyDashboardImage = async () => {
    // Select the dashboard grid container using the same class as download
    const dashboardElement = document.querySelector('.dashboard-charts-grid-premium');

    if (!dashboardElement) {
      showToast('Dashboard content not found', 'error');
      return;
    }

    try {
      showToast('Capturing dashboard...', 'info');

      const canvas = await html2canvas(dashboardElement, {
        scale: 2,
        useCORS: true,
        logging: false,
        backgroundColor: theme === 'dark' ? '#0f172a' : '#f8fafc',
        ignoreElements: (element) => element.classList.contains('do-not-capture')
      });

      canvas.toBlob(async (blob) => {
        if (!blob) {
          showToast('Failed to generate image blob', 'error');
          return;
        }

        try {
          // Use the Clipboard Item API
          const item = new ClipboardItem({ 'image/png': blob });
          await navigator.clipboard.write([item]);
          showToast('Dashboard image copied to clipboard!', 'success');
        } catch (err) {
          console.error('Clipboard write failed:', err);
          showToast('Failed to copy image (browser restrictions?)', 'error');
        }
      }, 'image/png');

    } catch (error) {
      console.error('Error copying dashboard:', error);
      showToast('Failed to capture dashboard', 'error');
    }
  };

  const changeChartType = async (chartId, newType) => {
    try {
      const token = localStorage.getItem('authToken');
      const res = await fetch(`${API_BASE}/charts/${chartId}`, {
        method: 'PUT', // Assuming PUT updates the chart
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          chart_type: newType
        })
      });

      if (res.ok) {
        showToast(`Chart type changed to ${newType}`, 'success');
        setActiveChartTypeMenu(null);
        // Refresh dashboard data to reflect changes
        loadDashboard(selectedDashboard.id);
      } else {
        const err = await res.json();
        throw new Error(err.error || 'Failed to update chart type');
      }
    } catch (error) {
      console.error('Error changing chart type:', error);
      showToast('Failed to change chart type', 'error');
    }
  };

  const removeChartFromDashboard = async (chartId) => {
    if (!selectedDashboard) return;

    const chartIds = selectedDashboard.chart_ids.filter(id => id !== chartId);

    try {
      await fetch(`${API_BASE}/dashboards/${selectedDashboard.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          ...selectedDashboard,
          chart_ids: chartIds
        })
      });

      loadDashboard(selectedDashboard.id);
    } catch (error) {
      console.error('Error removing chart:', error);
    }
  };

  // Helper: Apply Filters (Global + Interactive)
  const applyFilters = (data, excludeKey = null) => {
    if (!data || data.length === 0) return [];

    return data.filter(row => {
      // Check Global Filters
      for (const [key, value] of Object.entries(dashboardFilters)) {
        if (row[key] !== undefined && row[key] != value) return false;
      }
      // Check Interactive Cross-Filters
      for (const [key, value] of Object.entries(interactiveFilters)) {
        // Exclude the filter if it matches the current chart's dimension (x-axis)
        // This prevents the chart from filtering itself down to one bar
        if (key === excludeKey) continue;

        if (row[key] !== undefined && row[key] != value) return false;
      }
      return true;
    });
  };

  const renderChart = (chartData, style = null, optionsOverride = {}) => {
    // We need data mapping first to know what to filter/exclude
    let mapping = chartData.dataMapping || chartData.data_mapping;

    // Safety check for missing mapping
    if (!mapping) {
      // Try to infer mapping or show error
      if (chartData.mapping) mapping = chartData.mapping;
      else {
        // console.warn('RenderChart: Missing dataMapping', chartData.title);
        // Fallback or skip
      }
    }

    // Parse if string
    if (typeof mapping === 'string') {
      try { mapping = JSON.parse(mapping); } catch (e) { }
    }

    // Helper: find the best matching key in data for a given mapping key
    const findMatchingKey = (dataRow, key) => {
      if (!dataRow || !key) return null;
      // Exact match
      if (dataRow.hasOwnProperty(key)) return key;
      // Case-insensitive match
      const lowerKey = key.toLowerCase();
      const match = Object.keys(dataRow).find(k => k.toLowerCase() === lowerKey);
      if (match) return match;
      // Partial match (e.g. mapping says "total_budget" but column is "sum_total_budget")
      const partial = Object.keys(dataRow).find(k => k.toLowerCase().includes(lowerKey) || lowerKey.includes(k.toLowerCase()));
      return partial || null;
    };

    // Helper: determine if most values in an array are numeric
    const isMostlyNumeric = (arr) => {
      if (!arr || arr.length === 0) return false;
      const numericCount = arr.filter(v => v !== null && v !== undefined && v !== '' && !isNaN(parseFloat(v)) && isFinite(v)).length;
      return numericCount >= arr.length * 0.7; // 70%+ are numeric
    };

    // Smart mapping resolution: fix column name mismatches
    if (mapping && chartData.data && chartData.data.length > 0) {
      const sampleRow = chartData.data[0];
      const resolvedX = findMatchingKey(sampleRow, mapping.x);
      const resolvedY = findMatchingKey(sampleRow, mapping.y);

      if (resolvedX && resolvedX !== mapping.x) {
        console.log(`[renderChart] Auto-corrected mapping.x: "${mapping.x}" → "${resolvedX}"`);
        mapping = { ...mapping, x: resolvedX };
      }
      if (resolvedY && resolvedY !== mapping.y) {
        console.log(`[renderChart] Auto-corrected mapping.y: "${mapping.y}" → "${resolvedY}"`);
        mapping = { ...mapping, y: resolvedY };
      }
    }

    // If absolutely no mapping, we can't render properly or filter accurately
    if (!mapping && chartData.data && chartData.data.length > 0) {
      // Smart guess: put text/categorical column on X, numeric column on Y
      const keys = Object.keys(chartData.data[0]);
      if (keys.length >= 2) {
        const sampleValues0 = chartData.data.slice(0, 5).map(row => row[keys[0]]);
        const sampleValues1 = chartData.data.slice(0, 5).map(row => row[keys[1]]);
        const key0Numeric = isMostlyNumeric(sampleValues0);
        const key1Numeric = isMostlyNumeric(sampleValues1);

        if (key0Numeric && !key1Numeric) {
          // First column is numeric, second is text → swap
          mapping = { x: keys[1], y: keys[0] };
        } else {
          mapping = { x: keys[0], y: keys[1] };
        }
      }
    }

    if (!mapping) return <div className="no-data">Invalid Configuration</div>;

    // Auto-swap detection: if x values are all numeric and y values are all text, swap them
    // This catches the common LLM error of putting numeric column as x and text column as y
    if (chartData.data && chartData.data.length > 0 && !['scatter', 'bubble'].includes(
      (style || {}).type || chartData.chart_type || chartData.type || 'bar'
    )) {
      const sampleX = chartData.data.slice(0, 5).map(row => row[mapping.x]);
      const sampleY = chartData.data.slice(0, 5).map(row => row[mapping.y]);
      const xIsNumeric = isMostlyNumeric(sampleX);
      const yIsNumeric = isMostlyNumeric(sampleY);

      if (xIsNumeric && !yIsNumeric) {
        // X has numbers, Y has text → they are swapped, fix it
        console.log(`[renderChart] Auto-swapping x/y: x="${mapping.x}" (numeric) ↔ y="${mapping.y}" (text)`);
        mapping = { ...mapping, x: mapping.y, y: mapping.x };
      }

      // Also detect scenario where both mapping keys don't exist in data at all
      const sampleRow = chartData.data[0];
      if (!sampleRow.hasOwnProperty(mapping.x) && !sampleRow.hasOwnProperty(mapping.y)) {
        // Neither key exists — re-infer from data
        const keys = Object.keys(sampleRow);
        if (keys.length >= 2) {
          const vals0 = chartData.data.slice(0, 5).map(row => row[keys[0]]);
          const vals1 = chartData.data.slice(0, 5).map(row => row[keys[1]]);
          if (isMostlyNumeric(vals0) && !isMostlyNumeric(vals1)) {
            mapping = { ...mapping, x: keys[1], y: keys[0] };
          } else {
            mapping = { ...mapping, x: keys[0], y: keys[1] };
          }
          console.log(`[renderChart] Re-inferred mapping: x="${mapping.x}", y="${mapping.y}"`);
        }
      }
    }

    const dataMapping = mapping;

    // Apply filters logic
    let data = chartData.data;

    // Only apply filters if we are in Dashboard View mode
    if (activeTab === 'dashboard' && dashboardMode === 'view') {
      // Pass this chart's X-axis key to exclude it from self-filtering
      data = applyFilters(chartData.data, dataMapping.x);
    }

    const hasActiveFilters = Object.keys(dashboardFilters).length > 0 || Object.keys(interactiveFilters).length > 0;

    if (!data || data.length === 0) {
      // Show different message depending on whether filters are causing the empty state
      if (hasActiveFilters && activeTab === 'dashboard') {
        return (
          <div className="no-data" style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100%', color: 'var(--text-dim)', textAlign: 'center', padding: '20px' }}>
            <Filter size={24} style={{ marginBottom: '8px', opacity: 0.5 }} />
            <span>No data matches the current filters</span>
            <button style={{ marginTop: '8px', fontSize: '0.75rem', color: 'var(--accent-primary)', background: 'none', border: 'none', cursor: 'pointer', textDecoration: 'underline' }} onClick={() => { setDashboardFilters({}); setInteractiveFilters({}); }}>Clear Filters</button>
          </div>
        );
      }
      // No filters active or not in dashboard — just show a generic "no data" message
      return (
        <div className="no-data" style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100%', color: 'var(--text-dim)', textAlign: 'center', padding: '20px' }}>
          <BarChart3 size={24} style={{ marginBottom: '8px', opacity: 0.5 }} />
          <span>No data available for this chart</span>
        </div>
      );
    }

    const { hideTitle = false, ref = null } = optionsOverride;

    const activeStyle = style || {};
    const rawChartType = activeStyle.type || chartData.chart_type || chartData.type || 'bar';
    // Normalize chart type: DB may store lowercase (e.g. 'horizontalbar'), switch cases use camelCase
    const normalizeChartType = (t) => {
      const map = {
        'horizontalbar': 'horizontalBar',
        'stackedbar': 'stackedBar',
        'groupedbar': 'groupedBar',
        'multiline': 'multiLine',
        'stackedarea': 'stackedArea',
        'polararea': 'polarArea',
      };
      return map[String(t).toLowerCase().trim()] || String(t).trim();
    };
    const chartType = normalizeChartType(rawChartType);
    if (rawChartType !== chartType) {
      console.log(`[renderChart] Normalized chart type: "${rawChartType}" → "${chartType}"`);
    }
    const chartTitle = activeStyle.title || chartData.title || '';
    const chartColors = activeStyle.colors || (chartData.colors ? (typeof chartData.colors === 'string' ? JSON.parse(chartData.colors) : chartData.colors) : colorPresets.professional);

    // Extract labels and values safely
    const labels = data.map(item => {
      const val = (item[mapping.x] ?? item[Object.keys(item).find(k => k.toLowerCase() === (mapping.x || '').toLowerCase())]);
      return val || '';
    });
    const values = data.map(item => {
      const val = (item[mapping.y] ?? item[Object.keys(item).find(k => k.toLowerCase() === (mapping.y || '').toLowerCase())]);
      return parseFloat(val) || 0;
    });

    // const dataMapping = mapping; // Removed duplicate decl

    // Generate colors for each data point
    const generateColors = (count, opacity = '80') => {
      const colorsToUse = chartColors || colorPresets.professional;
      return Array.from({ length: count }, (_, index) => {
        const label = labels[index];
        if (activeStyle.individualColors && activeStyle.individualColors[label]) {
          return activeStyle.individualColors[label];
        }
        const color = colorsToUse[index % colorsToUse.length];
        return ['pie', 'donut', 'radar', 'polarArea'].includes(chartType)
          ? color
          : color + opacity;
      });
    };

    const backgroundColors = generateColors(labels.length, '80');
    const borderColors = generateColors(labels.length, 'FF');

    // Highlight specific indices if specified
    const pointBorderWidth = labels.map((_, index) => {
      return activeStyle.highlightIndices && activeStyle.highlightIndices.includes(index) ? 4 : 2;
    });

    const pointRadius = activeStyle.highlightIndices && activeStyle.highlightIndices.length > 0
      ? labels.map((_, index) => activeStyle.highlightIndices.includes(index) ? 6 : 4)
      : 4;

    // Theme-aware colors for charts
    const chartThemeColors = {
      text: theme === 'dark' ? '#94a3b8' : '#475569',
      grid: theme === 'dark' ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.05)',
      title: theme === 'dark' ? '#f8fafc' : '#0f172a'
    };

    // Common chart options
    const baseOptions = {
      responsive: true,
      maintainAspectRatio: false,
      onClick: (event, elements) => {
        // Only enable cross-filtering in Dashboard View Mode
        if (activeTab === 'dashboard' && dashboardMode === 'view' && elements.length > 0) {
          const index = elements[0].index;
          const label = labels[index];
          const dimension = mapping.x; // e.g., 'category' or 'region'

          console.log(`Cross-filter click: ${dimension} = ${label}`);

          setInteractiveFilters(prev => {
            const newState = { ...prev };
            // Toggle logic: If clicking same value, remove filter
            if (newState[dimension] === label) {
              delete newState[dimension];
            } else {
              newState[dimension] = label;
            }
            return newState;
          });
        }
      },
      onHover: (event, elements, chart) => {
        if (!chart || !chart.scales || !chart.canvas || !event.native) return;

        // For horizontal bar charts, the CATEGORY axis is Y. For all others, it's X.
        const isHorizontal = chartType === 'horizontalBar';
        const indexAxis = isHorizontal ? chart.scales.y : chart.scales.x;
        if (!indexAxis) return;

        const { x, y } = event;

        // Ensure our custom tooltip div exists
        let tooltipEl = document.getElementById('custom-axis-tooltip');
        if (!tooltipEl) {
          tooltipEl = document.createElement('div');
          tooltipEl.id = 'custom-axis-tooltip';
          tooltipEl.style.position = 'fixed'; // Use fixed so event.native.clientX/Y works perfectly
          tooltipEl.style.background = 'var(--bg-tertiary, #0f172a)';
          tooltipEl.style.color = 'var(--text-main, #fff)';
          tooltipEl.style.padding = '6px 10px';
          tooltipEl.style.borderRadius = '6px';
          tooltipEl.style.fontSize = '12px';
          tooltipEl.style.pointerEvents = 'none';
          tooltipEl.style.zIndex = '100000';
          tooltipEl.style.display = 'none';
          tooltipEl.style.boxShadow = '0 4px 6px rgba(0,0,0,0.1)';
          tooltipEl.style.border = '1px solid var(--glass-border, rgba(255,255,255,0.1))';
          document.body.appendChild(tooltipEl);

          // Cleanup when mouse completely leaves canvas
          chart.canvas.addEventListener('mouseleave', () => {
            if (tooltipEl) tooltipEl.style.display = 'none';
          });
        }

        // Check if mouse is within the index (category) axis boundary
        if (y >= indexAxis.top && y <= indexAxis.bottom && x >= indexAxis.left && x <= indexAxis.right) {
          const valueIndex = Math.round(isHorizontal
            ? indexAxis.getValueForPixel(y)
            : indexAxis.getValueForPixel(x)
          );

          if (valueIndex >= 0 && valueIndex < labels.length) {
            const currentLabel = String(labels[valueIndex]);

            tooltipEl.innerText = currentLabel;
            tooltipEl.style.display = 'block';
            // Position near the mouse cursor globally via clientX/Y
            tooltipEl.style.left = (event.native.clientX + 10) + 'px';
            tooltipEl.style.top = (event.native.clientY + 10) + 'px';

            chart.canvas.style.cursor = 'pointer';
            return;
          }
        }

        // Hide tooltip if not hovering properly over an axis text
        tooltipEl.style.display = 'none';
        chart.canvas.style.cursor = 'default';
      },
      plugins: {
        tooltip: {
          callbacks: {
            title: function (tooltipItems) {
              if (tooltipItems.length > 0) {
                // Return exact original label from the globally scoped `labels` array which has original data
                return labels[tooltipItems[0].dataIndex] || tooltipItems[0].label;
              }
              return '';
            }
          }
        },
        legend: {
          display: ['pie', 'donut', 'radar', 'polarArea', 'multiLine', 'stackedBar', 'stackedArea', 'groupedBar'].includes(chartType),
          labels: {
            color: chartThemeColors.text
          }
        },
        title: {
          display: !!chartTitle && !hideTitle,
          text: chartTitle,
          color: chartThemeColors.title,
          font: {
            size: 16,
            weight: 'bold'
          }
        },
        datalabels: {
          display: function (context) {
            const cId = chartData.id || optionsOverride.chartId || 'unsaved';
            return dataLabelsMap[cId] === true;
          },
          color: chartThemeColors.text,
          align: 'end',
          anchor: 'end',
          backgroundColor: theme === 'dark' ? 'rgba(15, 23, 42, 0.75)' : 'rgba(255, 255, 255, 0.75)',
          borderRadius: 4,
          padding: 3,
          font: { weight: 'bold', size: 10 },
          formatter: (value) => {
            const numVal = Number(value);
            if (!isNaN(numVal) && value !== null && value !== '') {
              if (Math.abs(numVal) >= 1000000) return (numVal / 1000000).toFixed(1) + 'M';
              if (Math.abs(numVal) >= 1000) return (numVal / 1000).toFixed(1) + 'K';
              if (numVal % 1 !== 0) {
                return numVal.toFixed(2);
              }
              return numVal;
            }
            return value;
          }
        }
      }
    };

    // Chart-specific configurations
    let chartConfig;
    let options;
    let ChartComponent;

    switch (chartType) {
      case 'bar':
      case 'horizontalBar':
        ChartComponent = Bar;
        chartConfig = {
          labels,
          datasets: [{
            label: dataMapping.y,
            data: values,
            backgroundColor: backgroundColors,
            borderColor: borderColors,
            borderWidth: 2
          }]
        };
        options = {
          ...baseOptions,
          indexAxis: chartType === 'horizontalBar' ? 'y' : 'x',
          scales: {
            x: chartType === 'horizontalBar' ? {
              // VALUE axis for horizontal bar → linear/numeric
              type: 'linear',
              ticks: { color: chartThemeColors.text },
              grid: { color: chartThemeColors.grid },
              beginAtZero: true,
              min: 0
            } : {
              // CATEGORY axis for normal bar
              type: 'category',
              ticks: { color: chartThemeColors.text },
              grid: { color: chartThemeColors.grid }
            },
            y: chartType === 'horizontalBar' ? {
              // CATEGORY axis for horizontal bar
              type: 'category',
              ticks: { color: '#64748b' },
              grid: { color: '#e2e8f0' }
            } : {
              // VALUE axis for normal bar → linear/numeric
              type: 'linear',
              ticks: { color: '#64748b' },
              grid: { color: '#e2e8f0' },
              beginAtZero: true,
              min: 0
            }
          }
        };
        break;

      case 'stackedBar':
      case 'groupedBar':
        ChartComponent = Bar;
        // For stacked/grouped, we need series data
        const series = dataMapping.series ? [...new Set(data.map(item => item[dataMapping.series]))] : null;

        if (series && series.length > 1) {
          const uniqueLabels = [...new Set(labels)];
          const datasets = series.map((s, idx) => ({
            label: s,
            data: uniqueLabels.map(label => {
              const item = data.find(d => d[dataMapping.x] === label && d[dataMapping.series] === s);
              return item ? parseFloat(item[dataMapping.y]) || 0 : 0;
            }),
            backgroundColor: chartColors[idx % chartColors.length] + '80',
            borderColor: chartColors[idx % chartColors.length],
            borderWidth: 2
          }));

          chartConfig = { labels: uniqueLabels, datasets };
        } else {
          // Fallback to regular bar if no series
          chartConfig = {
            labels,
            datasets: [{
              label: dataMapping.y,
              data: values,
              backgroundColor: backgroundColors,
              borderColor: borderColors,
              borderWidth: 2
            }]
          };
        }

        options = {
          ...baseOptions,
          scales: {
            x: {
              type: 'category',
              stacked: chartType === 'stackedBar',
              ticks: { color: chartThemeColors.text },
              grid: { color: chartThemeColors.grid },
              beginAtZero: true
            },
            y: {
              type: 'linear',
              stacked: chartType === 'stackedBar',
              ticks: { color: '#94a3b8' },
              grid: { color: '#334155' },
              beginAtZero: true
            }
          }
        };
        break;

      case 'line':
      case 'multiLine':
        ChartComponent = Line;
        const lineSeries = dataMapping.series ? [...new Set(data.map(item => item[dataMapping.series]))] : null;

        if (chartType === 'multiLine' && lineSeries && lineSeries.length > 1) {
          const uniqueLineLabels = [...new Set(labels)];
          const datasets = lineSeries.map((s, idx) => ({
            label: s,
            data: uniqueLineLabels.map(label => {
              const item = data.find(d => d[dataMapping.x] === label && d[dataMapping.series] === s);
              return item ? parseFloat(item[dataMapping.y]) || 0 : 0;
            }),
            borderColor: chartColors[idx % chartColors.length],
            backgroundColor: chartColors[idx % chartColors.length] + '20',
            borderWidth: 2,
            tension: 0.4
          }));

          chartConfig = { labels: uniqueLineLabels, datasets };
        } else {
          chartConfig = {
            labels,
            datasets: [{
              label: dataMapping.y,
              data: values,
              borderColor: chartColors[0],
              backgroundColor: chartColors[0] + '20',
              borderWidth: 2,
              pointRadius: pointRadius,
              pointBorderWidth: pointBorderWidth,
              tension: 0.4
            }]
          };
        }

        options = {
          ...baseOptions,
          scales: {
            x: {
              ticks: { color: chartThemeColors.text },
              grid: { color: chartThemeColors.grid }
            },
            y: {
              ticks: { color: chartThemeColors.text },
              grid: { color: chartThemeColors.grid }
            }
          }
        };
        break;

      case 'area':
      case 'stackedArea':
        ChartComponent = Line;
        const areaSeries = dataMapping.series ? [...new Set(data.map(item => item[dataMapping.series]))] : null;

        if (chartType === 'stackedArea' && areaSeries && areaSeries.length > 1) {
          const uniqueAreaLabels = [...new Set(labels)];
          const datasets = areaSeries.map((s, idx) => ({
            label: s,
            data: uniqueAreaLabels.map(label => {
              const item = data.find(d => d[dataMapping.x] === label && d[dataMapping.series] === s);
              return item ? parseFloat(item[dataMapping.y]) || 0 : 0;
            }),
            borderColor: chartColors[idx % chartColors.length],
            backgroundColor: chartColors[idx % chartColors.length] + '80',
            borderWidth: 2,
            fill: true,
            tension: 0.4
          }));

          chartConfig = { labels: uniqueAreaLabels, datasets };
        } else {
          chartConfig = {
            labels,
            datasets: [{
              label: dataMapping.y,
              data: values,
              borderColor: chartColors[0],
              backgroundColor: chartColors[0] + '80',
              borderWidth: 2,
              fill: true,
              tension: 0.4
            }]
          };
        }

        options = {
          ...baseOptions,
          scales: {
            x: {
              stacked: chartType === 'stackedArea',
              ticks: { color: '#94a3b8' },
              grid: { color: '#334155' }
            },
            y: {
              stacked: chartType === 'stackedArea',
              ticks: { color: '#94a3b8' },
              grid: { color: '#334155' }
            }
          }
        };
        break;

      case 'pie':
        ChartComponent = Pie;
        chartConfig = {
          labels,
          datasets: [{
            data: values,
            backgroundColor: backgroundColors,
            borderColor: borderColors,
            borderWidth: 2
          }]
        };
        options = {
          ...baseOptions
        };
        break;

      case 'donut':
        ChartComponent = Doughnut;
        chartConfig = {
          labels,
          datasets: [{
            data: values,
            backgroundColor: backgroundColors,
            borderColor: borderColors,
            borderWidth: 2
          }]
        };
        options = {
          ...baseOptions,
          cutout: '60%'
        };
        break;

      case 'combo':
        ChartComponent = Bar;
        const y2Values = dataMapping.y2 ? data.map(item => parseFloat(item[dataMapping.y2]) || 0) : null;

        chartConfig = {
          labels,
          datasets: [
            {
              type: 'bar',
              label: dataMapping.y,
              data: values,
              backgroundColor: chartColors[0] + '80',
              borderColor: chartColors[0],
              borderWidth: 2,
              yAxisID: 'y'
            },
            ...(y2Values ? [{
              type: 'line',
              label: dataMapping.y2,
              data: y2Values,
              borderColor: chartColors[1],
              backgroundColor: chartColors[1] + '20',
              borderWidth: 2,
              yAxisID: 'y2',
              tension: 0.4
            }] : [])
          ]
        };

        options = {
          ...baseOptions,
          scales: {
            x: {
              ticks: { color: chartThemeColors.text },
              grid: { color: chartThemeColors.grid }
            },
            y: {
              type: 'linear',
              position: 'left',
              ticks: { color: chartThemeColors.text },
              grid: { color: chartThemeColors.grid }
            },
            y2: {
              type: 'linear',
              position: 'right',
              ticks: { color: chartThemeColors.text },
              grid: { color: chartThemeColors.grid }
            }
          }
        };
        break;

      case 'scatter':
        ChartComponent = Scatter;
        chartConfig = {
          datasets: [{
            label: `${dataMapping.x} vs ${dataMapping.y}`,
            data: data.map(item => ({
              x: parseFloat(item[dataMapping.x]) || 0,
              y: parseFloat(item[dataMapping.y]) || 0
            })),
            backgroundColor: chartColors[0] + '80',
            borderColor: chartColors[0],
            borderWidth: 2,
            pointRadius: 6
          }]
        };

        options = {
          ...baseOptions,
          scales: {
            x: {
              type: 'linear',
              ticks: { color: '#64748b' },
              grid: { color: '#e2e8f0' }
            },
            y: {
              ticks: { color: '#64748b' },
              grid: { color: '#e2e8f0' }
            }
          }
        };
        break;

      case 'bubble':
        ChartComponent = Bubble;
        const sizeColumn = dataMapping.size || dataMapping.y;

        // Check if x values are numeric or categorical
        const firstXValue = data[0]?.[dataMapping.x];
        const isNumericX = !isNaN(parseFloat(firstXValue)) && isFinite(firstXValue);

        // Find min and max for size scaling
        const sizeValues = data.map(item => Math.abs(parseFloat(item[sizeColumn])) || 1);
        const maxSize = Math.max(...sizeValues);
        const minSize = Math.min(...sizeValues);
        const sizeRange = maxSize - minSize || 1;

        chartConfig = {
          datasets: [{
            label: `${dataMapping.x} vs ${dataMapping.y}`,
            data: data.map((item, index) => ({
              x: isNumericX ? (parseFloat(item[dataMapping.x]) || 0) : index,
              y: parseFloat(item[dataMapping.y]) || 0,
              r: 5 + ((Math.abs(parseFloat(item[sizeColumn])) - minSize) / sizeRange) * 25, // Scale radius between 5-30
              label: item[dataMapping.x] // Store original label for tooltip
            })),
            backgroundColor: chartColors[0] + '60',
            borderColor: chartColors[0],
            borderWidth: 2
          }]
        };

        options = {
          ...baseOptions,
          plugins: {
            ...baseOptions.plugins,
            tooltip: {
              callbacks: {
                label: function (context) {
                  const point = context.raw;
                  const xLabel = isNumericX ? point.x : (point.label || labels[context.dataIndex]);
                  const sizeVal = sizeValues[context.dataIndex];
                  return `${xLabel}: ${dataMapping.y}=${point.y}, Size=${sizeVal.toLocaleString()}`;
                }
              }
            }
          },
          scales: {
            x: {
              type: isNumericX ? 'linear' : 'linear',
              title: {
                display: true,
                text: isNumericX ? dataMapping.x : 'Index',
                color: '#94a3b8'
              },
              ticks: {
                color: chartThemeColors.text,
                callback: function (value, index) {
                  if (!isNumericX && labels[value] !== undefined) {
                    return labels[value];
                  }
                  return value;
                }
              },
              grid: { color: chartThemeColors.grid }
            },
            y: {
              title: {
                display: true,
                text: dataMapping.y,
                color: chartThemeColors.text
              },
              ticks: { color: chartThemeColors.text },
              grid: { color: chartThemeColors.grid }
            }
          }
        };
        break;

      case 'radar':
        ChartComponent = Radar;
        chartConfig = {
          labels,
          datasets: [{
            label: dataMapping.y,
            data: values,
            backgroundColor: chartColors[0] + '40',
            borderColor: chartColors[0],
            borderWidth: 2,
            pointBackgroundColor: chartColors[0],
            pointBorderColor: '#fff',
            pointHoverBackgroundColor: '#fff',
            pointHoverBorderColor: chartColors[0]
          }]
        };

        options = {
          ...baseOptions,
          scales: {
            r: {
              ticks: { color: chartThemeColors.text },
              grid: { color: chartThemeColors.grid },
              pointLabels: { color: '#cbd5e1' }
            }
          }
        };
        break;

      case 'polarArea':
        ChartComponent = PolarArea;
        chartConfig = {
          labels,
          datasets: [{
            data: values,
            backgroundColor: backgroundColors,
            borderColor: borderColors,
            borderWidth: 2
          }]
        };

        options = {
          ...baseOptions,
          scales: {
            r: {
              ticks: { color: '#94a3b8' },
              grid: { color: '#334155' }
            }
          }
        };
        break;

      default:
        // Default to bar chart
        ChartComponent = Bar;
        chartConfig = {
          labels,
          datasets: [{
            label: dataMapping.y,
            data: values,
            backgroundColor: backgroundColors,
            borderColor: borderColors,
            borderWidth: 2
          }]
        };
        options = {
          ...baseOptions,
          scales: {
            x: {
              ticks: { color: '#94a3b8' },
              grid: { color: '#334155' }
            },
            y: {
              ticks: { color: '#94a3b8' },
              grid: { color: '#334155' }
            }
          }
        };
    }

    // Globally truncate long x-axis labels to prevent ugly overlapping texts
    if (options && options.scales && options.scales.x && options.scales.x.ticks) {
      options.scales.x.ticks.maxRotation = 45;
      options.scales.x.ticks.minRotation = 45;
      options.scales.x.ticks.callback = function (value, index, values) {
        // Find the actual string label from the array instead of raw ticks
        const title = labels[index] !== undefined ? labels[index] : (this.getLabelForValue ? this.getLabelForValue(value) : value);

        if (typeof title === 'string' && title.length > 15) {
          return title.substring(0, 12) + '...';
        }
        return title;
      };
    }

    return <ChartComponent ref={ref} data={chartConfig} options={options} />;
  };

  const handleDownloadImage = () => {
    if (chartRef.current) {
      const link = document.createElement('a');
      link.download = `${viewOnlyChart?.title || 'chart'}.png`;
      link.href = chartRef.current.toBase64Image();
      link.click();
      showToast('Image downloaded!', 'success');
    } else {
      showToast('Chart not ready for download', 'error');
    }
  };



  const handleCopyImage = async (e, chart = null) => {
    if (e) e.stopPropagation();

    // Scenario 1: Copy from Dashboard Card (html2canvas on container)
    if (chart) {
      const targetElement = e.currentTarget.closest('.dashboard-chart-card-premium') || e.currentTarget.closest('.inventory-card-premium');
      if (!targetElement) {
        showToast('Could not find chart to copy', 'error');
        return;
      }

      try {
        const canvas = await html2canvas(targetElement, {
          scale: 2,
          useCORS: true,
          backgroundColor: '#ffffff',
          ignoreElements: (element) => {
            // Hide buttons when copying
            return element.tagName === 'BUTTON' || element.classList.contains('drag-handle');
          }
        });

        canvas.toBlob(blob => {
          if (!blob) {
            showToast('Failed to generate image', 'error');
            return;
          }
          const item = new ClipboardItem({ 'image/png': blob });
          navigator.clipboard.write([item]).then(() => {
            showToast('Image copied to clipboard!', 'success');
          }).catch(err => {
            console.error('Clipboard write failed:', err);
            showToast('Failed to copy image', 'error');
          });
        });
      } catch (error) {
        console.error('Error copying image:', error);
        showToast('Failed to copy image', 'error');
      }
      return;
    }

    // Scenario 2: Copy from View Only Modal (Direct Canvas Access)
    if (chartRef.current) {
      const canvas = chartRef.current.canvas;
      canvas.toBlob(blob => {
        if (!blob) {
          showToast('Failed to copy image', 'error');
          return;
        }
        const item = new ClipboardItem({ "image/png": blob });
        navigator.clipboard.write([item]).then(() => {
          showToast('Image copied to clipboard!', 'success');
        }).catch(err => {
          console.error("Copy failed:", err);
          showToast('Failed to copy image', 'error');
        });
      });
    } else {
      showToast('Chart not ready for copy', 'error');
    }
  };

  return (
    <div className="app-layout">
      {/* Premium Left Sidebar */}
      <aside className={`premium-sidebar ${isSidebarOpen ? 'expanded' : ''}`}>
        {/* Toggle Button on Border */}
        <button
          onClick={() => setIsSidebarOpen(!isSidebarOpen)}
          className="sidebar-border-toggle"
          title={isSidebarOpen ? "Close Sidebar" : "Open Sidebar"}
        >
          {isSidebarOpen ? <PanelLeftClose size={16} /> : <PanelLeft size={16} />}
        </button>

        {/* Logo Section */}
        <div className="sidebar-brand-wrapper" style={{
          margin: '0 0 32px 0',
          width: '100%',
          display: 'flex',
          justifyContent: isSidebarOpen ? 'flex-start' : 'center',
          padding: isSidebarOpen ? '0 24px' : '0 12px'
        }}>
          {isSidebarOpen ? (
            <img
              src={theme === 'dark' ? chartiqLogoLight : chartiqLogo}
              alt="ChartIQ"
              style={{
                height: '32px',
                width: 'auto',
                objectFit: 'contain'
              }}
            />
          ) : (
            <img
              src={theme === 'dark' ? symbolLight : symbol}
              alt="ChartIQ"
              style={{
                height: '32px',
                width: '32px',
                objectFit: 'contain'
              }}
            />
          )}
        </div>

        {/* Navigation Icons */}
        <nav className="sidebar-nav">
          {!isViewer && userRole !== 'builder' && (
            <button
              className={`sidebar-nav-item ${activeTab === 'overview' && !showNotificationPanel ? 'active' : ''}`}
              onClick={() => { setActiveTab('overview'); setShowNotificationPanel(false); }}
              title="Overview"
            >
              <TrendingUp size={22} />
              {isSidebarOpen && <span>Overview</span>}
            </button>
          )}

          <button
            className={`sidebar-nav-item ${activeTab === 'dashboard' && !showNotificationPanel ? 'active' : ''}`}
            onClick={() => { setActiveTab('dashboard'); setShowNotificationPanel(false); }}
            title="Dashboards"
          >
            <Home size={22} />
            {isSidebarOpen && <span>Dashboards</span>}
          </button>

          {!isViewer && (
            <button
              className={`sidebar-nav-item ${activeTab === 'inventory' && !showNotificationPanel ? 'active' : ''}`}
              onClick={() => { setActiveTab('inventory'); setShowNotificationPanel(false); }}
              title="Charts"
            >
              <BarChart3 size={22} />
              {isSidebarOpen && <span>Charts</span>}
            </button>
          )}

          {!isViewer && (
            <button
              className={`sidebar-nav-item ${activeTab === 'explore' && !showNotificationPanel ? 'active' : ''}`}
              onClick={() => { setActiveTab('explore'); setShowNotificationPanel(false); }}
              title="Data"
            >
              <Database size={22} />
              {isSidebarOpen && <span>Explore Data</span>}
            </button>
          )}


          <button
            className={`sidebar-nav-item ${showNotificationPanel ? 'active' : ''}`}
            onClick={() => setShowNotificationPanel(!showNotificationPanel)}
            title="Notifications"
            style={{ position: 'relative' }}
          >
            <Bell size={22} />
            {unreadCount > 0 && <span className="notification-badge-sidebar">{unreadCount}</span>}
            {isSidebarOpen && <span>Notifications</span>}
          </button>

          {/* Notification Popover */}
          {showNotificationPanel && (
            <div className="notification-popover">
              <div className="notification-header">
                <h3>Notifications</h3>
                <div style={{ display: 'flex', gap: '8px' }}>
                  {unreadCount > 0 && (
                    <button
                      onClick={handleMarkAllRead}
                      title="Mark all as read"
                      style={{ background: 'transparent', border: 'none', color: 'var(--accent-primary)', cursor: 'pointer', fontSize: '0.8rem', fontWeight: 600 }}
                    >
                      Mark all read
                    </button>
                  )}
                  <button onClick={() => setShowNotificationPanel(false)}><X size={16} /></button>
                </div>
              </div>
              <div className="notification-list">
                {notifications.length === 0 ? (
                  <div className="empty-notif">No new notifications</div>
                ) : (
                  notifications.map(n => (
                    <div key={n.id} className={`notif-item ${!n.is_read ? 'unread' : ''}`} onClick={() => handleNotificationClick(n)}>
                      <div className="notif-title">{n.title}</div>
                      <div className="notif-msg">{n.message}</div>
                      <div className="notif-time">{new Date(n.created_at).toLocaleString()}</div>
                    </div>
                  ))
                )}
              </div>
            </div>
          )}

        </nav>

        {/* Sidebar Footer */}
        <div className="sidebar-footer">
          {isSidebarOpen ? (
            <div className="theme-toggle-wrapper-premium" onClick={toggleTheme} title="Toggle Theme">
              <div className="toggle-info-premium">
                {theme === 'dark' ? <Sun size={18} /> : <Moon size={18} />}
                <span>{theme === 'dark' ? 'Light Mode' : 'Dark Mode'}</span>
              </div>
              <div className="toggle-switch-premium">
                <div className="toggle-thumb-premium"></div>
              </div>
            </div>
          ) : (
            <button className="sidebar-nav-item" onClick={toggleTheme} title="Toggle Theme">
              {theme === 'dark' ? <Sun size={22} /> : <Moon size={22} />}
            </button>
          )}

          {!isViewer && (
            <button
              className={`sidebar-nav-item ${activeTab === 'settings' && !showNotificationPanel ? 'active' : ''}`}
              onClick={() => {
                if (user?.role !== 'super_admin' && user?.role !== 'workspace_admin' && user?.role !== 'builder') {
                  showToast(`Access Denied: As a ${user?.role}, you cannot access Settings.`, 'error');
                  return;
                }
                setActiveTab('settings');
                setShowNotificationPanel(false);
              }}
              title="Settings & Admin"
            >
              <Settings size={22} />
              {isSidebarOpen && <span>Settings</span>}
            </button>
          )}

          <div
            className="user-avatar-sidebar"
            title="My Profile"
            onClick={() => setActiveTab('profile')}
            style={{
              cursor: 'pointer',
              width: isSidebarOpen ? 'auto' : '40px',
              height: '40px',
              padding: isSidebarOpen ? '0 12px' : '0',
              borderRadius: '20px',
              background: 'var(--bg-tertiary)',
              display: 'flex',
              alignItems: 'center',
              gap: '12px',
              justifyContent: isSidebarOpen ? 'flex-start' : 'center',
              marginBottom: '10px'
            }}
          >
            <div style={{
              width: '32px', height: '32px', borderRadius: '50%', background: 'var(--accent-primary)',
              display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'white', fontSize: '0.9rem', fontWeight: 'bold'
            }}>
              {user?.name?.charAt(0)?.toUpperCase()}
            </div>
            {isSidebarOpen && (
              <span style={{ color: 'var(--text-main)', fontSize: '0.9rem', fontWeight: '500', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                {user?.name}
              </span>
            )}
          </div>



          <button className="sidebar-nav-item logout-item" onClick={handleLogout} title="Logout">
            <LogOut size={22} />
            {isSidebarOpen && <span>Sign Out</span>}
          </button>
        </div>
      </aside>

      {/* Main Content Area */}
      <main className={`main-wrapper ${isSidebarOpen ? 'expanded-sidebar' : ''}`}>

        {/* Global Floating Bottom Right Sutherland Logo */}
        <div style={{
          position: 'absolute',
          bottom: '24px',
          right: '40px',
          zIndex: 50,
          pointerEvents: 'none',
          display: 'flex',
          alignItems: 'center'
        }}>
          <img
            src={sutherlandLogo}
            alt="Sutherland"
            style={{
              height: '36px',
              objectFit: 'contain',
              opacity: 0.8
            }}
          />
        </div>

        {/* Page Content */}
        <div className="page-content">

          {/* PROFILE TAB */}
          {activeTab === 'profile' && (
            <div className="profile-container" style={{ padding: '40px', maxWidth: '800px', margin: '0 auto', animation: 'fadeIn 0.5s ease-out' }}>
              <div className="profile-card premium-card" style={{ position: 'relative', background: 'var(--bg-secondary)', padding: '40px', borderRadius: '24px', border: '1px solid var(--glass-border)', boxShadow: '0 20px 40px rgba(0,0,0,0.2)', textAlign: 'center' }}>
                <button
                  onClick={() => navigate(-1)}
                  style={{
                    position: 'absolute', top: '20px', right: '20px',
                    background: 'transparent', border: 'none', color: 'var(--text-dim)',
                    cursor: 'pointer', padding: '8px', borderRadius: '50%',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    transition: 'all 0.2s ease'
                  }}
                  onMouseOver={(e) => { e.currentTarget.style.background = 'var(--hover-bg)'; e.currentTarget.style.color = 'var(--text-main)'; }}
                  onMouseOut={(e) => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = 'var(--text-dim)'; }}
                  title="Close Profile"
                >
                  <X size={20} />
                </button>

                <div style={{
                  width: '120px', height: '120px', margin: '0 auto 24px',
                  borderRadius: '50%', background: 'linear-gradient(135deg, var(--accent-primary), var(--accent-secondary))',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontSize: '3rem', fontWeight: '800', color: '#fff',
                  boxShadow: '0 10px 20px rgba(139, 92, 246, 0.4)'
                }}>
                  {user?.name?.charAt(0)?.toUpperCase()}
                </div>

                {isEditingName ? (
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', marginBottom: '8px' }}>
                    <input
                      type="text"
                      value={tempUserName}
                      onChange={(e) => setTempUserName(e.target.value)}
                      autoFocus
                      style={{
                        fontSize: '2rem', fontWeight: '800', color: 'var(--text-main)',
                        background: 'var(--bg-primary)', border: '1px solid var(--accent-primary)',
                        borderRadius: '8px', padding: '4px 12px', outline: 'none',
                        textAlign: 'center', width: 'auto', minWidth: '200px'
                      }}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') handleUpdateName();
                        if (e.key === 'Escape') setIsEditingName(false);
                      }}
                    />
                    <button onClick={handleUpdateName} className="btn-primary" style={{ padding: '8px 16px' }}>Save</button>
                    <button onClick={() => setIsEditingName(false)} className="btn-secondary" style={{ padding: '8px 16px' }}>Cancel</button>
                  </div>
                ) : (
                  <h1
                    onClick={() => {
                      setTempUserName(user?.name || '');
                      setIsEditingName(true);
                    }}
                    style={{
                      fontSize: '2.5rem', fontWeight: '800', color: 'var(--text-main)', margin: '0 0 8px',
                      cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '12px',
                      transition: 'color 0.2s'
                    }}
                    onMouseOver={e => e.currentTarget.style.color = 'var(--accent-primary)'}
                    onMouseOut={e => e.currentTarget.style.color = 'var(--text-main)'}
                    title="Click to edit name"
                  >
                    {user?.name}
                    <Edit3 size={24} style={{ opacity: 0.5 }} />
                  </h1>
                )}
                <p style={{ fontSize: '1.2rem', color: 'var(--text-dim)', margin: '0 0 32px' }}>{user?.email}</p>

                <div className="profile-details-grid" style={{
                  display: 'grid',
                  gridTemplateColumns: (user?.role === 'super_admin' || user?.role === 'admin') ? '1fr' : '1fr 1fr',
                  gap: '20px',
                  textAlign: 'left',
                  maxWidth: (user?.role === 'super_admin' || user?.role === 'admin') ? '400px' : '100%',
                  margin: '0 auto'
                }}>

                  <div style={{ background: 'var(--bg-tertiary)', padding: '20px', borderRadius: '16px', border: '1px solid var(--glass-border)' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '8px', color: 'var(--text-dim)' }}>
                      <Shield size={18} />
                      <span style={{ fontSize: '0.9rem', fontWeight: '600', textTransform: 'uppercase' }}>Role</span>
                    </div>
                    <div style={{ fontSize: '1.2rem', fontWeight: '600', color: 'var(--text-main)' }}>{user?.role?.replace('_', ' ')}</div>
                  </div>

                  {user?.role !== 'super_admin' && user?.role !== 'admin' && (
                    <div style={{ background: 'var(--bg-tertiary)', padding: '20px', borderRadius: '16px', border: '1px solid var(--glass-border)' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '8px', color: 'var(--text-dim)' }}>
                        <Layers size={18} />
                        <span style={{ fontSize: '0.9rem', fontWeight: '600', textTransform: 'uppercase' }}>
                          {user?.role === 'workspace_admin' ? 'Managed Workspace' : 'Workspace'}
                        </span>
                      </div>
                      <div style={{ fontSize: '1.2rem', fontWeight: '600', color: 'var(--text-main)' }}>
                        {user?.role === 'workspace_admin'
                          ? (user?.administered_workspaces?.map(w => typeof w === 'object' ? w.name : w).join(', ') || 'None')
                          : (user?.workspaces?.map(w => typeof w === 'object' ? w.name : w).join(', ') || 'None')
                        }
                      </div>
                    </div>
                  )}

                </div>

                <div style={{ marginTop: '40px', paddingTop: '30px', borderTop: '1px solid var(--glass-border)' }}>
                  <button className="btn-secondary" onClick={handleLogout} style={{ width: '100%', padding: '16px', borderRadius: '12px', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '12px', fontSize: '1.1rem' }}>
                    <LogOut size={20} />
                    Sign Out
                  </button>
                </div>

              </div>
            </div>
          )}

          {/* OVERVIEW TAB */}
          {activeTab === 'overview' && (
            <div className="overview-container" style={{ padding: '40px 50px', maxWidth: '1800px', margin: '0 auto', animation: 'fadeIn 0.6s ease-out' }}>

              {/* Stats Grid - Premium Cards */}
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', gap: '28px', marginBottom: '50px' }}>

                {/* Users Card */}
                <div className="stat-card-glow" style={{
                  background: 'linear-gradient(145deg, var(--bg-secondary) 0%, var(--bg-tertiary) 100%)',
                  padding: '20px', borderRadius: '20px', border: '1px solid var(--glass-border)',
                  display: 'flex', alignItems: 'center', gap: '16px',
                  boxShadow: '0 10px 30px rgba(0,0,0,0.2)',
                  transition: 'transform 0.3s ease, box-shadow 0.3s ease', cursor: 'pointer'
                }}>
                  <div style={{
                    width: '50px', height: '50px', borderRadius: '14px',
                    background: 'var(--bg-tertiary)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#a78bfa'
                  }}>
                    <User size={24} />
                  </div>
                  <div style={{ position: 'relative', zIndex: 1 }}>
                    <h3 style={{ fontSize: '2.2rem', fontWeight: '800', margin: 0, color: 'var(--text-main)', lineHeight: 1, letterSpacing: '-0.02em' }}>{stats?.users || 0}</h3>
                    <p style={{ margin: '4px 0 0 0', color: 'var(--text-dim)', fontSize: '0.9rem', fontWeight: 500 }}>Total Users</p>
                  </div>
                  <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: '4px', background: 'rgba(16, 185, 129, 0.15)', padding: '4px 8px', borderRadius: '14px', color: '#10b981', fontSize: '0.8rem', fontWeight: 600 }}>
                    <TrendingUp size={12} /> +12%
                  </div>
                </div>

                {/* Dashboards Card */}
                <div className="stat-card-glow" style={{
                  background: 'linear-gradient(145deg, var(--bg-secondary) 0%, var(--bg-tertiary) 100%)',
                  padding: '20px', borderRadius: '20px', border: '1px solid var(--glass-border)',
                  display: 'flex', alignItems: 'center', gap: '16px',
                  boxShadow: '0 10px 30px rgba(0,0,0,0.2)',
                  transition: 'transform 0.3s ease, box-shadow 0.3s ease', cursor: 'pointer'
                }}>
                  <div style={{
                    width: '50px', height: '50px', borderRadius: '14px',
                    background: 'var(--bg-tertiary)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#22d3ee'
                  }}>
                    <Layout size={24} />
                  </div>
                  <div style={{ position: 'relative', zIndex: 1 }}>
                    <h3 style={{ fontSize: '2.2rem', fontWeight: '800', margin: 0, color: 'var(--text-main)', lineHeight: 1, letterSpacing: '-0.02em' }}>{stats?.dashboards || 0}</h3>
                    <p style={{ margin: '4px 0 0 0', color: 'var(--text-dim)', fontSize: '0.9rem', fontWeight: 500 }}>Dashboards Created</p>
                  </div>
                  <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: '4px', background: 'rgba(16, 185, 129, 0.15)', padding: '4px 8px', borderRadius: '14px', color: '#10b981', fontSize: '0.8rem', fontWeight: 600 }}>
                    <TrendingUp size={12} /> +8%
                  </div>
                </div>

                {/* Charts Card */}
                <div className="stat-card-glow" style={{
                  background: 'linear-gradient(145deg, var(--bg-secondary) 0%, var(--bg-tertiary) 100%)',
                  padding: '20px', borderRadius: '20px', border: '1px solid var(--glass-border)',
                  display: 'flex', alignItems: 'center', gap: '16px',
                  boxShadow: '0 10px 30px rgba(0,0,0,0.2)',
                  transition: 'transform 0.3s ease, box-shadow 0.3s ease', cursor: 'pointer'
                }}>
                  <div style={{
                    width: '50px', height: '50px', borderRadius: '14px',
                    background: 'var(--bg-tertiary)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fbbf24'
                  }}>
                    <BarChart3 size={24} />
                  </div>
                  <div style={{ position: 'relative', zIndex: 1 }}>
                    <h3 style={{ fontSize: '2.2rem', fontWeight: '800', margin: 0, color: 'var(--text-main)', lineHeight: 1, letterSpacing: '-0.02em' }}>{stats?.charts || 0}</h3>
                    <p style={{ margin: '4px 0 0 0', color: 'var(--text-dim)', fontSize: '0.9rem', fontWeight: 500 }}>Total Charts</p>
                  </div>
                  <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: '4px', background: 'rgba(16, 185, 129, 0.15)', padding: '4px 8px', borderRadius: '14px', color: '#10b981', fontSize: '0.8rem', fontWeight: 600 }}>
                    <TrendingUp size={12} /> +24%
                  </div>
                </div>

              </div>

              {/* Quick Actions */}
              <div style={{ marginBottom: '50px' }}>
                <h2 style={{ fontSize: '1.4rem', fontWeight: '700', marginBottom: '20px', color: 'var(--text-main)', display: 'flex', alignItems: 'center', gap: '10px' }}>
                  <Sparkles size={22} style={{ color: 'var(--accent-primary)' }} /> Quick Actions
                </h2>
                <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap' }}>
                  <button onClick={() => setActiveTab('chat')} style={{
                    display: 'flex', alignItems: 'center', gap: '12px', padding: '16px 24px',
                    background: 'linear-gradient(135deg, var(--accent-primary) 0%, #9333ea 100%)',
                    border: 'none', borderRadius: '16px', color: 'white', fontSize: '1rem', fontWeight: 600,
                    cursor: 'pointer', boxShadow: '0 8px 24px rgba(139, 92, 246, 0.4)',
                    transition: 'transform 0.2s ease, box-shadow 0.2s ease'
                  }}>
                    <Plus size={20} /> Create New Chart
                  </button>
                  <button onClick={() => setShowDashboardModal(true)} style={{
                    display: 'flex', alignItems: 'center', gap: '12px', padding: '16px 24px',
                    background: 'var(--bg-tertiary)', border: '1px solid var(--glass-border)',
                    borderRadius: '16px', color: 'var(--text-main)', fontSize: '1rem', fontWeight: 600,
                    cursor: 'pointer', transition: 'all 0.2s ease'
                  }}>
                    <Layout size={20} /> New Dashboard
                  </button>
                  <button onClick={() => setActiveTab('explore')} style={{
                    display: 'flex', alignItems: 'center', gap: '12px', padding: '16px 24px',
                    background: 'var(--bg-tertiary)', border: '1px solid var(--glass-border)',
                    borderRadius: '16px', color: 'var(--text-main)', fontSize: '1rem', fontWeight: 600,
                    cursor: 'pointer', transition: 'all 0.2s ease'
                  }}>
                    <Database size={20} /> Explore Data
                  </button>
                </div>
              </div>

              {/* Activity Charts */}
              <h2 style={{ fontSize: '1.4rem', fontWeight: '700', marginBottom: '24px', color: 'var(--text-main)', display: 'flex', alignItems: 'center', gap: '10px' }}>
                <TrendingUp size={22} style={{ color: 'var(--accent-secondary)' }} /> Platform Activity
              </h2>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(480px, 1fr))', gap: '28px' }}>

                <div style={{
                  background: 'linear-gradient(180deg, var(--bg-secondary) 0%, var(--bg-tertiary) 100%)',
                  padding: '32px', borderRadius: '28px', border: '1px solid var(--glass-border)',
                  boxShadow: '0 15px 40px rgba(0,0,0,0.15)', position: 'relative', overflow: 'hidden'
                }}>
                  <div style={{ position: 'absolute', bottom: 0, left: 0, right: 0, height: '60%', background: 'linear-gradient(180deg, transparent 0%, rgba(6, 182, 212, 0.03) 100%)', pointerEvents: 'none' }}></div>
                  <h3 style={{ margin: '0 0 28px 0', color: 'var(--text-main)', fontSize: '1.2rem', fontWeight: 700, display: 'flex', alignItems: 'center', gap: '12px' }}>
                    <div style={{ width: '36px', height: '36px', borderRadius: '10px', background: 'rgba(6, 182, 212, 0.15)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#22d3ee' }}>
                      <Layout size={18} />
                    </div>
                    New Dashboards <span style={{ marginLeft: 'auto', fontSize: '0.85rem', fontWeight: 500, color: 'var(--text-dim)', background: 'var(--bg-tertiary)', padding: '6px 12px', borderRadius: '8px' }}>Last 30 Days</span>
                  </h3>
                  <div style={{ height: '280px', position: 'relative', zIndex: 1 }}>
                    {stats?.trends ? (
                      <Bar
                        data={{
                          labels: stats.trends.dashboards.map(d => d.date),
                          datasets: [{
                            label: 'Dashboards Created',
                            data: stats.trends.dashboards.map(d => d.count),
                            backgroundColor: 'rgba(6, 182, 212, 0.8)',
                            hoverBackgroundColor: '#22d3ee',
                            borderRadius: 8,
                            barThickness: 24
                          }]
                        }}
                        options={{
                          maintainAspectRatio: false,
                          responsive: true,
                          plugins: { legend: { display: false } },
                          scales: {
                            y: { grid: { color: 'rgba(255,255,255,0.04)' }, ticks: { color: '#64748b', font: { weight: 500 } } },
                            x: { grid: { display: false }, ticks: { color: '#64748b', font: { weight: 500 } } }
                          }
                        }}
                      />
                    ) : <div className="loading-spinner"></div>}
                  </div>
                </div>

                <div style={{
                  background: 'linear-gradient(180deg, var(--bg-secondary) 0%, var(--bg-tertiary) 100%)',
                  padding: '32px', borderRadius: '28px', border: '1px solid var(--glass-border)',
                  boxShadow: '0 15px 40px rgba(0,0,0,0.15)', position: 'relative', overflow: 'hidden'
                }}>
                  <div style={{ position: 'absolute', bottom: 0, left: 0, right: 0, height: '60%', background: 'linear-gradient(180deg, transparent 0%, rgba(245, 158, 11, 0.03) 100%)', pointerEvents: 'none' }}></div>
                  <h3 style={{ margin: '0 0 28px 0', color: 'var(--text-main)', fontSize: '1.2rem', fontWeight: 700, display: 'flex', alignItems: 'center', gap: '12px' }}>
                    <div style={{ width: '36px', height: '36px', borderRadius: '10px', background: 'rgba(245, 158, 11, 0.15)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fbbf24' }}>
                      <BarChart3 size={18} />
                    </div>
                    New Charts <span style={{ marginLeft: 'auto', fontSize: '0.85rem', fontWeight: 500, color: 'var(--text-dim)', background: 'var(--bg-tertiary)', padding: '6px 12px', borderRadius: '8px' }}>Last 30 Days</span>
                  </h3>
                  <div style={{ height: '280px', position: 'relative', zIndex: 1 }}>
                    {stats?.trends ? (
                      <Bar
                        data={{
                          labels: stats.trends.charts.map(d => d.date),
                          datasets: [{
                            label: 'Charts Created',
                            data: stats.trends.charts.map(d => d.count),
                            backgroundColor: 'rgba(245, 158, 11, 0.8)',
                            hoverBackgroundColor: '#fbbf24',
                            borderRadius: 8,
                            barThickness: 24
                          }]
                        }}
                        options={{
                          maintainAspectRatio: false,
                          responsive: true,
                          plugins: { legend: { display: false } },
                          scales: {
                            y: { grid: { color: 'rgba(255,255,255,0.04)' }, ticks: { color: '#64748b', font: { weight: 500 } } },
                            x: { grid: { display: false }, ticks: { color: '#64748b', font: { weight: 500 } } }
                          }
                        }}
                      />
                    ) : <div className="loading-spinner"></div>}
                  </div>
                </div>

              </div>
            </div>
          )}

          {/* DASHBOARD TAB (List View) - Non-viewer gets card grid, Viewer gets inline published dashboard */}
          {activeTab === 'dashboard' && !selectedDashboard && (
            isViewer ? (
              /* ===================== VIEWER INLINE DASHBOARD VIEW ===================== */
              <div className="viewer-dashboard-container" style={{
                padding: '24px',
                background: 'var(--bg-primary)',
                minHeight: '100%',
                width: '100%',
                overflowY: 'auto'
              }}>
                {/* Header with dropdown */}
                <div className="viewer-dashboard-header" style={{
                  marginBottom: '24px',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  flexWrap: 'wrap',
                  gap: '16px'
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flex: 1 }}>
                    <Layout size={28} style={{ color: 'var(--accent-primary)' }} />
                    <h1 style={{
                      margin: 0, fontSize: '1.7rem', fontWeight: 800,
                      color: 'var(--text-main)'
                    }}>
                      {viewerDashboardData?.title || 'Dashboard'}
                    </h1>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                    {/* Dashboard Selector Dropdown */}
                    {sharedDashboards.length > 0 && (
                      <div style={{ position: 'relative' }}>
                        <select
                          value={viewerSelectedDashboardId || ''}
                          onChange={(e) => setViewerSelectedDashboardId(Number(e.target.value))}
                          style={{
                            padding: '10px 40px 10px 16px',
                            borderRadius: '12px',
                            border: '1px solid var(--glass-border)',
                            background: 'var(--bg-secondary)',
                            color: 'var(--text-main)',
                            fontSize: '0.9rem',
                            fontWeight: 600,
                            cursor: 'pointer',
                            appearance: 'none',
                            outline: 'none',
                            minWidth: '220px',
                            boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
                            transition: 'all 0.2s ease'
                          }}
                        >
                          {sharedDashboards.map(d => (
                            <option key={d.id} value={d.id}>
                              {d.title || d.name || 'Untitled Dashboard'}
                            </option>
                          ))}
                        </select>
                        <ChevronDown size={16} style={{
                          position: 'absolute', right: '14px', top: '50%',
                          transform: 'translateY(-50%)', pointerEvents: 'none',
                          color: 'var(--text-dim)'
                        }} />
                      </div>
                    )}
                    {/* Review & Comments Button */}
                    {viewerDashboardData && (
                      <button
                        onClick={() => setShowReviewSidebar(true)}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '6px',
                          padding: '8px 16px',
                          borderRadius: '12px',
                          background: 'var(--bg-secondary)',
                          color: 'var(--text-main)',
                          border: '1px solid var(--glass-border)',
                          fontWeight: 600,
                          fontSize: '0.85rem',
                          cursor: 'pointer',
                          transition: 'all 0.2s ease',
                          boxShadow: '0 2px 8px rgba(0,0,0,0.04)'
                        }}
                        onMouseOver={(e) => e.currentTarget.style.background = 'var(--hover-bg)'}
                        onMouseOut={(e) => e.currentTarget.style.background = 'var(--bg-secondary)'}
                      >
                        <MessageSquare size={16} />
                        Review & Comments
                      </button>
                    )}
                  </div>
                </div>

                {/* Review Sidebar for Viewer */}
                {viewerDashboardData && (
                  <ReviewSidebar
                    assetType="dashboard"
                    assetId={viewerDashboardData.id}
                    isOpen={showReviewSidebar}
                    onClose={() => setShowReviewSidebar(false)}
                    currentUser={user}
                    dashboardOwnerId={viewerDashboardData.user_id}
                    onStatusChange={async (newStatus) => {
                      // Reload the viewer dashboard
                      setViewerSelectedDashboardId(prev => {
                        // Trigger re-fetch by toggling
                        setViewerDashboardData(d => d ? { ...d, status: newStatus } : d);
                        return prev;
                      });
                    }}
                  />
                )}

                {viewerDashboardData?.description && (
                  <p style={{
                    color: 'var(--text-dim)', fontSize: '1rem', marginBottom: '24px',
                    marginTop: '-8px', paddingLeft: '40px'
                  }}>
                    {viewerDashboardData.description}
                  </p>
                )}

                {/* Dashboard Content */}
                {viewerDashboardLoading ? (
                  <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', padding: '80px 0' }}>
                    <div className="loading-spinner" style={{ width: '40px', height: '40px' }}></div>
                    <p style={{ marginLeft: '16px', color: 'var(--text-dim)', fontWeight: 500 }}>Loading dashboard...</p>
                  </div>
                ) : viewerDashboardData && viewerDashboardData.charts && viewerDashboardData.charts.length > 0 ? (
                  <div className="dashboard-charts-grid-premium" style={{ display: 'block' }}>
                    <ResponsiveGridLayout
                      key={viewerDashboardData.id}
                      className="layout"
                      layout={viewerDashboardLayout}
                      cols={12}
                      rowHeight={30}
                      margin={[16, 16]}
                      isDraggable={false}
                      isResizable={false}
                      useCSSTransforms={false}
                      compactType={null}
                      preventCollision={false}
                    >
                      {viewerDashboardData.charts.map(chart => (
                        <div
                          key={chart.id ? chart.id.toString() : `chart-${Math.random()}`}
                          className="dashboard-chart-card-premium"
                          style={{
                            position: 'relative',
                            cursor: 'pointer',
                            display: 'flex',
                            flexDirection: 'column',
                            minHeight: 0
                          }}
                          onClick={() => setViewOnlyChart(chart)}
                        >
                          {/* Chart Header */}
                          <div style={{
                            padding: '12px 16px',
                            borderBottom: '1px solid var(--glass-border)',
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center'
                          }}>
                            <h3 style={{
                              margin: 0, fontSize: '1rem', fontWeight: 600,
                              color: 'var(--text-main)'
                            }}>
                              {chart.title}
                            </h3>
                            <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }} onMouseDown={e => e.stopPropagation()}>
                              <button
                                className="inventory-btn-square"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  toggleDataLabel(chart.id);
                                }}
                                title={dataLabelsMap[chart.id] ? "Hide Data Labels" : "Show Data Labels"}
                                style={{ width: '46px', height: '30px', padding: '0 8px', borderRadius: '15px', background: 'var(--bg-tertiary)', border: '1px solid var(--glass-border)', color: 'var(--text-dim)' }}
                              >
                                {dataLabelsMap[chart.id] ? <ToggleRight size={22} className="text-accent-primary toggle-icon" style={{ color: 'inherit' }} /> : <ToggleLeft size={22} style={{ color: 'inherit' }} />}
                              </button>
                              <button
                                className="inventory-btn-square"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleCopyImage(e, chart);
                                }}
                                title="Copy Chart Image"
                                style={{ width: '30px', height: '30px', background: 'var(--bg-tertiary)', border: '1px solid var(--glass-border)', color: 'var(--text-dim)', display: 'flex', alignItems: 'center', justifyContent: 'center', borderRadius: '6px' }}
                              >
                                <Copy size={16} />
                              </button>
                            </div>
                          </div>
                          {/* Chart Body */}
                          <div className="chart-card-body" style={{
                            flex: 1, minHeight: 0, position: 'relative',
                            padding: '16px', overflow: 'hidden'
                          }}>
                            <div style={{ width: '100%', height: '100%' }}>
                              {renderChart({
                                ...chart,
                                chart_type: (() => {
                                  const typeMap = {
                                    'horizontalbar': 'horizontalBar',
                                    'stackedbar': 'stackedBar',
                                    'groupedbar': 'groupedBar',
                                    'multiline': 'multiLine',
                                    'stackedarea': 'stackedArea',
                                    'polararea': 'polarArea',
                                  };
                                  const t = String(chart.chart_type || 'bar').toLowerCase().trim();
                                  return typeMap[t] || chart.chart_type || 'bar';
                                })()
                              }, null, { hideTitle: true, maintainAspectRatio: false, responsive: true })}
                            </div>
                          </div>
                        </div>
                      ))}
                    </ResponsiveGridLayout>
                  </div>
                ) : viewerDashboardData ? (
                  <div style={{
                    textAlign: 'center', padding: '80px 20px',
                    color: 'var(--text-muted)'
                  }}>
                    <Sparkles size={48} color="var(--accent-primary)" style={{ marginBottom: '16px' }} />
                    <h3 style={{ fontWeight: 600, marginBottom: '8px', color: 'var(--text-dim)' }}>This dashboard is empty</h3>
                    <p style={{ fontSize: '0.9rem' }}>No charts have been added to this dashboard yet</p>
                  </div>
                ) : sharedDashboards.length === 0 ? (
                  <div style={{
                    textAlign: 'center', padding: '80px 20px',
                    color: 'var(--text-muted)'
                  }}>
                    <Share2 size={48} strokeWidth={1} style={{ marginBottom: '16px', opacity: 0.3 }} />
                    <h3 style={{ fontWeight: 600, marginBottom: '8px', color: 'var(--text-dim)' }}>No dashboards shared with you</h3>
                    <p style={{ fontSize: '0.9rem' }}>Dashboards shared with you by other users will appear here</p>
                  </div>
                ) : null}

                {/* Footer */}
                {viewerDashboardData && (
                  <div style={{
                    marginTop: '40px', textAlign: 'center', color: 'var(--text-muted)',
                    padding: '20px', borderTop: '1px solid var(--glass-border)'
                  }}>
                    <p style={{ margin: 0, fontSize: '0.85rem' }}>
                      Powered by <strong style={{ color: 'var(--accent-primary)' }}>ChartIQ</strong>
                      {viewerDashboardData.owner_name && <> • Shared by <strong>{viewerDashboardData.owner_name}</strong></>}
                    </p>
                  </div>
                )}
              </div>
            ) : (
              /* ===================== NON-VIEWER CARD GRID VIEW ===================== */
              <div className="dashboard-layout-premium">
                <div className="dashboard-controls" style={{ display: 'flex', gap: '16px', alignItems: 'center', flexWrap: 'wrap' }}>
                  {/* My / Shared Tabs */}
                  <div style={{
                    display: 'flex', background: 'var(--bg-tertiary)', borderRadius: '12px',
                    padding: '4px', border: '1px solid var(--glass-border)'
                  }}>
                    <button
                      onClick={() => { setDashboardViewTab('my'); loadDashboards('my'); }}
                      style={{
                        padding: '8px 20px', borderRadius: '10px', border: 'none', cursor: 'pointer',
                        fontWeight: 600, fontSize: '0.85rem', transition: 'all 0.2s ease',
                        background: dashboardViewTab === 'my' ? 'var(--accent-primary)' : 'transparent',
                        color: dashboardViewTab === 'my' ? 'white' : 'var(--text-dim)'
                      }}
                    >
                      My Dashboards
                    </button>
                    <button
                      onClick={() => { setDashboardViewTab('shared'); loadDashboards('shared'); }}
                      style={{
                        padding: '8px 20px', borderRadius: '10px', border: 'none', cursor: 'pointer',
                        fontWeight: 600, fontSize: '0.85rem', transition: 'all 0.2s ease',
                        background: dashboardViewTab === 'shared' ? 'var(--accent-primary)' : 'transparent',
                        color: dashboardViewTab === 'shared' ? 'white' : 'var(--text-dim)',
                        display: 'flex', alignItems: 'center', gap: '6px'
                      }}
                    >
                      <Share2 size={14} /> Shared with me
                      {sharedDashboards.length > 0 && (
                        <span style={{
                          background: 'rgba(255,255,255,0.2)', borderRadius: '20px',
                          padding: '1px 8px', fontSize: '0.75rem', fontWeight: 700
                        }}>
                          {sharedDashboards.length}
                        </span>
                      )}
                    </button>
                  </div>

                  <div className="dashboard-search-container" style={{ flex: 1, minWidth: '200px' }}>
                    <Search className="dashboard-search-icon" size={18} />
                    <input
                      type="text"
                      placeholder="Search dashboards..."
                      className="dashboard-search-input"
                      value={dashboardSearch}
                      onChange={(e) => setDashboardSearch(e.target.value)}
                    />
                  </div>
                </div>

                <div className="dashboards-grid-premium">
                  {/* Create New Card - only on My tab */}
                  {dashboardViewTab === 'my' && (
                    <div
                      className="dashboard-card-premium new-dashboard-card"
                      onClick={() => setShowDashboardModal(true)}
                    >
                      <div className="new-dashboard-content">
                        <div className="icon-circle">
                          <Plus size={32} />
                        </div>
                        <h3>Create New Dashboard</h3>
                        <p>Start a new visual project</p>
                      </div>
                    </div>
                  )}

                  {/* Dashboard Cards */}
                  {(dashboardViewTab === 'shared' ? sharedDashboards : dashboards)
                    .filter(d => ((d.title || d.name || '').toLowerCase()).includes((dashboardSearch || '').toLowerCase()))
                    .map(dashboard => {
                      const chartCount = Array.isArray(dashboard.chart_ids)
                        ? dashboard.chart_ids.length
                        : (typeof dashboard.chart_ids === 'string' ? (JSON.parse(dashboard.chart_ids || '[]')).length : 0);

                      return (
                        <div
                          key={dashboard.id}
                          className="dashboard-card-premium"
                          onClick={() => loadDashboard(dashboard.id)}
                        >
                          <div className="card-top">
                            <span className="chart-count-badge">
                              {chartCount} Charts
                            </span>
                            <div style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
                              {dashboard.is_shared && (
                                <span style={{
                                  background: 'rgba(6, 182, 212, 0.15)', color: '#06b6d4',
                                  padding: '3px 10px', borderRadius: '20px', fontSize: '0.7rem', fontWeight: 600
                                }}>
                                  Shared
                                </span>
                              )}
                              <StatusBadge status={dashboard.status || (dashboard.published ? 'published' : 'draft')} />

                            </div>
                          </div>

                          <div>
                            <h3 className="card-title">{dashboard.title || dashboard.name || 'Untitled Dashboard'}</h3>
                            <p className="card-desc">{dashboard.description || 'No description provided'}</p>
                          </div>

                          <div className="card-footer">
                            <span className="card-author">
                              {dashboard.user_id !== user?.id ? (dashboard.owner_name || 'Unknown') : (user?.name || 'Customer Insights')}
                            </span>
                            <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                              <span>{new Date(dashboard.created_at || Date.now()).toLocaleDateString()}</span>
                              {!dashboard.is_shared && (
                                <button
                                  onClick={(e) => { e.stopPropagation(); openShareModal('dashboard', dashboard); }}
                                  title="Share Dashboard"
                                  style={{
                                    background: 'rgba(139, 92, 246, 0.1)', border: '1px solid rgba(139, 92, 246, 0.2)',
                                    color: '#8b5cf6', borderRadius: '8px', padding: '4px 8px',
                                    cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '4px',
                                    fontSize: '0.75rem', fontWeight: 600
                                  }}
                                >
                                  <Share2 size={12} /> Share
                                </button>
                              )}
                            </div>
                          </div>
                        </div>
                      );
                    })
                  }

                  {/* Empty state for shared */}
                  {dashboardViewTab === 'shared' && sharedDashboards.length === 0 && (
                    <div style={{
                      gridColumn: '1 / -1', textAlign: 'center', padding: '60px 20px',
                      color: 'var(--text-muted)'
                    }}>
                      <Share2 size={48} strokeWidth={1} style={{ marginBottom: '16px', opacity: 0.3 }} />
                      <h3 style={{ fontWeight: 600, marginBottom: '8px', color: 'var(--text-dim)' }}>No shared dashboards</h3>
                      <p style={{ fontSize: '0.9rem' }}>Dashboards shared with you by other users will appear here</p>
                    </div>
                  )}
                </div>

                {/* Decorative Background Element to fill empty space */}
                <div className="dashboard-watermark-bg">
                  <BarChart3 size={400} strokeWidth={0.5} />
                </div>
              </div>
            )
          )}

          {/* SINGLE DASHBOARD VIEW */}
          {activeTab === 'dashboard' && selectedDashboard && (
            <div className="dashboard-detail-view-premium">
              <button className="back-btn-premium" onClick={() => setSelectedDashboard(null)}>
                <ChevronLeft size={20} />
                <span>Back to Dashboards</span>
              </button>

              <div className="dashboard-detail-header">
                <div className="header-left">
                  {isEditingDashTitle ? (
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <input
                        type="text"
                        value={tempDashTitle}
                        autoFocus
                        onChange={(e) => setTempDashTitle(e.target.value)}
                        onKeyDown={async (e) => {
                          if (e.key === 'Enter') {
                            if (!tempDashTitle.trim()) return;
                            await fetch(`${API_BASE}/dashboards/${selectedDashboard.id}`, {
                              method: 'PUT',
                              headers: { 'Content-Type': 'application/json' },
                              credentials: 'include',
                              body: JSON.stringify({ title: tempDashTitle.trim() })
                            });
                            setSelectedDashboard(prev => ({ ...prev, title: tempDashTitle.trim() }));
                            setDashboards(prev => prev.map(d => d.id === selectedDashboard.id ? { ...d, title: tempDashTitle.trim() } : d));
                            setIsEditingDashTitle(false);
                            showToast('Dashboard renamed!', 'success');
                          }
                          if (e.key === 'Escape') setIsEditingDashTitle(false);
                        }}
                        style={{
                          fontSize: '1.6rem', fontWeight: 800, color: 'var(--text-main)',
                          background: 'var(--bg-primary)', border: '2px solid var(--accent-primary)',
                          borderRadius: '8px', padding: '4px 10px', outline: 'none', minWidth: '260px'
                        }}
                      />
                      <button
                        className="btn-primary"
                        style={{ padding: '6px 14px', fontSize: '0.85rem' }}
                        onClick={async () => {
                          if (!tempDashTitle.trim()) return;
                          await fetch(`${API_BASE}/dashboards/${selectedDashboard.id}`, {
                            method: 'PUT',
                            headers: { 'Content-Type': 'application/json' },
                            credentials: 'include',
                            body: JSON.stringify({ title: tempDashTitle.trim() })
                          });
                          setSelectedDashboard(prev => ({ ...prev, title: tempDashTitle.trim() }));
                          setDashboards(prev => prev.map(d => d.id === selectedDashboard.id ? { ...d, title: tempDashTitle.trim() } : d));
                          setIsEditingDashTitle(false);
                          showToast('Dashboard renamed!', 'success');
                        }}
                      >Save</button>
                      <button
                        className="btn-secondary"
                        style={{ padding: '6px 14px', fontSize: '0.85rem' }}
                        onClick={() => setIsEditingDashTitle(false)}
                      >Cancel</button>
                    </div>
                  ) : (
                    <h1
                      className="dashboard-title-premium"
                      style={{ cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '8px' }}
                      title="Click to rename dashboard"
                      onClick={() => { setTempDashTitle(selectedDashboard.title || ''); setIsEditingDashTitle(true); }}
                    >
                      {selectedDashboard.title || selectedDashboard.name || 'Untitled Dashboard'}
                      <Edit3 size={16} style={{ opacity: 0.4, flexShrink: 0 }} />
                    </h1>
                  )}
                  {selectedDashboard.description && (
                    <p className="dashboard-desc-premium">{selectedDashboard.description}</p>
                  )}
                </div>

                <div className="header-right">
                  <div className="dashboard-actions-group">
                    {/* Copy/Download Options Menu - Moved to start */}
                    {(isSuperAdmin || userRole === 'workspace_admin' || userRole === 'builder') && (
                      <div style={{ position: 'relative' }} ref={copyMenuRef}>
                        <button
                          className="dashboard-action-btn"
                          onClick={(e) => {
                            e.stopPropagation();
                            setShowCopyMenu(!showCopyMenu);
                          }}
                          title="Copy & Download Options"
                        >
                          <Copy size={18} />
                        </button>

                        {showCopyMenu && (
                          <div className="dropdown-menu-premium" style={{ top: '100%', left: 0, right: 'auto', marginTop: '8px', zIndex: 100 }}>
                            {selectedDashboard.share_id && (
                              <button
                                className="dropdown-item-premium"
                                onClick={() => {
                                  copyToClipboard(`${window.location.origin}/shared/${selectedDashboard.share_id}`);
                                  setShowCopyMenu(false);
                                }}
                              >
                                <Link size={16} />
                                <span>Copy Public Link</span>
                              </button>
                            )}

                            <button
                              className="dropdown-item-premium"
                              onClick={() => {
                                handleCopyDashboardImage();
                                setShowCopyMenu(false);
                              }}
                            >
                              <Image size={16} />
                              <span>Copy Dashboard Image</span>
                            </button>

                            <button
                              className="dropdown-item-premium"
                              onClick={() => {
                                downloadDashboardImage();
                                setShowCopyMenu(false);
                              }}
                            >
                              <Download size={16} />
                              <span>Download Image</span>
                            </button>
                          </div>
                        )}
                      </div>
                    )}

                    <button className="dashboard-action-btn" onClick={generateDashboardSummary} title="Generate Summary">
                      <FileText size={18} />
                    </button>

                    {isBuilder && (
                      <>
                        {dashboardMode === 'view' ? (
                          <button
                            className="dashboard-action-btn"
                            onClick={() => setDashboardMode('edit')}
                            title="Edit Dashboard"
                          >
                            <Edit3 size={18} />
                          </button>
                        ) : (
                          <div style={{ display: 'flex', gap: '8px' }}>
                            <button
                              className="dashboard-action-btn"
                              onClick={cancelLayoutEdit}
                              title="Cancel Edit"
                              style={{ color: '#ef4444' }}
                            >
                              <X size={18} />
                            </button>
                            <button
                              className="dashboard-action-btn primary"
                              onClick={() => {
                                saveLayout();
                                setDashboardMode('view');
                              }}
                              style={{ background: 'var(--accent-primary)', color: 'white', borderColor: 'var(--accent-primary)' }}
                              title="Save & Exit"
                            >
                              <Save size={18} />
                            </button>
                          </div>
                        )}
                      </>
                    )}

                    {(isSuperAdmin || userRole === 'workspace_admin') && (
                      <>
                        {!selectedDashboard.share_id ? (
                          <>
                            {(selectedDashboard.approval_count > 0 || userRole === 'super_admin' || userRole === 'admin') ? (
                              <button className="dashboard-action-btn primary-gradient" onClick={publishDashboard} title="Publish Dashboard">
                                <Share2 size={18} />
                              </button>
                            ) : (
                              <button
                                className="dashboard-action-btn"
                                style={{
                                  opacity: 0.5,
                                  cursor: 'not-allowed',
                                  background: 'rgba(139, 92, 246, 0.1)',
                                  borderColor: '#8b5cf6',
                                  color: '#8b5cf6'
                                }}
                                title="Need at least 1 approval to publish"
                                disabled
                              >
                                <Share2 size={18} />
                              </button>
                            )}
                          </>
                        ) : (
                          <button className="dashboard-action-btn danger" style={{ background: 'rgba(239, 68, 68, 0.1)', color: '#ef4444', borderColor: '#ef4444' }} onClick={unpublishDashboard} title="Unpublish Dashboard">
                            <X size={18} />
                          </button>
                        )}
                        {/* Password View Button - only when published */}
                        {selectedDashboard.share_id && (
                          <button
                            className="dashboard-action-btn"
                            style={{ background: 'rgba(16, 185, 129, 0.1)', color: '#10b981', borderColor: '#10b981' }}
                            title="View/Manage Share Password"
                            onClick={() => { fetchDashboardPassword(selectedDashboard.id); setShowPasswordModal(true); }}
                          >
                            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="11" width="18" height="11" rx="2" ry="2" /><path d="M7 11V7a5 5 0 0 1 10 0v4" /></svg>
                          </button>
                        )}
                      </>
                    )}



                    {/* Builder publish button - only for pure 'builder' role (not admin/workspace_admin who already have their own above) */}
                    {userRole === 'builder' && !selectedDashboard.share_id && (
                      <>
                        {selectedDashboard.approval_count > 0 ? (
                          <button className="dashboard-action-btn primary-gradient" onClick={publishDashboard}>
                            <Share2 size={18} />
                            <span>Publish</span>
                          </button>
                        ) : (
                          <button
                            className="dashboard-action-btn"
                            style={{
                              opacity: 0.5,
                              cursor: 'not-allowed',
                              background: 'rgba(139, 92, 246, 0.1)',
                              borderColor: '#8b5cf6',
                              color: '#8b5cf6'
                            }}
                            title="Need at least 1 approval to publish"
                            disabled
                          >
                            <Share2 size={18} />
                            <span>Publish (Needs Approval)</span>
                          </button>
                        )}
                      </>
                    )}

                    {/* Builder published state - Unpublish & Password */}
                    {userRole === 'builder' && selectedDashboard.share_id && (
                      <>
                        <button className="dashboard-action-btn danger" style={{ background: 'rgba(239, 68, 68, 0.1)', color: '#ef4444', borderColor: '#ef4444' }} onClick={unpublishDashboard}>
                          <X size={18} />
                          <span>Unpublish</span>
                        </button>
                        <button
                          className="dashboard-action-btn"
                          style={{ background: 'rgba(16, 185, 129, 0.1)', color: '#10b981', borderColor: '#10b981' }}
                          title="View/Manage Share Password"
                          onClick={() => { fetchDashboardPassword(selectedDashboard.id); setShowPasswordModal(true); }}
                        >
                          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="11" width="18" height="11" rx="2" ry="2" /><path d="M7 11V7a5 5 0 0 1 10 0v4" /></svg>
                          <span>Password</span>
                        </button>
                      </>
                    )}

                    {isBuilder && (
                      <button className="dashboard-action-btn danger" title="Delete Dashboard" onClick={deleteDashboard}>
                        <Trash2 size={18} />
                      </button>
                    )}
                  </div>
                </div>
              </div>

              {/* Status Bar for Dashboard Detail */}
              <div style={{
                margin: '0 24px 16px 24px',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                background: 'var(--bg-secondary)',
                border: '1px solid var(--glass-border)',
                padding: '12px 20px',
                borderRadius: '12px',
                boxShadow: '0 2px 8px rgba(0,0,0,0.04)'
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px', color: 'var(--text-main)' }}>
                  <StatusBadge status={selectedDashboard.status} />
                  {selectedDashboard.status === 'stakeholder_approved' && (
                    <span style={{ color: '#f59e0b', display: 'flex', alignItems: 'center' }}>
                      <Clock size={14} style={{ marginRight: '4px' }} /> Stakeholder Approved
                    </span>
                  )}
                  {selectedDashboard.status === 'rejected' && (
                    <span style={{ fontSize: '0.85rem', color: '#ef4444' }}>
                      Updates required. Check review comments.
                    </span>
                  )}
                </div>

                <div style={{ display: 'flex', gap: '8px' }}>
                  {/* Submit for Review Button (Builder only) */}
                  {isBuilder && (selectedDashboard.status === 'draft' || !selectedDashboard.status) && (selectedDashboard.approval_count === 0 || !selectedDashboard.approval_count) && (
                    <button
                      className="btn-primary"
                      style={{ background: '#f59e0b', borderColor: '#d97706', display: 'flex', alignItems: 'center', color: 'white' }}
                      onClick={submitForReview}
                    >
                      <Send size={16} style={{ marginRight: '6px' }} /> Submit for Review
                    </button>
                  )}



                  <button
                    onClick={() => setShowReviewSidebar(true)}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '6px',
                      padding: '8px 16px',
                      borderRadius: '8px',
                      background: 'var(--bg-tertiary)',
                      color: 'var(--text-main)',
                      border: '1px solid var(--glass-border)',
                      fontWeight: 600,
                      fontSize: '0.9rem',
                      cursor: 'pointer',
                      transition: 'all 0.2s ease'
                    }}
                    onMouseOver={(e) => e.currentTarget.style.background = 'var(--hover-bg)'}
                    onMouseOut={(e) => e.currentTarget.style.background = 'var(--bg-tertiary)'}
                  >
                    <MessageSquare size={18} />
                    Review & Comments
                  </button>
                </div>
              </div>

              {/* Review Sidebar Trigger if applicable */}
              <ReviewSidebar
                assetType="dashboard"
                assetId={selectedDashboard.id}
                isOpen={showReviewSidebar}
                onClose={() => setShowReviewSidebar(false)}
                currentUser={user}
                dashboardOwnerId={selectedDashboard.user_id}
                onStatusChange={async (newStatus) => {
                  setSelectedDashboard(prev => ({ ...prev, status: newStatus }));
                  loadDashboards('my');
                  await loadDashboard(selectedDashboard.id); // Reload to get approval_count
                }}
              />

              <div className="dashboard-charts-grid-premium" style={{ display: 'block' }}>
                {selectedDashboard.charts && selectedDashboard.charts.length > 0 ? (
                  <ResponsiveGridLayout
                    key={selectedDashboard.id}
                    className="layout"
                    layout={dashboardLayout}
                    cols={12}
                    rowHeight={30}
                    margin={[16, 16]}
                    onLayoutChange={onLayoutChange}
                    onDragStart={() => { isLayoutDraggingRef.current = true; }}
                    onDrag={() => { isLayoutDraggingRef.current = true; }}
                    onDragStop={() => { setTimeout(() => { isLayoutDraggingRef.current = false; }, 250); }}
                    onResizeStart={() => { isLayoutDraggingRef.current = true; }}
                    onResize={() => { isLayoutDraggingRef.current = true; }}
                    onResizeStop={() => { setTimeout(() => { isLayoutDraggingRef.current = false; }, 300); }}
                    isDraggable={dashboardMode === 'edit'}
                    isResizable={dashboardMode === 'edit'}
                    draggableHandle=".drag-handle"
                    useCSSTransforms={false}
                    compactType={null}
                    preventCollision={false}
                  >
                    {selectedDashboard.charts.map(chart => (
                      <div
                        key={chart.id ? chart.id.toString() : `chart-${Math.random()}`}
                        className="dashboard-chart-card-premium"
                        onClickCapture={(e) => {
                          if (isLayoutDraggingRef.current) {
                            e.preventDefault();
                            e.stopPropagation();
                            return;
                          }
                        }}
                        onClick={(e) => {
                          if (isLayoutDraggingRef.current || dashboardMode === 'edit') {
                            e.stopPropagation();
                            return;
                          }
                          setViewOnlyChart(chart);
                        }}
                        style={{ position: 'relative', cursor: 'pointer', display: 'flex', flexDirection: 'column', minHeight: 0 }}
                      >
                        {/* Status Badge */}
                        {chart.status === 'rejected' && (
                          <div style={{ position: 'absolute', top: 5, right: 5, zIndex: 10 }}>
                            <StatusBadge status="rejected" />
                          </div>
                        )}

                        {/* Header with Drag Handle */}
                        <div className="chart-card-header drag-handle" style={{ cursor: dashboardMode === 'edit' ? 'move' : 'default', padding: '12px 16px', borderBottom: '1px solid var(--glass-border)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <h3 style={{ margin: 0, fontSize: '1rem', fontWeight: 600, color: 'var(--text-main)' }}>{chart.title}</h3>
                          <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }} onMouseDown={e => e.stopPropagation()}>
                            {/* Toggle Data Labels Button */}
                            <button
                              className="inventory-btn-square"
                              onClick={(e) => {
                                e.stopPropagation();
                                toggleDataLabel(chart.id);
                              }}
                              title={dataLabelsMap[chart.id] ? "Hide Data Labels" : "Show Data Labels"}
                              style={{ width: '46px', height: '30px', padding: '0 8px', borderRadius: '15px' }}
                            >
                              {dataLabelsMap[chart.id] ? <ToggleRight size={22} className="text-accent-primary toggle-icon" style={{ color: 'inherit' }} /> : <ToggleLeft size={22} style={{ color: 'inherit' }} />}
                            </button>

                            {/* Copy Image Button */}
                            <button
                              className="inventory-btn-square"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleCopyImage(e, chart);
                              }}
                              title="Copy Chart Image"
                            >
                              <Copy size={16} />
                            </button>

                            {/* Change Type Menu */}
                            {!isViewer && (
                              <>
                                <div style={{ position: 'relative' }}>
                                  <button
                                    className="inventory-btn-square"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      setActiveChartTypeMenu(activeChartTypeMenu === chart.id ? null : chart.id);
                                    }}
                                    title="Change Chart Type"
                                    style={{ width: '30px', height: '30px' }}
                                  >
                                    <Settings size={14} />
                                  </button>
                                  {activeChartTypeMenu === chart.id && (
                                    <div style={{
                                      position: 'absolute',
                                      top: '36px',
                                      right: 0,
                                      background: 'var(--bg-secondary)',
                                      border: '1px solid var(--glass-border)',
                                      borderRadius: '8px',
                                      padding: '4px',
                                      zIndex: 100,
                                      boxShadow: '0 4px 12px rgba(0,0,0,0.2)',
                                      display: 'flex',
                                      flexDirection: 'column',
                                      gap: '2px',
                                      minWidth: '160px'
                                    }} onMouseDown={e => e.stopPropagation()}>
                                      {['bar', 'horizontalBar', 'stackedBar', 'groupedBar', 'line', 'multiLine', 'area', 'stackedArea', 'pie', 'donut', 'scatter', 'bubble', 'radar', 'polarArea'].map(type => (
                                        <button
                                          key={type}
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            changeChartType(chart.id, type);
                                          }}
                                          style={{
                                            padding: '8px 12px',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'space-between',
                                            border: 'none',
                                            background: 'transparent',
                                            color: 'var(--text-main)',
                                            fontSize: '0.85rem',
                                            cursor: 'pointer',
                                            borderRadius: '6px',
                                            textAlign: 'left',
                                            textTransform: 'capitalize'
                                          }}
                                          onMouseOver={e => { if ((chart.chart_type || 'bar') !== type) e.currentTarget.style.background = 'var(--hover-bg)'; }}
                                          onMouseOut={e => { if ((chart.chart_type || 'bar') !== type) e.currentTarget.style.background = 'transparent'; }}
                                        >
                                          {type.replace(/([A-Z])/g, ' $1').trim()}
                                          {(chart.chart_type || 'bar') === type && <Check size={12} />}
                                        </button>
                                      ))}
                                    </div>
                                  )}
                                </div>
                                <button
                                  className="inventory-btn-square"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setCurrentChart(chart);
                                    setChartStyle({
                                      type: chart.chart_type || 'bar',
                                      title: chart.title || '',
                                      colors: chart.colors || colorPresets.professional,
                                      theme: 'professional',
                                      individualColors: {},
                                      highlightIndices: []
                                    });
                                    setMessages(chart.messages || []);
                                    setActiveTab('chat');
                                  }}
                                  title="Open in Editor"
                                  style={{ width: '30px', height: '30px' }}
                                >
                                  <Edit3 size={14} />
                                </button>
                              </>
                            )}
                            <button
                              className="inventory-btn-square"
                              onClick={(e) => {
                                e.stopPropagation();
                                setViewDataContent(chart);
                                setShowViewDataModal(true);
                              }}
                              title="View Data Source"
                              style={{ width: '30px', height: '30px' }}
                            >
                              <Table size={14} />
                            </button>
                            {dashboardMode === 'edit' && (
                              <button
                                className="remove-chart-btn"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  removeChartFromDashboard(chart.id);
                                }}
                              >
                                <X size={16} />
                              </button>
                            )}
                          </div>
                        </div>

                        {/* Chart Body */}
                        < div className="chart-card-body" style={{ flex: 1, minHeight: 0, position: 'relative', padding: '16px', overflow: 'hidden' }}>
                          <div style={{ width: '100%', height: '100%' }}>
                            {renderChart({
                              ...chart,
                              chart_type: (() => {
                                const typeMap = { 'horizontalbar': 'horizontalBar', 'stackedbar': 'stackedBar', 'groupedbar': 'groupedBar', 'multiline': 'multiLine', 'stackedarea': 'stackedArea', 'polararea': 'polarArea' };
                                const t = String(chart.chart_type || 'bar').toLowerCase().trim();
                                return typeMap[t] || chart.chart_type || 'bar';
                              })()
                            }, null, { hideTitle: true, maintainAspectRatio: false, responsive: true })}
                          </div>
                        </div>
                      </div>
                    ))}
                  </ResponsiveGridLayout>
                ) : (
                  <div className="empty-dashboard-state">
                    <Sparkles size={48} color="#8b5cf6" />
                    <h2>This dashboard is empty</h2>
                    <p>Go to the Charts tab to create and add some insights!</p>
                    <button className="btn-primary" onClick={() => setActiveTab('chat')}>
                      Go to Charts
                    </button>
                  </div>
                )}
              </div>
            </div>
          )
          }

          {/* Explore Data Tab */}
          {
            !isViewer && activeTab === 'explore' && (
              <div className="explore-layout-premium">
                <aside className="explore-sidebar-premium">
                  <div className="sidebar-header-premium">
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
                      <h2 style={{ margin: 0 }}>Tables</h2>
                      <button
                        onClick={() => loadAvailableTables(true)}
                        className={`refresh-btn-premium ${isRefreshing ? 'spinning' : ''}`}
                        title="Refresh Database Schema"
                        style={{
                          background: 'transparent',
                          border: 'none',
                          color: 'var(--text-dim)',
                          cursor: 'pointer',
                          padding: '4px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          transition: 'color 0.2s'
                        }}
                      >
                        <RefreshCw size={18} />
                      </button>
                    </div>

                    {/* Features temporarily hidden
                    <div style={{ display: 'flex', gap: '8px', marginBottom: '8px' }}>
                      <button
                        onClick={() => setShowCombineModal(true)}
                        className="btn-action-premium"
                        title="Combine Datasets (Join/Union)"
                        style={{ flex: 1, padding: '6px 0', fontSize: '0.8rem', justifyContent: 'center' }}
                      >
                        <Combine size={14} style={{ marginRight: '6px' }} /> Combine
                      </button>
                      <button
                        onClick={() => setShowTransformModal(true)}
                        className="btn-action-premium"
                        title="Transform Data (Formula/Filter/Sample)"
                        style={{ flex: 1, padding: '6px 0', fontSize: '0.8rem', justifyContent: 'center' }}
                      >
                        <Scissors size={14} style={{ marginRight: '6px' }} /> Transform
                      </button>
                    </div>

                    <button
                      onClick={() => setShowRestructureModal(true)}
                      className="btn-action-premium"
                      title="Restructure Data (Transpose/Pivot/Summarize)"
                      style={{ width: '100%', padding: '8px 0', fontSize: '0.85rem', justifyContent: 'center', background: 'rgba(245, 158, 11, 0.1)', borderColor: 'rgba(245, 158, 11, 0.3)', color: '#f59e0b' }}
                    >
                      <Shuffle size={14} style={{ marginRight: '8px' }} /> Restructure Data
                    </button>
                    */}
                  </div>

                  <div className="explore-nav-groups">
                    <div className={`nav-group-premium ${isDatabaseExpanded ? '' : 'collapsed'}`}>
                      <div className="group-header" onClick={() => setIsDatabaseExpanded(!isDatabaseExpanded)} style={{ cursor: 'pointer' }}>
                        <Database size={16} />
                        <span>Data Sources</span>
                        {isDatabaseExpanded ? (
                          <ChevronDown size={14} className="group-chevron" />
                        ) : (
                          <ChevronRight size={14} className="group-chevron" />
                        )}
                      </div>
                      {isDatabaseExpanded && (
                        <div className="group-items" style={{ padding: 0 }}>
                          {/* Group tables by source */}
                          {(() => {
                            const sources = {};
                            availableTables.forEach(table => {
                              const sourceKey = table.source?.id != null ? String(table.source.id) : 'main';
                              if (!sources[sourceKey]) {
                                sources[sourceKey] = {
                                  id: table.source?.id || null,
                                  name: table.source?.name || 'Main Database',
                                  type: table.source?.type || 'built_in',
                                  tables: []
                                };
                              }
                              sources[sourceKey].tables.push(table);
                            });

                            return Object.entries(sources).map(([sourceKey, source]) => (
                              <div key={sourceKey} style={{ marginBottom: '2px' }}>
                                <div
                                  onClick={() => setExpandedSources(prev => ({ ...prev, [sourceKey]: !prev[sourceKey] }))}
                                  style={{
                                    display: 'flex', alignItems: 'center', gap: '8px', padding: '8px 12px',
                                    cursor: 'pointer', fontSize: '0.82rem', fontWeight: '600',
                                    color: 'var(--text-main)', borderBottom: '1px solid rgba(255,255,255,0.04)',
                                    background: expandedSources[sourceKey] ? 'rgba(139, 92, 246, 0.05)' : 'transparent',
                                    transition: 'background 0.15s'
                                  }}
                                >
                                  {source.type === 'built_in' ? <Database size={13} style={{ color: '#3b82f6', flexShrink: 0 }} /> :
                                    source.type === 'postgresql' ? <Database size={13} style={{ color: '#10b981', flexShrink: 0 }} /> :
                                      source.type === 'rest_api' ? <Globe size={13} style={{ color: '#f59e0b', flexShrink: 0 }} /> :
                                        <Zap size={13} style={{ color: '#8b5cf6', flexShrink: 0 }} />}
                                  <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{source.name}</span>
                                  <span style={{ fontSize: '0.7rem', color: 'var(--text-dim)', flexShrink: 0 }}>{source.tables.length}</span>
                                  {expandedSources[sourceKey] ? <ChevronDown size={12} /> : <ChevronRight size={12} />}
                                </div>
                                {expandedSources[sourceKey] && source.tables.map(table => (
                                  <div
                                    key={`${sourceKey}-${table.name}`}
                                    className={`table-item-premium ${selectedTables.includes(table.name) && activeConnectionId === source.id ? 'active' : ''}`}
                                    onClick={() => {
                                      setSelectedTables([table.name]);
                                      setActiveConnectionId(source.id);
                                      setJoinConfig([]);
                                      setDataFilters({});
                                      setSelectedColumns([]);
                                      exploreData([table.name], [], {}, []);
                                    }}
                                    style={{ paddingLeft: '32px' }}
                                  >
                                    <Table size={14} className="item-icon" />
                                    <span className="item-name">{table.name}</span>
                                    {selectedTables.includes(table.name) && activeConnectionId === source.id ? (
                                      <Check size={14} className="active-check" />
                                    ) : (
                                      <ChevronRight size={14} className="item-chevron" />
                                    )}
                                  </div>
                                ))}
                              </div>
                            ));
                          })()}
                        </div>
                      )}
                    </div>


                  </div>
                </aside>

                <main className="explore-main-premium">
                  <div className="data-glass-panel">
                    <header className="data-panel-header">
                      <div className="header-left-actions">
                        <h3>Data</h3>
                        <div className="data-search-wrapper">
                          <Search size={16} />
                          <input
                            type="text"
                            placeholder="Search..."
                            value={dataSearch}
                            onChange={(e) => setDataSearch(e.target.value)}
                          />
                        </div>
                      </div>
                      <button className="export-btn-premium" onClick={handleExportData}>
                        <Download size={16} />
                        <span>Export</span>
                      </button>
                    </header>

                    {!exploredData ? (
                      <div className="data-empty-state">
                        <Database size={64} strokeWidth={1} />
                        <h2>Select a table to view data</h2>
                        <p>Browse through the database categories on the left</p>
                      </div>
                    ) : (
                      <div className="data-table-viewport">
                        <table className="premium-data-table">
                          <thead>
                            <tr>
                              {exploredData.columns.map(col => (
                                <th key={col.name} style={{ userSelect: 'none', position: 'relative' }}>
                                  <div className="th-content" style={{ display: 'flex', alignItems: 'center', gap: '6px', justifyContent: 'space-between' }}>
                                    <div
                                      onClick={() => handleDataSort(col.name)}
                                      style={{ cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '6px', flex: 1 }}
                                    >
                                      <span>{col.name}</span>
                                      {dataSortConfig.key === col.name ? (
                                        dataSortConfig.direction === 'asc' ? <ChevronUp size={14} className="text-accent" /> : <ChevronDown size={14} className="text-accent" />
                                      ) : (
                                        <ChevronDown size={14} style={{ opacity: 0.3 }} />
                                      )}
                                    </div>

                                    <div
                                      className={`filter-trigger-premium ${columnFilters[col.name]?.value ? 'active' : ''}`}
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        setActiveFilterColumn(activeFilterColumn === col.name ? null : col.name);
                                      }}
                                    >
                                      <Filter size={12} />
                                    </div>
                                  </div>

                                  {activeFilterColumn === col.name && (
                                    <div className="column-filter-dropdown-premium" onClick={(e) => e.stopPropagation()}>
                                      <div className="filter-group-premium">
                                        <label className="filter-title-premium">Filter {col.name}</label>
                                        <select
                                          value={columnFilters[col.name]?.type || 'contains'}
                                          onChange={(e) => setColumnFilters({
                                            ...columnFilters,
                                            [col.name]: { ...(columnFilters[col.name] || {}), type: e.target.value }
                                          })}
                                          className="filter-select-premium"
                                        >
                                          <option value="contains">Contains</option>
                                          <option value="equals">Equals</option>
                                          <option value=">">Greater than</option>
                                          <option value="<">Less than</option>
                                          <option value=">=">Greater or Equal</option>
                                          <option value="<=">Less or Equal</option>
                                        </select>
                                        <input
                                          type="text"
                                          autoFocus
                                          placeholder="Value..."
                                          value={columnFilters[col.name]?.value || ''}
                                          onChange={(e) => setColumnFilters({
                                            ...columnFilters,
                                            [col.name]: { ...(columnFilters[col.name] || {}), value: e.target.value }
                                          })}
                                          onKeyDown={(e) => { if (e.key === 'Enter') setActiveFilterColumn(null); }}
                                          className="filter-input-premium"
                                        />
                                        <div className="filter-actions-premium">
                                          <button
                                            className="filter-btn-premium clear"
                                            onClick={() => {
                                              const newFilters = { ...columnFilters };
                                              delete newFilters[col.name];
                                              setColumnFilters(newFilters);
                                              setActiveFilterColumn(null);
                                            }}
                                          >
                                            Clear
                                          </button>
                                          <button
                                            className="filter-btn-premium apply"
                                            onClick={() => setActiveFilterColumn(null)}
                                          >
                                            Apply
                                          </button>
                                        </div>
                                      </div>
                                    </div>
                                  )}
                                </th>
                              ))}
                            </tr>
                          </thead>
                          <tbody>
                            {(() => {
                              const filtered = exploredData.data.filter(row => {
                                // 1. Global Search
                                let matchesGlobal = true;
                                if (dataSearch) {
                                  const searchLower = dataSearch.toLowerCase();
                                  matchesGlobal = exploredData.columns.some(col => {
                                    const val = row[col.name];
                                    return String(val ?? '').toLowerCase().includes(searchLower);
                                  });
                                }
                                if (!matchesGlobal) return false;

                                // 2. Column Filters
                                return Object.entries(columnFilters).every(([colName, filter]) => {
                                  if (!filter.value) return true;
                                  const rowVal = row[colName];
                                  const filterVal = filter.value;
                                  const type = filter.type || 'contains';

                                  // Numeric conversion if possible
                                  const numericRowVal = parseFloat(rowVal);
                                  const numericFilterVal = parseFloat(filterVal);
                                  const isNumeric = !isNaN(numericRowVal) && !isNaN(numericFilterVal);

                                  switch (type) {
                                    case 'contains':
                                      return String(rowVal ?? '').toLowerCase().includes(String(filterVal).toLowerCase());
                                    case 'equals':
                                      return String(rowVal ?? '').toLowerCase() === String(filterVal).toLowerCase();
                                    case '>':
                                      return isNumeric ? numericRowVal > numericFilterVal : rowVal > filterVal;
                                    case '<':
                                      return isNumeric ? numericRowVal < numericFilterVal : rowVal < filterVal;
                                    case '>=':
                                      return isNumeric ? numericRowVal >= numericFilterVal : rowVal >= filterVal;
                                    case '<=':
                                      return isNumeric ? numericRowVal <= numericFilterVal : rowVal <= filterVal;
                                    default:
                                      return true;
                                  }
                                });
                              });

                              const sorted = [...filtered].sort((a, b) => {
                                if (!dataSortConfig.key) return 0;
                                const aVal = a[dataSortConfig.key] ?? '';
                                const bVal = b[dataSortConfig.key] ?? '';

                                if (aVal < bVal) return dataSortConfig.direction === 'asc' ? -1 : 1;
                                if (aVal > bVal) return dataSortConfig.direction === 'asc' ? 1 : -1;
                                return 0;
                              });

                              const paginated = sorted.slice(
                                (dataCurrentPage - 1) * dataRowsPerPage,
                                dataCurrentPage * dataRowsPerPage
                              );

                              return paginated.map((row, idx) => (
                                <tr key={idx} className="data-table-row">
                                  {exploredData.columns.map(col => (
                                    <td key={col.name}>
                                      {typeof row[col.name] === 'object'
                                        ? JSON.stringify(row[col.name])
                                        : String(row[col.name] ?? '')}
                                    </td>
                                  ))}
                                </tr>
                              ));
                            })()}
                          </tbody>
                        </table>
                      </div>
                    )}

                    {exploredData && (
                      <footer className="data-panel-footer">
                        {(() => {
                          const filteredRows = exploredData.data.filter(row => {
                            if (!dataSearch) return true;
                            const searchLower = dataSearch.toLowerCase();
                            return exploredData.columns.some(col => {
                              const val = row[col.name];
                              return String(val ?? '').toLowerCase().includes(searchLower);
                            });
                          });
                          const totalPages = Math.ceil(filteredRows.length / dataRowsPerPage) || 1;

                          return (
                            <>
                              <div className="pagination-info">
                                Page {dataCurrentPage} of {totalPages}
                              </div>
                              <div className="table-controls-footer">
                                <div className="rows-per-page" style={{ position: 'relative', display: 'flex', alignItems: 'center', gap: '8px' }}>
                                  <select
                                    value={dataRowsPerPage}
                                    onChange={(e) => {
                                      setDataRowsPerPage(Number(e.target.value));
                                      setDataCurrentPage(1);
                                    }}
                                    style={{
                                      appearance: 'none', background: 'transparent', border: 'none',
                                      color: 'inherit', fontWeight: 'bold', cursor: 'pointer', paddingRight: '16px'
                                    }}
                                  >
                                    <option value={10}>10 rows</option>
                                    <option value={25}>25 rows</option>
                                    <option value={50}>50 rows</option>
                                    <option value={100}>100 rows</option>
                                  </select>
                                  <ChevronDown size={14} style={{ position: 'absolute', right: 0, pointerEvents: 'none' }} />
                                </div>
                                <div className="pagination-arrows">
                                  <button
                                    disabled={dataCurrentPage === 1}
                                    onClick={() => setDataCurrentPage(p => Math.max(1, p - 1))}
                                    title="Previous Page"
                                  >
                                    <ChevronLeft size={16} />
                                  </button>

                                  <button style={{
                                    background: 'var(--accent-primary)',
                                    color: 'white',
                                    border: 'none',
                                    minWidth: '32px',
                                    height: '32px',
                                    borderRadius: '8px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    fontSize: '0.9rem',
                                    fontWeight: 'bold',
                                    cursor: 'default',
                                    boxShadow: '0 2px 5px rgba(139, 92, 246, 0.3)'
                                  }}>
                                    {dataCurrentPage}
                                  </button>

                                  <button
                                    disabled={dataCurrentPage >= totalPages}
                                    onClick={() => setDataCurrentPage(p => Math.min(totalPages, p + 1))}
                                    title="Next Page"
                                  >
                                    <ChevronRight size={16} />
                                  </button>
                                </div>
                              </div>
                            </>
                          );
                        })()}
                      </footer>
                    )}
                  </div>
                </main>

                {/* AI Data Chat Panel */}
                {dataChatOpen && selectedTables.length > 0 && (
                  <aside className="data-chat-sidebar" style={{
                    width: '380px',
                    background: 'var(--bg-secondary)',
                    borderLeft: '1px solid var(--glass-border)',
                    display: 'flex',
                    flexDirection: 'column',
                    flexShrink: 0
                  }}>
                    <header style={{
                      padding: '20px 24px',
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      borderBottom: '1px solid var(--glass-border)'
                    }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                        <Sparkles size={18} style={{ color: '#a78bfa' }} />
                        <h3 style={{ margin: 0, fontSize: '1rem', fontWeight: 700 }}>AI Data Assistant</h3>
                      </div>
                      <button onClick={() => setDataChatOpen(false)} style={{
                        background: 'transparent', border: 'none', color: 'var(--text-dim)',
                        cursor: 'pointer', padding: '4px', display: 'flex'
                      }}>
                        <X size={18} />
                      </button>
                    </header>

                    <div style={{ flex: 1, overflowY: 'auto', padding: '16px', display: 'flex', flexDirection: 'column', gap: '12px' }}>
                      {dataChatMessages.length === 0 ? (
                        <div style={{ textAlign: 'center', padding: '32px 16px', color: 'var(--text-dim)' }}>
                          <Database size={48} strokeWidth={1} style={{ marginBottom: '16px', color: '#a78bfa', opacity: 0.5 }} />
                          <p style={{ margin: '0 0 8px', fontSize: '0.95rem', fontWeight: 600, color: 'var(--text-main)' }}>Ask about your data</p>
                          <p style={{ margin: '0 0 20px', fontSize: '0.82rem' }}>I can filter, sort, aggregate, and analyze the selected tables.</p>
                          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                            {[
                              `Show top 10 rows by ${exploredData?.columns?.[1]?.name || 'value'}`,
                              `How many unique records are there?`,
                              `Group by ${exploredData?.columns?.[0]?.name || 'category'} and count`
                            ].map((suggestion, i) => (
                              <button
                                key={i}
                                onClick={() => handleDataChatSend(null, suggestion)}
                                style={{
                                  padding: '10px 14px', borderRadius: '10px', fontSize: '0.8rem',
                                  background: 'var(--bg-tertiary)', border: '1px solid var(--glass-border)',
                                  color: 'var(--text-main)', cursor: 'pointer', textAlign: 'left',
                                  transition: 'all 0.2s', fontWeight: 500
                                }}
                                onMouseOver={e => e.currentTarget.style.borderColor = 'rgba(139,92,246,0.4)'}
                                onMouseOut={e => e.currentTarget.style.borderColor = 'var(--glass-border)'}
                              >
                                <span style={{ color: '#a78bfa', marginRight: '6px' }}>→</span> {suggestion}
                              </button>
                            ))}
                          </div>
                        </div>
                      ) : (
                        dataChatMessages.map((msg, idx) => (
                          <div key={idx} style={{
                            display: 'flex', flexDirection: 'column',
                            alignItems: msg.role === 'user' ? 'flex-end' : 'flex-start',
                            gap: '4px'
                          }}>
                            <div style={{
                              maxWidth: '90%', padding: '12px 16px', borderRadius: '14px',
                              fontSize: '0.85rem', lineHeight: '1.5',
                              background: msg.role === 'user'
                                ? 'linear-gradient(135deg, #8b5cf6, #7c3aed)'
                                : 'var(--bg-tertiary)',
                              color: msg.role === 'user' ? 'white' : 'var(--text-main)',
                              border: msg.role === 'user' ? 'none' : '1px solid var(--glass-border)'
                            }}>
                              {msg.content}
                              {msg.rowCount !== undefined && (
                                <div style={{ fontSize: '0.75rem', marginTop: '6px', opacity: 0.7 }}>
                                  {msg.rowCount} row{msg.rowCount !== 1 ? 's' : ''} returned
                                </div>
                              )}
                            </div>
                            {msg.suggestedFollowUps && msg.suggestedFollowUps.length > 0 && (
                              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', marginTop: '4px', maxWidth: '90%' }}>
                                {msg.suggestedFollowUps.map((q, qi) => (
                                  <button
                                    key={qi}
                                    onClick={() => handleDataChatSend(null, q)}
                                    style={{
                                      padding: '5px 10px', borderRadius: '8px', fontSize: '0.72rem',
                                      background: 'rgba(139,92,246,0.1)', border: '1px solid rgba(139,92,246,0.25)',
                                      color: '#a78bfa', cursor: 'pointer', fontWeight: 500
                                    }}
                                  >
                                    {q}
                                  </button>
                                ))}
                              </div>
                            )}
                          </div>
                        ))
                      )}
                      {isDataChatLoading && (
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '12px 16px', color: 'var(--text-dim)', fontSize: '0.85rem' }}>
                          <Loader size={16} className="spinning" />
                          Analyzing your data...
                        </div>
                      )}
                    </div>

                    <div style={{
                      padding: '16px', borderTop: '1px solid var(--glass-border)'
                    }}>
                      <form onSubmit={handleDataChatSend} style={{ display: 'flex', gap: '8px' }}>
                        <input
                          type="text"
                          placeholder="Ask about your data..."
                          value={dataChatMessage}
                          onChange={(e) => setDataChatMessage(e.target.value)}
                          style={{
                            flex: 1, padding: '10px 14px', borderRadius: '10px', fontSize: '0.85rem',
                            background: 'var(--bg-tertiary)', border: '1px solid var(--glass-border)',
                            color: 'var(--text-main)', outline: 'none'
                          }}
                        />
                        <button
                          type="submit"
                          disabled={!dataChatMessage.trim() || isDataChatLoading}
                          style={{
                            width: '40px', height: '40px', borderRadius: '10px',
                            background: dataChatMessage.trim() ? 'var(--accent-primary)' : 'var(--bg-tertiary)',
                            border: 'none', color: 'white', cursor: dataChatMessage.trim() ? 'pointer' : 'default',
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                            transition: 'all 0.2s'
                          }}
                        >
                          <Send size={16} />
                        </button>
                      </form>
                    </div>
                  </aside>
                )}

                {/* AI Chat Toggle Button - floating */}
                {selectedTables.length > 0 && !dataChatOpen && (
                  <button
                    onClick={() => setDataChatOpen(true)}
                    title="Ask AI about your data"
                    style={{
                      position: 'fixed', bottom: '80px', right: '32px', zIndex: 100,
                      width: '56px', height: '56px', borderRadius: '50%',
                      background: 'linear-gradient(135deg, #8b5cf6, #7c3aed)',
                      border: 'none', color: 'white', cursor: 'pointer',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      boxShadow: '0 4px 20px rgba(139,92,246,0.4)',
                      transition: 'all 0.3s ease'
                    }}
                    onMouseOver={e => { e.currentTarget.style.transform = 'scale(1.1)'; e.currentTarget.style.boxShadow = '0 6px 28px rgba(139,92,246,0.5)'; }}
                    onMouseOut={e => { e.currentTarget.style.transform = 'scale(1)'; e.currentTarget.style.boxShadow = '0 4px 20px rgba(139,92,246,0.4)'; }}
                  >
                    <MessageCircle size={24} />
                  </button>
                )}
              </div>
            )
          }

          {/* Chat & Charts Tab */}
          {
            !isViewer && activeTab === 'chat' && (
              <div className={`chat-layout-premium ${currentChart ? '' : messages.length > 0 ? 'chat-has-messages' : 'chat-centered'}`}>
                {/* Left Panel - Chart Workspace or Empty Workspace */}
                {(currentChart || messages.length > 0) && (
                  <div className={currentChart ? "chart-workspace-premium" : "chat-workspace-empty"}>
                    {/* Back to Inventory - ALWAYS visible */}
                    <button
                      className="chat-back-btn"
                      onClick={() => setActiveTab('inventory')}
                    >
                      <ChevronLeft size={18} />
                      <span>Back to Inventory</span>
                    </button>

                    {currentChart ? (
                      <div className="active-chart-view">
                        <header className="chart-view-header">
                          <div className="header-info">
                            <h2 title={chartStyle.title || currentChart.title}>{chartStyle.title || currentChart.title}</h2>
                            <p>Interactive Data Visualization</p>
                          </div>
                          <div className="header-actions">
                            <button className="btn-action-premium" onClick={() => setShowStyleEditor(true)}>
                              <Palette size={16} />
                              <span>Style</span>
                            </button>
                            <button className="btn-action-premium" onClick={() => {
                              setViewDataContent(currentChart);
                              setShowViewDataModal(true);
                            }}>
                              <Table size={16} />
                              <span>View Data</span>
                            </button>
                            <button className="btn-action-premium primary" onClick={saveChart}>
                              <Save size={16} />
                              <span>Save</span>
                            </button>

                            <div
                              onClick={handleDownloadImage}
                              title="Download Image"
                              className="btn-action-premium"
                              style={{ width: '36px', padding: 0, justifyContent: 'center' }}
                            >
                              <Download size={16} />
                            </div>

                            <div
                              onClick={handleCopyImage}
                              title="Copy Image"
                              className="btn-action-premium"
                              style={{ width: '36px', padding: 0, justifyContent: 'center' }}
                            >
                              <Copy size={16} />
                            </div>

                            <button className="btn-action-premium danger" onClick={() => setCurrentChart(null)}>
                              <X size={16} />
                            </button>
                          </div>
                        </header>
                        <div className="chart-display-area" style={{ position: 'relative' }}>
                          <div className="chart-display-inner" style={{ zIndex: 1 }}>
                            {renderChart(currentChart, chartStyle, { ref: chartRef })}
                          </div>

                          <div style={{ position: 'absolute', top: 16, right: 16, zIndex: 10 }}>
                            <button
                              className="btn-action-premium"
                              onClick={(e) => {
                                e.stopPropagation();
                                if (currentChart) toggleDataLabel(currentChart.id || 'unsaved');
                              }}
                              title={(currentChart && dataLabelsMap[currentChart.id || 'unsaved']) ? "Hide Data Labels" : "Show Data Labels"}
                              style={{ padding: '6px 12px', fontSize: '0.85rem' }}
                            >
                              <span style={{ color: 'var(--text-dim)' }}>Data Labels</span>
                              {(currentChart && dataLabelsMap[currentChart.id || 'unsaved']) ? (
                                <ToggleRight size={20} style={{ color: 'var(--accent-primary)' }} />
                              ) : (
                                <ToggleLeft size={20} style={{ color: 'var(--text-dim)' }} />
                              )}
                            </button>
                          </div>
                        </div>
                      </div>
                    ) : (
                      /* Empty workspace placeholder when chatting but no chart yet */
                      <div className="chat-empty-placeholder">
                        <BarChart3 size={80} strokeWidth={1} />
                        <h2>Your chart will appear here</h2>
                        <p>Ask the AI assistant to create a visualization from your data</p>
                      </div>
                    )}
                  </div>
                )}

                {/* AI Assistant */}
                <aside className="ai-assistant-sidebar">
                  <header className="assistant-header">
                    <div className="assistant-title">
                      <h3>AI Assistant</h3>
                    </div>
                    {!currentChart && messages.length === 0 && (
                      <button
                        onClick={() => setActiveTab('inventory')}
                        style={{
                          display: 'flex', alignItems: 'center', gap: '6px',
                          color: 'var(--text-dim)', fontSize: '0.85rem', fontWeight: 600,
                          padding: '6px 12px', borderRadius: '8px', background: 'var(--hover-bg)',
                          border: '1px solid var(--glass-border)', transition: 'all 0.2s', cursor: 'pointer'
                        }}
                      >
                        <ChevronLeft size={16} />
                        Back to Inventory
                      </button>
                    )}
                  </header>

                  <div className="assistant-content">
                    <div className="chat-viewport custom-scrollbar" ref={chatViewportRef}>
                      {messages.length === 0 ? (
                        <>
                          <div className="assistant-welcome-bubble">
                            <h2 style={{ fontSize: '1.5rem', fontWeight: 800, marginBottom: '8px', color: 'var(--text-main)', marginTop: '20px' }}>Create Chart</h2>
                            <p style={{ color: 'var(--text-dim)', fontSize: '0.9rem', maxWidth: '400px', margin: '0 auto' }}>
                              Describe the chart you want and AI will generate it for you. Bar, line, pie, scatter and more.
                            </p>
                          </div>

                          <div className="suggestion-stack">
                            {[
                              'Show me sales by region',
                              'Create a line chart for revenue',
                              'Compare product performance'
                            ].map((text, i) => (
                              <button key={i} className="suggestion-pill" onClick={() => setMessage(text)}>
                                <span className="pill-prefix">Try:</span>
                                <span className="pill-text">{text}</span>
                              </button>
                            ))}
                          </div>
                        </>
                      ) : (
                        messages.map((msg, idx) => (
                          <div key={idx} className={`bubble-wrapper ${msg.role === 'user' ? 'user' : 'ai'}`}>
                            <div className="bubble-content">
                              {msg.content}
                              {msg.role === 'assistant' && msg.suggestedFollowUps && msg.suggestedFollowUps.length > 0 && (
                                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', marginTop: '10px' }}>
                                  {msg.suggestedFollowUps.map((suggestion, sIdx) => (
                                    <button
                                      key={sIdx}
                                      onClick={() => setMessage(suggestion)}
                                      style={{
                                        padding: '5px 12px',
                                        borderRadius: '16px',
                                        fontSize: '0.75rem',
                                        background: 'rgba(139, 92, 246, 0.08)',
                                        color: '#7c3aed',
                                        border: '1px solid rgba(139, 92, 246, 0.2)',
                                        cursor: 'pointer',
                                        transition: 'all 0.2s',
                                        fontWeight: 500
                                      }}
                                    >
                                      {suggestion}
                                    </button>
                                  ))}
                                </div>
                              )}
                            </div>
                          </div>
                        ))
                      )}
                    </div>

                    <div className="assistant-footer">
                      <div className="premium-input-container">
                        <input
                          type="text"
                          placeholder="Ask anything about your data..."
                          value={message}
                          onChange={(e) => setMessage(e.target.value)}
                          onKeyDown={(e) => e.key === 'Enter' && handleSendMessage(e)}
                        />
                        <button
                          className="premium-send-btn"
                          disabled={!message.trim() || isLoading}
                          onClick={handleSendMessage}
                        >
                          <Send size={18} />
                        </button>
                      </div>
                    </div>
                  </div>
                </aside>
              </div>
            )
          }


          {/* Charts Inventory Tab */}
          {
            activeTab === 'inventory' && (
              <div className="inventory-view-premium">
                <header className="inventory-header-premium">
                  <h1>Charts</h1>
                  <div className="inventory-sub-header">
                    <div className="inventory-controls-row">
                      <div className="filter-dropdown-premium purple" style={{ position: 'relative', padding: 0, minWidth: '160px' }}>
                        <select
                          value={inventoryFilterType}
                          onChange={(e) => setInventoryFilterType(e.target.value)}
                          style={{
                            background: 'transparent', border: 'none', color: 'inherit',
                            width: '100%', height: '100%', padding: '8px 12px',
                            cursor: 'pointer', appearance: 'none', outline: 'none',
                            fontWeight: 500
                          }}
                        >
                          <option value="all">All Types</option>
                          <optgroup label="Bar Charts">
                            <option value="bar">Bar Chart</option>
                            <option value="horizontalBar">Horizontal Bar</option>
                            <option value="stackedBar">Stacked Bar</option>
                            <option value="groupedBar">Grouped Bar</option>
                          </optgroup>
                          <optgroup label="Line Charts">
                            <option value="line">Line Chart</option>
                            <option value="multiLine">Multi-Line</option>
                            <option value="area">Area Chart</option>
                            <option value="stackedArea">Stacked Area</option>
                          </optgroup>
                          <optgroup label="Circular Charts">
                            <option value="pie">Pie Chart</option>
                            <option value="donut">Donut Chart</option>
                            <option value="polarArea">Polar Area</option>
                          </optgroup>
                          <optgroup label="Advanced Charts">
                            <option value="combo">Combo (Bar + Line)</option>
                            <option value="scatter">Scatter Plot</option>
                            <option value="bubble">Bubble Chart</option>
                            <option value="radar">Radar Chart</option>
                          </optgroup>
                        </select>
                        <ChevronDown size={14} style={{ position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)', pointerEvents: 'none' }} />
                      </div>


                      <div className="inventory-search-premium">
                        <Search size={16} />
                        <input
                          type="text"
                          placeholder="Search charts..."
                          value={inventorySearch}
                          onChange={(e) => setInventorySearch(e.target.value)}
                        />
                      </div>
                    </div>

                    <div style={{ display: 'flex', alignItems: 'center', gap: '20px' }}>
                      {/* My / Shared Tabs */}
                      <div style={{
                        display: 'flex', background: 'var(--bg-tertiary)', borderRadius: '12px',
                        padding: '4px', border: '1px solid var(--glass-border)'
                      }}>
                        {!isViewer && (
                          <button
                            onClick={() => { setChartViewTab('my'); loadCharts('my'); }}
                            style={{
                              padding: '8px 20px', borderRadius: '10px', border: 'none', cursor: 'pointer',
                              fontWeight: 600, fontSize: '0.85rem', transition: 'all 0.2s ease',
                              background: chartViewTab === 'my' ? 'var(--accent-primary)' : 'transparent',
                              color: chartViewTab === 'my' ? 'white' : 'var(--text-dim)'
                            }}
                          >
                            My Charts
                          </button>
                        )}
                        <button
                          onClick={() => { setChartViewTab('shared'); loadCharts('shared'); }}
                          style={{
                            padding: '8px 20px', borderRadius: '10px', border: 'none', cursor: 'pointer',
                            fontWeight: 600, fontSize: '0.85rem', transition: 'all 0.2s ease',
                            background: chartViewTab === 'shared' ? 'var(--accent-primary)' : 'transparent',
                            color: chartViewTab === 'shared' ? 'white' : 'var(--text-dim)',
                            display: 'flex', alignItems: 'center', gap: '6px'
                          }}
                        >
                          <Share2 size={14} /> Shared with me
                          {sharedCharts.length > 0 && (
                            <span style={{
                              background: 'rgba(255,255,255,0.2)', borderRadius: '20px',
                              padding: '1px 8px', fontSize: '0.75rem', fontWeight: 700
                            }}>
                              {sharedCharts.length}
                            </span>
                          )}
                        </button>
                      </div>

                      {!isViewer && (
                        <button
                          className="btn-create-premium"
                          onClick={() => {
                            setCurrentChart(null);
                            setMessages([]);
                            setActiveTab('chat');
                          }}
                          style={{
                            display: 'flex', alignItems: 'center', gap: '8px',
                            padding: '0 20px', height: '40px', borderRadius: '12px',
                            background: 'var(--accent-primary)', color: 'white',
                            border: 'none', cursor: 'pointer', fontWeight: '600',
                            fontSize: '0.9rem', boxShadow: '0 4px 15px rgba(139, 92, 246, 0.3)',
                            transition: 'all 0.2s ease', whiteSpace: 'nowrap'
                          }}
                        >
                          <Plus size={18} />
                          <span>Create Chart</span>
                        </button>
                      )}
                    </div>
                  </div>
                </header>

                {(chartViewTab === 'shared' ? sharedCharts : savedCharts).length > 0 ? (
                  <div className="inventory-grid-premium">
                    {(chartViewTab === 'shared' ? sharedCharts : savedCharts)
                      .filter(chart => {
                        const matchesSearch = (chart.title || '').toLowerCase().includes(inventorySearch.toLowerCase());
                        const matchesType = inventoryFilterType === 'all' || chart.chart_type === inventoryFilterType;
                        return matchesSearch && matchesType;
                      })
                      .sort((a, b) => {
                        if (inventorySortOrder === 'newest') return new Date(b.created_at) - new Date(a.created_at);
                        if (inventorySortOrder === 'oldest') return new Date(a.created_at) - new Date(b.created_at);
                        if (inventorySortOrder === 'az') return (a.title || '').localeCompare(b.title || '');
                        if (inventorySortOrder === 'za') return (b.title || '').localeCompare(a.title || '');
                        return 0;
                      })
                      .map((chart, idx) => {
                        const typeColors = {
                          // Bar Charts (Purple)
                          bar: '#8b5cf6',
                          horizontalBar: '#8b5cf6',
                          stackedBar: '#7c3aed',
                          groupedBar: '#6d28d9',

                          // Line Charts (Cyan/Blue)
                          line: '#06b6d4',
                          multiLine: '#0891b2',
                          area: '#10b981',
                          stackedArea: '#059669',

                          // Circular Charts (Amber/Orange)
                          pie: '#f59e0b',
                          donut: '#d97706',
                          polarArea: '#ea580c',

                          // Advanced Charts (Pink/Rose/Teal)
                          combo: '#ec4899',
                          scatter: '#14b8a6',
                          bubble: '#f43f5e',
                          radar: '#a855f7'
                        };
                        const borderColor = typeColors[chart.chart_type] || '#8b5cf6';

                        return (
                          <div
                            key={chart.id}
                            className="inventory-card-premium"
                            style={{ '--card-accent': borderColor, cursor: 'pointer' }}
                            onClick={() => {
                              setViewOnlyChart(chart);
                            }}
                          >
                            <div className="card-header-premium">
                              <h3>{chart.title}</h3>
                              {chart.is_shared && (
                                <span style={{
                                  background: 'rgba(6, 182, 212, 0.15)', color: '#06b6d4',
                                  padding: '2px 8px', borderRadius: '20px', fontSize: '0.65rem',
                                  fontWeight: 600, marginLeft: '8px'
                                }}>
                                  Shared by {chart.owner_name || 'Unknown'}
                                </span>
                              )}
                            </div>

                            <div className="card-body-premium">
                              <div className="chart-preview-wrapper" style={{ pointerEvents: 'none' }}>
                                {chart.data && chart.data.length > 0 ? (
                                  renderChart({
                                    data: chart.data,
                                    dataMapping: chart.data_mapping,
                                    id: chart.id
                                  }, {
                                    type: chart.chart_type,
                                    title: '',
                                    colors: chart.colors || [borderColor, '#ffffff']
                                  })
                                ) : (
                                  <div className="no-data-preview" style={{ textAlign: 'center', padding: '10px' }}>
                                    <BarChart3 size={32} opacity={0.3} style={{ marginBottom: '8px' }} />
                                    {chart.dataError ? (
                                      <p style={{ fontSize: '0.75rem', color: '#ef4444', margin: 0 }}>
                                        Data linked to a deleted table or unavailable
                                      </p>
                                    ) : (
                                      <p style={{ fontSize: '0.75rem', color: 'var(--text-dim)', margin: 0 }}>
                                        No preview available
                                      </p>
                                    )}
                                  </div>
                                )}
                              </div>
                            </div>

                            <div className="card-footer-premium">
                              <div className="footer-left" style={{ flex: 1, minWidth: 0, overflow: 'hidden', paddingRight: '8px' }}>
                                <span
                                  className="type-badge-premium"
                                  title={(chart.chart_type || 'CHART').toUpperCase()}
                                  style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: '100%', display: 'inline-block' }}
                                >
                                  {(chart.chart_type || 'CHART').toUpperCase()}
                                </span>
                                <span className="date-text-premium" style={{ display: 'block', whiteSpace: 'nowrap' }}>
                                  {chart.created_at ? new Date(chart.created_at).toLocaleDateString() : 'No date'}
                                </span>
                              </div>
                              <div className="footer-right" style={{ display: 'flex', gap: '4px', alignItems: 'center', flexShrink: 0, overflow: 'visible', paddingBottom: '4px' }}>
                                {/* Toggle Data Labels Button */}
                                <button
                                  className="inventory-btn-square"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    toggleDataLabel(chart.id);
                                  }}
                                  title={dataLabelsMap[chart.id] ? "Hide Data Labels" : "Show Data Labels"}
                                  style={{ width: '40px', height: '30px', padding: '0 4px', borderRadius: '8px' }}
                                >
                                  {dataLabelsMap[chart.id] ? <ToggleRight size={22} className="text-accent-primary toggle-icon" style={{ color: 'inherit' }} /> : <ToggleLeft size={22} style={{ color: 'inherit' }} />}
                                </button>

                                <button
                                  className="inventory-btn-square"
                                  onClick={(e) => {
                                    handleCopyImage(e, chart);
                                  }}
                                  title="Copy Chart Image"
                                  style={{ width: '32px', height: '32px' }}
                                >
                                  <Copy size={14} />
                                </button>
                                <button
                                  className="inventory-btn-square"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setCurrentChart(chart);
                                    setChartStyle({
                                      type: chart.chart_type || 'bar',
                                      title: chart.title || '',
                                      colors: chart.colors || colorPresets.professional,
                                      theme: 'professional',
                                      individualColors: {},
                                      highlightIndices: []
                                    });
                                    setMessages(chart.messages || []);
                                    setActiveTab('chat');
                                  }}
                                  title="Open in Editor"
                                  style={{ width: '32px', height: '32px' }}
                                >
                                  <Edit3 size={14} />
                                </button>
                                <button
                                  className="inventory-btn-square"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setViewDataContent(chart);
                                    setShowViewDataModal(true);
                                  }}
                                  title="View Data Source"
                                  style={{ width: '32px', height: '32px' }}
                                >
                                  <Table size={14} />
                                </button>
                                <button
                                  className="inventory-btn-square"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setSelectedInventoryChart(chart);
                                    setShowAddFromInventoryModal(true);
                                  }}
                                  title="Add to Dashboard"
                                  style={{ width: '32px', height: '32px' }}
                                >
                                  <Plus size={14} />
                                </button>
                                <button
                                  className="inventory-btn-square"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    openShareModal('chart', chart);
                                  }}
                                  title="Share Chart"
                                  style={{ width: '32px', height: '32px' }}
                                >
                                  <Share2 size={14} />
                                </button>
                                {!chart.is_shared && (
                                  <button
                                    className="inventory-btn-square danger"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      deleteChart(chart.id);
                                    }}
                                    title="Delete Chart"
                                    style={{
                                      width: '32px', height: '32px',
                                      background: 'rgba(239, 68, 68, 0.1)',
                                      border: '1px solid rgba(239, 68, 68, 0.2)',
                                      color: '#ef4444'
                                    }}
                                  >
                                    <Trash2 size={14} />
                                  </button>
                                )}
                              </div>
                            </div>
                          </div>
                        );
                      })}
                  </div>
                ) : (
                  <div className="empty-state-premium">
                    {chartViewTab === 'shared' ? (
                      <>
                        <Share2 size={80} strokeWidth={1} />
                        <h2>No Shared Charts</h2>
                        <p>Charts shared with you by other users will appear here.</p>
                      </>
                    ) : (
                      <>
                        <BarChart3 size={80} strokeWidth={1} />
                        <h2>No Charts Saved Yet</h2>
                        <p>Create and save charts from the Chat &amp; Charts tab to see them here.</p>
                        <button className="btn-primary-premium" onClick={() => setActiveTab('chat')}>
                          Go to Chat &amp; Charts
                        </button>
                      </>
                    )}
                  </div>
                )}
              </div>
            )
          }
          {
            activeTab === 'settings' && (
              <AdminPanel onClose={() => setActiveTab('dashboard')} isFullPage={true} />
            )
          }

          <StyleChartModal
            isOpen={showStyleEditor}
            onClose={() => setShowStyleEditor(false)}
            config={chartStyle}
            onChange={(key, value) => {
              if (key === 'theme') {
                setChartStyle(prev => ({
                  ...prev,
                  theme: value,
                  colors: colorPresets[value],
                  individualColors: {}
                }));
              } else {
                setChartStyle(prev => ({ ...prev, [key]: value }));
              }
            }}
            colorPresets={colorPresets}
          />

          {/* AI Insights Summary Modal */}
          {
            showSummaryModal && (
              <div className="style-modal-overlay">
                <div className="style-modal" style={{ maxWidth: '700px' }}>
                  <div className="style-modal-header">
                    <div className="header-title-group">
                      <div className="icon-box-sm">
                        <Sparkles size={20} />
                      </div>
                      <div>
                        <h2 style={{ fontSize: '1.25rem', fontWeight: 700, margin: 0 }}>AI Executive Summary</h2>
                        <p style={{ margin: 0, fontSize: '0.85rem', color: 'var(--text-dim)' }}>
                          Analysis for {selectedDashboard?.title}
                        </p>
                      </div>
                    </div>
                    <button className="btn-close-modal" onClick={() => setShowSummaryModal(false)}>
                      <X size={20} />
                    </button>
                  </div>

                  <div className="style-modal-content" style={{ maxHeight: '70vh', overflowY: 'auto' }}>
                    {isGeneratingSummary ? (
                      <div className="loading-state" style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '40px' }}>
                        <div className="loading-spinner"></div>
                        <p style={{ marginTop: '20px', color: 'var(--text-dim)' }}>Generating insights from your data...</p>
                      </div>
                    ) : dashboardSummary ? (
                      <div className="summary-content" style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>

                        {/* Key Insights */}
                        <div className="summary-section">
                          <h3 style={{ fontSize: '1rem', fontWeight: 700, color: '#f43f5e', marginBottom: '12px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <Lightbulb size={20} className="summary-icon pulse" />
                            Key Insights
                          </h3>
                          <ul className="insights-list" style={{ paddingLeft: '0', listStyle: 'none', display: 'flex', flexDirection: 'column', gap: '12px' }}>
                            {dashboardSummary.keyInsights.map((insight, idx) => (
                              <li key={idx} style={{
                                background: 'var(--bg-tertiary)',
                                padding: '12px 16px',
                                borderRadius: '12px',
                                borderLeft: '4px solid #f43f5e',
                                fontSize: '0.95rem',
                                lineHeight: '1.5',
                                display: 'flex',
                                gap: '12px'
                              }}>
                                <span style={{ color: '#f43f5e', fontWeight: 'bold' }}>→</span>
                                {insight}
                              </li>
                            ))}
                          </ul>
                        </div>

                        {/* Highlights Grid */}
                        <div className="summary-section">
                          <h3 style={{ fontSize: '1rem', fontWeight: 700, color: '#8b5cf6', marginBottom: '12px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <BarChart3 size={20} />
                            Highlights
                          </h3>
                          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: '16px' }}>
                            {Object.entries(Array.isArray(dashboardSummary.highlights) ? {} : dashboardSummary.highlights).map(([key, value], idx) => (
                              <div key={idx} className="highlight-card-premium" style={{
                                background: 'linear-gradient(145deg, var(--bg-tertiary), var(--bg-secondary))',
                                padding: '20px',
                                borderRadius: '16px',
                                border: '1px solid var(--glass-border)',
                                boxShadow: '0 4px 12px rgba(0,0,0,0.05)'
                              }}>
                                <div style={{
                                  fontSize: '0.75rem',
                                  color: '#8b5cf6',
                                  textTransform: 'uppercase',
                                  letterSpacing: '0.5px',
                                  fontWeight: 600,
                                  marginBottom: '8px'
                                }}>
                                  {key}
                                </div>
                                <div style={{
                                  fontSize: '1.1rem',
                                  fontWeight: 500,
                                  color: 'var(--text-main)',
                                  lineHeight: '1.4'
                                }}>
                                  {value}
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>

                        {/* Recommendations */}
                        <div className="summary-section">
                          <h3 style={{ fontSize: '1rem', fontWeight: 700, color: '#f59e0b', marginBottom: '12px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <Lightbulb size={20} />
                            Recommendations
                          </h3>
                          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                            {dashboardSummary.recommendations.map((rec, idx) => (
                              <div key={idx} style={{
                                display: 'flex',
                                gap: '16px',
                                alignItems: 'flex-start',
                                background: 'rgba(245, 158, 11, 0.05)',
                                padding: '16px',
                                borderRadius: '12px',
                                border: '1px solid rgba(245, 158, 11, 0.1)'
                              }}>
                                <div style={{
                                  color: '#f59e0b',
                                  fontSize: '1.2rem',
                                  fontWeight: '800',
                                  lineHeight: '1'
                                }}>
                                  {idx + 1}.
                                </div>
                                <div style={{ fontSize: '0.95rem', color: 'var(--text-main)', lineHeight: '1.5' }}>{rec}</div>
                              </div>
                            ))}
                          </div>
                        </div>

                        {/* Executive Summary Narrative - NOW AT BOTTOM */}
                        <div className="summary-section">
                          <div style={{
                            background: 'linear-gradient(to right, rgba(139, 92, 246, 0.1), rgba(6, 182, 212, 0.1))',
                            padding: '24px',
                            borderRadius: '16px',
                            border: '1px solid var(--glass-border)',
                            color: 'var(--text-main)',
                            fontSize: '1rem',
                            lineHeight: '1.7',
                            position: 'relative'
                          }}>
                            <h3 style={{
                              fontSize: '0.9rem',
                              fontWeight: 700,
                              color: 'var(--text-dim)',
                              textTransform: 'uppercase',
                              marginBottom: '12px',
                              display: 'flex', alignItems: 'center', gap: '8px'
                            }}>
                              <FileText size={16} />
                              Executive Summary
                            </h3>
                            {dashboardSummary.executiveSummary}
                          </div>
                        </div>


                      </div>
                    ) : (
                      <div className="error-state">
                        <p>Failed to generate summary. Please try again.</p>
                      </div>
                    )}
                  </div>

                  <div className="style-modal-footer" style={{ padding: '20px', borderTop: '1px solid var(--glass-border)', display: 'flex', justifyContent: 'flex-end', gap: '12px' }}>
                    <button className="btn-secondary" onClick={() => setShowSummaryModal(false)} style={{
                      background: 'transparent',
                      border: '1px solid var(--glass-border)',
                      color: 'var(--text-dim)',
                      padding: '10px 20px',
                      borderRadius: '10px',
                      cursor: 'pointer',
                      fontWeight: 600
                    }}>
                      Close
                    </button>
                    {dashboardSummary && (
                      <button className="btn-primary" onClick={downloadSummaryAsPDF} style={{
                        background: 'var(--accent-primary)',
                        border: 'none',
                        color: 'white',
                        padding: '10px 20px',
                        borderRadius: '10px',
                        cursor: 'pointer',
                        fontWeight: 600,
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px'
                      }}>
                        <Download size={16} />
                        Download Report
                      </button>
                    )}
                  </div>
                </div>
              </div>
            )
          }

          {/* New Dashboard Modal */}
          {
            showDashboardModal && (
              <div className="style-modal-overlay">
                <div className="style-modal-content" style={{ maxWidth: '500px' }}>
                  <div className="style-modal-header">
                    <h3>Create New Dashboard</h3>
                    <button onClick={() => setShowDashboardModal(false)} className="close-btn">
                      <X size={20} />
                    </button>
                  </div>
                  <div className="style-modal-body">
                    <div className="form-group">
                      <label>Dashboard Title</label>
                      <input
                        type="text"
                        className="premium-input"
                        placeholder="e.g., Q3 Sales Report"
                        value={newDashboardTitle}
                        onChange={(e) => setNewDashboardTitle(e.target.value)}
                      />
                    </div>
                    <div className="form-group">
                      <label>Description (Optional)</label>
                      <textarea
                        className="premium-input"
                        placeholder="What is this dashboard about?"
                        rows={3}
                        value={newDashboardDesc}
                        onChange={(e) => setNewDashboardDesc(e.target.value)}
                      />
                    </div>
                    {user?.workspaces?.length > 0 && (
                      <div className="form-group">
                        <label>Target Workspace</label>
                        <select
                          className="premium-input"
                          value={targetWorkspaceId}
                          onChange={(e) => setTargetWorkspaceId(e.target.value)}
                          style={{ appearance: 'auto', background: 'var(--bg-main)' }}
                        >
                          <option value="">Personal (No Workspace)</option>
                          {user.workspaces.map((ws, idx) => (
                            <option key={ws.id || idx} value={ws.id}>{ws.name}</option>
                          ))}
                        </select>
                      </div>
                    )}
                  </div>
                  <div className="style-modal-footer">
                    <button className="btn-secondary" onClick={() => setShowDashboardModal(false)}>Cancel</button>
                    <button className="btn-primary" onClick={createDashboard} disabled={!newDashboardTitle.trim()}>
                      Create Dashboard
                    </button>
                  </div>
                </div>
              </div>
            )
          }

          {/* Reviewer Selection Modal */}
          {
            showReviewerSelectModal && (
              <div className="style-modal-overlay">
                <div className="style-modal-content" style={{ maxWidth: '450px' }}>
                  <div className="style-modal-header">
                    <h3>Submit for Review</h3>
                    <button onClick={() => setShowReviewerSelectModal(false)} className="close-btn"><X size={20} /></button>
                  </div>
                  <div className="style-modal-body">
                    <p style={{ marginBottom: '16px', color: 'var(--text-dim)' }}>Select a stakeholder to review this dashboard. They will be notified and granted view access.</p>

                    <div className="form-group">
                      <label>Select Reviewer</label>
                      <select
                        className="premium-input"
                        value={selectedReviewerId}
                        onChange={(e) => setSelectedReviewerId(e.target.value)}
                        style={{ appearance: 'auto', background: 'var(--bg-main)' }}
                      >
                        <option value="">-- Select a Workspace Member --</option>
                        {potentialReviewers.map(u => (
                          <option key={u.id} value={u.id}>
                            {u.name} ({u.role}{u.email ? ` - ${u.email}` : ''})
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                  <div className="style-modal-footer">
                    <button className="btn-secondary" onClick={() => setShowReviewerSelectModal(false)}>Cancel</button>
                    <button
                      className="btn-primary"
                      onClick={() => {
                        if (!selectedReviewerId && !window.confirm("No reviewer selected. Submit directly to Workspace Admins?")) return;
                        confirmReviewSubmission(selectedReviewerId);
                        setShowReviewerSelectModal(false);
                      }}
                    >
                      {selectedReviewerId ? 'Send to Reviewer' : 'Submit to Admins'}
                    </button>
                  </div>
                </div>
              </div>
            )
          }

        </div >
      </main >

      {/* View Only Chart Modal */}
      {
        viewOnlyChart && (
          <div className="view-only-overlay" onClick={() => setViewOnlyChart(null)} style={{
            position: 'fixed', inset: 0, zIndex: 9999,
            background: 'rgba(0, 0, 0, 0.7)', backdropFilter: 'blur(8px)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            animation: 'fadeIn 0.2s ease-out'
          }}>
            <div
              className="view-only-content"
              onClick={e => e.stopPropagation()}
              style={{
                width: '90vw', maxWidth: '1200px', height: '85vh',
                background: 'var(--bg-secondary)', border: '1px solid var(--glass-border)',
                borderRadius: '24px', boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.5)',
                display: 'flex', flexDirection: 'column', overflow: 'hidden',
                animation: 'scaleUp 0.3s cubic-bezier(0.16, 1, 0.3, 1)'
              }}
            >
              {/* Modal Header */}
              <div className="view-only-header" style={{
                padding: '24px 32px', background: 'var(--bg-tertiary)',
                borderBottom: '1px solid var(--glass-border)',
                display: 'flex', justifyContent: 'space-between', alignItems: 'center'
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                  <div style={{
                    width: '40px', height: '40px', borderRadius: '12px',
                    background: 'rgba(139, 92, 246, 0.1)', color: '#8b5cf6',
                    display: 'flex', alignItems: 'center', justifyContent: 'center'
                  }}>
                    <BarChart3 size={20} />
                  </div>
                  <div>
                    {isEditingTitle ? (
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <input
                          type="text"
                          value={tempChartTitle}
                          onChange={(e) => setTempChartTitle(e.target.value)}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') handleUpdateChartTitle();
                            if (e.key === 'Escape') setIsEditingTitle(false);
                          }}
                          autoFocus
                          style={{
                            fontSize: '1.25rem', fontWeight: 700, color: 'var(--text-main)',
                            background: 'var(--bg-primary)', border: '1px solid var(--accent-primary)',
                            borderRadius: '8px', padding: '4px 8px', outline: 'none'
                          }}
                        />
                        <button
                          onClick={handleUpdateChartTitle}
                          className="btn-primary"
                          style={{ padding: '6px 12px', fontSize: '0.8rem' }}
                        >
                          Save
                        </button>
                        <button
                          onClick={() => setIsEditingTitle(false)}
                          className="btn-secondary"
                          style={{ padding: '6px 12px', fontSize: '0.8rem' }}
                        >
                          Cancel
                        </button>
                      </div>
                    ) : (
                      <h2
                        onClick={() => {
                          setTempChartTitle(viewOnlyChart.title);
                          setIsEditingTitle(true);
                        }}
                        style={{
                          fontSize: '1.25rem', fontWeight: 700, margin: 0, color: 'var(--text-main)',
                          cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '8px'
                        }}
                        title="Click to edit title"
                      >
                        {viewOnlyChart.title}
                        <Edit3 size={14} className="edit-icon-hover" style={{ opacity: 0.5 }} />
                      </h2>
                    )}
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginTop: '6px' }}>
                      <p style={{ margin: 0, fontSize: '0.85rem', color: 'var(--text-dim)', fontWeight: 500 }}>
                        {(viewOnlyChart.chart_type || 'CHART').toUpperCase()} • {viewOnlyChart.created_at ? new Date(viewOnlyChart.created_at).toLocaleDateString() : 'No date'}
                      </p>
                    </div>
                  </div>
                </div>

                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                  {/* Toggle Data Labels Button */}
                  <button
                    onClick={() => toggleDataLabel(viewOnlyChart.id)}
                    title={dataLabelsMap[viewOnlyChart.id] ? "Hide Data Labels" : "Show Data Labels"}
                    style={{
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      width: '46px', height: '40px', padding: '0 8px', borderRadius: '12px',
                      background: 'transparent', border: '1px solid var(--glass-border)',
                      color: 'var(--text-dim)', cursor: 'pointer', transition: 'all 0.15s ease'
                    }}
                    onMouseOver={e => {
                      e.currentTarget.style.background = 'var(--bg-tertiary)';
                      e.currentTarget.style.color = 'var(--text-main)';
                    }}
                    onMouseOut={e => {
                      e.currentTarget.style.background = 'transparent';
                      e.currentTarget.style.color = 'var(--text-dim)';
                    }}
                  >
                    {dataLabelsMap[viewOnlyChart.id] ? <ToggleRight size={22} style={{ color: 'var(--accent-primary)' }} /> : <ToggleLeft size={22} style={{ color: 'inherit' }} />}
                  </button>

                  <button
                    onClick={() => {
                      setCurrentChart(viewOnlyChart);
                      setChartStyle({
                        type: viewOnlyChart.chart_type || 'bar',
                        title: viewOnlyChart.title || '',
                        colors: viewOnlyChart.colors || colorPresets.professional,
                        theme: 'professional',
                        individualColors: {},
                        highlightIndices: []
                      });
                      setMessages(viewOnlyChart.messages || []);
                      setViewOnlyChart(null);
                      setActiveTab('chat');
                    }}
                    style={{
                      display: 'flex', alignItems: 'center', gap: '8px',
                      padding: '10px 20px', borderRadius: '12px',
                      background: 'var(--accent-primary)', color: 'white',
                      border: 'none', fontWeight: 600, cursor: 'pointer',
                      transition: 'all 0.2s'
                    }}
                    onMouseOver={e => e.currentTarget.style.transform = 'translateY(-1px)'}
                    onMouseOut={e => e.currentTarget.style.transform = 'translateY(0)'}
                  >
                    <Edit3 size={16} />
                    Open in Editor
                  </button>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      setViewDataContent(viewOnlyChart);
                      setShowViewDataModal(true);
                    }}
                    style={{
                      display: 'flex', alignItems: 'center', gap: '8px',
                      padding: '10px 20px', borderRadius: '12px',
                      background: 'var(--bg-secondary)', color: 'var(--text-main)',
                      border: '1px solid var(--glass-border)', fontWeight: 600,
                      cursor: 'pointer', transition: 'all 0.2s'
                    }}
                    onMouseOver={e => {
                      e.currentTarget.style.background = 'var(--bg-tertiary)';
                      e.currentTarget.style.transform = 'translateY(-1px)';
                    }}
                    onMouseOut={e => {
                      e.currentTarget.style.background = 'var(--bg-secondary)';
                      e.currentTarget.style.transform = 'translateY(0)';
                    }}
                  >
                    <Table size={16} />
                    View Data
                  </button>

                  <div
                    onClick={handleDownloadImage}
                    title="Download Image"
                    style={{
                      width: '40px', height: '40px', borderRadius: '12px',
                      background: 'transparent', border: '1px solid var(--glass-border)',
                      color: 'var(--text-dim)', cursor: 'pointer',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      transition: 'all 0.2s'
                    }}
                    onMouseOver={e => {
                      e.currentTarget.style.background = 'var(--bg-tertiary)';
                      e.currentTarget.style.color = 'var(--text-main)';
                    }}
                    onMouseOut={e => {
                      e.currentTarget.style.background = 'transparent';
                      e.currentTarget.style.color = 'var(--text-dim)';
                    }}
                  >
                    <Download size={20} />
                  </div>

                  <div
                    onClick={handleCopyImage}
                    title="Copy Image"
                    style={{
                      width: '40px', height: '40px', borderRadius: '12px',
                      background: 'transparent', border: '1px solid var(--glass-border)',
                      color: 'var(--text-dim)', cursor: 'pointer',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      transition: 'all 0.2s'
                    }}
                    onMouseOver={e => {
                      e.currentTarget.style.background = 'var(--bg-tertiary)';
                      e.currentTarget.style.color = 'var(--text-main)';
                    }}
                    onMouseOut={e => {
                      e.currentTarget.style.background = 'transparent';
                      e.currentTarget.style.color = 'var(--text-dim)';
                    }}
                  >
                    <Copy size={20} />
                  </div>

                  <div
                    onClick={() => setViewOnlyChart(null)}
                    style={{
                      width: '40px', height: '40px', borderRadius: '12px',
                      background: 'transparent', border: '1px solid var(--glass-border)',
                      color: 'var(--text-dim)', cursor: 'pointer',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      transition: 'all 0.2s'
                    }}
                    onMouseOver={e => {
                      e.currentTarget.style.background = 'rgba(239, 68, 68, 0.1)';
                      e.currentTarget.style.color = '#ef4444';
                      e.currentTarget.style.borderColor = 'rgba(239, 68, 68, 0.2)';
                    }}
                    onMouseOut={e => {
                      e.currentTarget.style.background = 'transparent';
                      e.currentTarget.style.color = 'var(--text-dim)';
                      e.currentTarget.style.borderColor = 'var(--glass-border)';
                    }}
                  >
                    <X size={20} />
                  </div>
                </div>
              </div>

              {/* Modal Body */}
              <div className="view-only-body" style={{ flex: 1, position: 'relative', overflow: 'hidden', padding: '40px' }}>
                <div style={{
                  width: '100%', height: '100%',
                  background: 'var(--bg-primary)',
                  borderRadius: '16px', border: '1px solid var(--glass-border)',
                  overflow: 'hidden', position: 'relative'
                }}>
                  <div className="chart-container" style={{ width: '100%', height: '100%', padding: '20px' }}>
                    {renderChart({
                      data: viewOnlyChart.data,
                      dataMapping: viewOnlyChart.data_mapping,
                      id: viewOnlyChart.id
                    }, {
                      type: viewOnlyChart.chart_type,
                      title: '',
                      colors: viewOnlyChart.colors || ['#8b5cf6', '#06b6d4', '#10b981', '#f59e0b']
                    }, {
                      ref: chartRef
                    })}
                  </div>
                </div>
              </div>
            </div>
          </div>
        )
      }


      {/* Add From Inventory Modal */}
      {
        showAddFromInventoryModal && (
          <div className="style-modal-overlay" onClick={() => setShowAddFromInventoryModal(false)}>
            <div className="style-modal-content" onClick={e => e.stopPropagation()} style={{ maxWidth: '400px', width: '90%' }}>
              <div className="style-modal-header">
                <div className="header-title-group">
                  <div className="icon-box-sm">
                    <Plus size={20} />
                  </div>
                  <div className="header-text">
                    <h3>Add to Dashboard</h3>
                    <p style={{ fontSize: '0.8rem', color: 'var(--text-dim)', margin: '4px 0 0 0' }}>
                      Select a dashboard to add "{selectedInventoryChart?.title}"
                    </p>
                  </div>
                </div>
                <button className="btn-close-modal" onClick={() => setShowAddFromInventoryModal(false)}>
                  <X size={20} />
                </button>
              </div>
              <div className="style-modal-body" style={{ maxHeight: '400px', overflowY: 'auto', padding: '16px' }}>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                  <button
                    className="btn-primary"
                    onClick={() => {
                      setShowAddFromInventoryModal(false);
                      setShowDashboardModal(true);
                    }}
                    style={{ justifyContent: 'center', marginBottom: '8px' }}
                  >
                    <Plus size={18} />
                    Create New Dashboard
                  </button>

                  <div style={{ borderTop: '1px solid var(--glass-border)', margin: '8px 0', paddingTop: '16px' }}>
                    <p style={{ fontSize: '0.75rem', color: 'var(--text-dim)', marginBottom: '12px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                      Existing Dashboards
                    </p>
                    {dashboards.length === 0 ? (
                      <p style={{ textAlign: 'center', color: 'var(--text-dim)', padding: '20px', fontSize: '0.9rem' }}>No dashboards found</p>
                    ) : (
                      dashboards.map(dash => (
                        <div
                          key={dash.id}
                          className="dashboard-select-item"
                          onClick={() => addInventoryChartToDashboard(dash.id)}
                          style={{
                            padding: '12px 16px',
                            background: 'var(--bg-secondary)',
                            border: '1px solid var(--glass-border)',
                            borderRadius: '12px',
                            cursor: 'pointer',
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            marginBottom: '8px',
                            transition: 'all 0.2s'
                          }}
                          onMouseOver={e => {
                            e.currentTarget.style.background = 'var(--bg-tertiary)';
                            e.currentTarget.style.borderColor = 'var(--accent-primary)';
                          }}
                          onMouseOut={e => {
                            e.currentTarget.style.background = 'var(--bg-secondary)';
                            e.currentTarget.style.borderColor = 'var(--glass-border)';
                          }}
                        >
                          <span style={{ fontWeight: 600, color: 'var(--text-main)', fontSize: '0.9rem' }}>{dash.title}</span>
                          <ChevronRight size={16} color="var(--text-dim)" />
                        </div>
                      ))
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        )
      }


      {/* Share Modal */}
      {
        showShareModal && (
          <div className="style-modal-overlay">
            <div className="style-modal-content" style={{ maxWidth: '520px' }}>
              <div className="style-modal-header">
                <h3 style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                  <Share2 size={20} />
                  Share {showShareModal.type === 'chart' ? 'Chart' : 'Dashboard'}
                </h3>
                <button onClick={() => setShowShareModal(null)} className="close-btn">
                  <X size={20} />
                </button>
              </div>
              <div className="style-modal-body" style={{ padding: '24px' }}>
                <p style={{ color: 'var(--text-dim)', marginBottom: '16px', fontSize: '0.9rem' }}>
                  <strong style={{ color: 'var(--text-main)' }}>{showShareModal.item?.title}</strong>
                </p>

                {/* Search Users */}
                <div style={{ marginBottom: '24px' }}>
                  <label style={{ display: 'block', fontSize: '0.85rem', fontWeight: 600, color: 'var(--text-dim)', marginBottom: '8px' }}>
                    Search user by name or email
                  </label>
                  <div style={{ position: 'relative' }}>
                    <input
                      type="text"
                      className="premium-input"
                      placeholder="Type name or email..."
                      value={shareSearchQuery}
                      onChange={(e) => {
                        setShareSearchQuery(e.target.value);
                        searchUsersForSharing(e.target.value);
                      }}
                      style={{ width: '100%', paddingRight: '40px' }}
                    />
                    <Search size={16} style={{ position: 'absolute', right: '14px', top: '50%', transform: 'translateY(-50%)', color: 'var(--text-muted)', pointerEvents: 'none' }} />
                  </div>

                  {/* Search Results Dropdown */}
                  {shareSearchResults.length > 0 && (
                    <div style={{
                      marginTop: '4px',
                      background: 'var(--bg-secondary)',
                      border: '1px solid var(--glass-border)',
                      borderRadius: '12px',
                      maxHeight: '200px',
                      overflowY: 'auto',
                      boxShadow: '0 8px 24px rgba(0,0,0,0.15)'
                    }}>
                      {shareSearchResults.map(u => (
                        <div
                          key={u.id}
                          onClick={() => shareItem(showShareModal.type, showShareModal.item.id, u.id)}
                          style={{
                            display: 'flex', alignItems: 'center', gap: '12px',
                            padding: '12px 16px', cursor: 'pointer',
                            borderBottom: '1px solid var(--glass-border)',
                            transition: 'background 0.15s ease'
                          }}
                          onMouseEnter={e => e.currentTarget.style.background = 'var(--hover-bg)'}
                          onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                        >
                          <div style={{
                            width: '36px', height: '36px', borderRadius: '50%',
                            background: 'linear-gradient(135deg, var(--accent-primary), var(--accent-secondary))',
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                            color: 'white', fontWeight: 700, fontSize: '0.85rem', flexShrink: 0
                          }}>
                            {(u.name || u.email || '?')[0].toUpperCase()}
                          </div>
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <div style={{ fontWeight: 600, color: 'var(--text-main)', fontSize: '0.9rem' }}>{u.name || 'No Name'}</div>
                            <div style={{ fontSize: '0.8rem', color: 'var(--text-muted)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{u.email}</div>
                          </div>
                          <Plus size={16} style={{ color: 'var(--accent-primary)', flexShrink: 0 }} />
                        </div>
                      ))}
                    </div>
                  )}

                  {isShareSearching && (
                    <div style={{ padding: '12px', textAlign: 'center', color: 'var(--text-muted)', fontSize: '0.85rem' }}>
                      Searching...
                    </div>
                  )}
                </div>

                {/* Currently Shared With */}
                <div>
                  <label style={{ display: 'block', fontSize: '0.85rem', fontWeight: 600, color: 'var(--text-dim)', marginBottom: '12px' }}>
                    Shared with ({sharedWithUsers.length})
                  </label>
                  {sharedWithUsers.length === 0 ? (
                    <div style={{
                      textAlign: 'center', padding: '24px',
                      background: 'var(--bg-tertiary)', borderRadius: '12px',
                      color: 'var(--text-muted)', fontSize: '0.85rem'
                    }}>
                      Not shared with anyone yet
                    </div>
                  ) : (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', maxHeight: '250px', overflowY: 'auto' }}>
                      {sharedWithUsers.map(su => (
                        <div key={su.id} style={{
                          display: 'flex', alignItems: 'center', gap: '12px',
                          padding: '10px 14px', background: 'var(--bg-tertiary)',
                          borderRadius: '12px', border: '1px solid var(--glass-border)'
                        }}>
                          <div style={{
                            width: '34px', height: '34px', borderRadius: '50%',
                            background: 'linear-gradient(135deg, #f59e0b, #ef4444)',
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                            color: 'white', fontWeight: 700, fontSize: '0.8rem', flexShrink: 0
                          }}>
                            {(su.name || su.email || '?')[0].toUpperCase()}
                          </div>
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <div style={{ fontWeight: 600, color: 'var(--text-main)', fontSize: '0.85rem' }}>{su.name || 'No Name'}</div>
                            <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>{su.email}</div>
                          </div>
                          <button
                            onClick={() => unshareItem(showShareModal.type, showShareModal.item.id, su.shared_with)}
                            style={{
                              background: 'rgba(239, 68, 68, 0.1)',
                              border: '1px solid rgba(239, 68, 68, 0.2)',
                              color: '#ef4444',
                              borderRadius: '8px',
                              padding: '6px 10px',
                              cursor: 'pointer',
                              fontSize: '0.75rem',
                              fontWeight: 600,
                              display: 'flex', alignItems: 'center', gap: '4px'
                            }}
                          >
                            <X size={12} /> Remove
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
              <div style={{ padding: '16px 24px', borderTop: '1px solid var(--glass-border)', display: 'flex', justifyContent: 'flex-end' }}>
                <button onClick={() => setShowShareModal(null)} style={{
                  background: 'transparent',
                  border: '1px solid var(--glass-border)',
                  color: 'var(--text-dim)',
                  padding: '10px 20px',
                  borderRadius: '10px',
                  cursor: 'pointer',
                  fontWeight: 600
                }}>
                  Done
                </button>
              </div>
            </div>
          </div>
        )
      }

      {/* Data Combine Modal */}
      <DataCombineModal
        isOpen={showCombineModal}
        onClose={() => setShowCombineModal(false)}
        tables={availableTables}
        onSaveDataset={() => loadAvailableTables(true)}
      />

      {/* Data Transform Modal */}
      <DataTransformModal
        isOpen={showTransformModal}
        onClose={() => setShowTransformModal(false)}
        tables={availableTables}
        onSaveDataset={() => loadAvailableTables(true)}
      />

      {/* Data Restructure Modal */}
      <DataRestructureModal
        isOpen={showRestructureModal}
        onClose={() => setShowRestructureModal(false)}
        tables={availableTables}
        onSaveDataset={() => loadAvailableTables(true)}
      />

      {/* View Data Modal */}
      <ViewDataModal
        isOpen={showViewDataModal}
        onClose={() => setShowViewDataModal(false)}
        data={viewDataContent?.data}
        title={viewDataContent?.title}
        sql={viewDataContent?.sql}
      />

      {/* ===== SHARE PASSWORD MODAL ===== */}
      {showPasswordModal && (
        <div
          style={{
            position: 'fixed', inset: 0, zIndex: 9999,
            background: 'rgba(0,0,0,0.65)',
            backdropFilter: 'blur(8px)',
            display: 'flex', alignItems: 'center', justifyContent: 'center'
          }}
          onClick={(e) => { if (e.target === e.currentTarget) setShowPasswordModal(false); }}
        >
          <div style={{
            background: 'linear-gradient(135deg, #1e1b4b 0%, #0f172a 100%)',
            border: '1px solid rgba(99,102,241,0.4)',
            borderRadius: '20px',
            padding: '40px',
            width: '460px',
            maxWidth: '95vw',
            boxShadow: '0 25px 50px rgba(0,0,0,0.5), 0 0 0 1px rgba(255,255,255,0.05)',
            position: 'relative',
            animation: 'fadeInScale 0.25s ease'
          }}>
            {/* Close */}
            <button
              onClick={() => setShowPasswordModal(false)}
              style={{ position: 'absolute', top: 16, right: 16, background: 'rgba(255,255,255,0.06)', border: 'none', color: '#94a3b8', borderRadius: '8px', width: 32, height: 32, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 18 }}
            >×</button>

            {/* Icon + Title */}
            <div style={{ textAlign: 'center', marginBottom: 28 }}>
              <div style={{
                width: 64, height: 64, borderRadius: '50%',
                background: 'linear-gradient(135deg, #10b981, #059669)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                margin: '0 auto 16px',
                boxShadow: '0 0 30px rgba(16,185,129,0.4)'
              }}>
                <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.5"><rect x="3" y="11" width="18" height="11" rx="2" /><path d="M7 11V7a5 5 0 0 1 10 0v4" /></svg>
              </div>
              <h2 style={{ color: '#f1f5f9', fontSize: '1.4rem', fontWeight: 700, margin: 0 }}>
                Dashboard Published!
              </h2>
              <p style={{ color: '#94a3b8', fontSize: '0.9rem', margin: '8px 0 0' }}>
                Share this password with viewers — they'll need it to access the dashboard.
              </p>
            </div>

            {/* Dashboard name */}
            {selectedDashboard && (
              <div style={{ background: 'rgba(255,255,255,0.04)', borderRadius: '10px', padding: '10px 14px', marginBottom: 20, color: '#e2e8f0', fontSize: '0.9rem' }}>
                📊 <strong>{selectedDashboard.title}</strong>
              </div>
            )}

            {/* Password Display */}
            <div style={{ marginBottom: 20 }}>
              <label style={{ color: '#94a3b8', fontSize: '0.78rem', textTransform: 'uppercase', letterSpacing: '0.05em', display: 'block', marginBottom: 8 }}>
                🔑 Share Password
              </label>
              <div style={{
                display: 'flex', alignItems: 'center', gap: 8,
                background: 'rgba(255,255,255,0.06)',
                border: '1px solid rgba(99,102,241,0.4)',
                borderRadius: 12, padding: '12px 16px'
              }}>
                <span style={{
                  flex: 1,
                  fontFamily: 'monospace',
                  fontSize: '1.4rem',
                  fontWeight: 700,
                  letterSpacing: '0.12em',
                  color: '#a5f3fc',
                  userSelect: 'all'
                }}>
                  {dashboardPassword || '————————'}
                </span>
                {/* Copy */}
                <button
                  onClick={copyPasswordToClipboard}
                  title="Copy Password"
                  style={{
                    background: passwordCopied ? 'rgba(16,185,129,0.2)' : 'rgba(255,255,255,0.08)',
                    border: '1px solid ' + (passwordCopied ? '#10b981' : 'rgba(255,255,255,0.12)'),
                    color: passwordCopied ? '#10b981' : '#94a3b8',
                    borderRadius: 8, padding: '6px 12px', cursor: 'pointer',
                    fontSize: '0.8rem', display: 'flex', alignItems: 'center', gap: 4,
                    transition: 'all 0.2s'
                  }}
                >
                  {passwordCopied
                    ? <><svg width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><polyline points="20 6 9 17 4 12" /></svg> Copied!</>
                    : <><svg width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><rect x="9" y="9" width="13" height="13" rx="2" /><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" /></svg> Copy</>
                  }
                </button>
                {/* Regenerate */}
                <button
                  onClick={regenerateDashboardPassword}
                  disabled={isRegeneratingPassword}
                  title="Generate New Password (invalidates old one)"
                  style={{
                    background: 'rgba(239,68,68,0.1)',
                    border: '1px solid rgba(239,68,68,0.3)',
                    color: '#f87171',
                    borderRadius: 8, padding: '6px 12px', cursor: isRegeneratingPassword ? 'not-allowed' : 'pointer',
                    fontSize: '0.8rem', display: 'flex', alignItems: 'center', gap: 4,
                    opacity: isRegeneratingPassword ? 0.6 : 1,
                    transition: 'all 0.2s'
                  }}
                >
                  <svg
                    width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"
                    style={{ animation: isRegeneratingPassword ? 'spin 1s linear infinite' : 'none' }}
                  ><polyline points="23 4 23 10 17 10" /><path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10" /></svg>
                  {isRegeneratingPassword ? 'Generating...' : 'Reset'}
                </button>
              </div>
              <p style={{ color: '#64748b', fontSize: '0.75rem', margin: '8px 0 0' }}>
                ⚠️ Resetting generates a new password — old links will stop working.
              </p>
            </div>

            {/* Share Link */}
            {selectedDashboard?.share_url && (
              <div style={{ marginBottom: 24 }}>
                <label style={{ color: '#94a3b8', fontSize: '0.78rem', textTransform: 'uppercase', letterSpacing: '0.05em', display: 'block', marginBottom: 8 }}>
                  🔗 Public Link
                </label>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <input
                    readOnly
                    value={selectedDashboard.share_url}
                    style={{
                      flex: 1, background: 'rgba(255,255,255,0.04)',
                      border: '1px solid rgba(255,255,255,0.1)',
                      borderRadius: 8, padding: '8px 12px',
                      color: '#94a3b8', fontSize: '0.82rem',
                      fontFamily: 'monospace', overflow: 'hidden', textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap'
                    }}
                    onClick={e => e.target.select()}
                  />
                  <button
                    onClick={() => { navigator.clipboard.writeText(selectedDashboard.share_url); showToast('Link copied!', 'success'); }}
                    style={{
                      background: 'rgba(99,102,241,0.15)',
                      border: '1px solid rgba(99,102,241,0.4)',
                      color: '#818cf8', borderRadius: 8, padding: '8px 14px',
                      cursor: 'pointer', fontSize: '0.8rem', whiteSpace: 'nowrap'
                    }}
                  >Copy Link</button>
                </div>
              </div>
            )}

            {/* Done button */}
            <button
              onClick={() => setShowPasswordModal(false)}
              style={{
                width: '100%',
                padding: '12px',
                background: 'linear-gradient(135deg, #6366f1, #8b5cf6)',
                border: 'none', borderRadius: 12, color: 'white',
                fontWeight: 600, fontSize: '1rem', cursor: 'pointer',
                boxShadow: '0 4px 15px rgba(99,102,241,0.4)',
                transition: 'transform 0.1s'
              }}
              onMouseEnter={e => e.target.style.transform = 'translateY(-1px)'}
              onMouseLeave={e => e.target.style.transform = 'translateY(0)'}
            >
              Done
            </button>
          </div>
        </div>
      )}
      {/* ============================= */}
    </div >
  );
}

// App wrapper with AuthProvider
function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <AppContent />
      </AuthProvider>
    </BrowserRouter>
  );
}

// AppContent - decides whether to show Login, MainApp, or PublicDashboard

function AppContent() { // Renamed to App below
  const { isAuthenticated, loading } = useAuth();

  // Check if this is a public shared dashboard URL
  const location = useLocation();
  const path = location.pathname;
  // Match UUID format (e.g., /shared/550e8400-e29b-41d4-a716-446655440000)
  const sharedMatch = path.match(/^\/shared\/([a-zA-Z0-9-]+)$/);

  let content;

  if (sharedMatch) {
    // Public dashboard view - no auth required
    const dashboardId = sharedMatch[1];
    content = <PublicDashboard dashboardId={dashboardId} />;
  } else if (loading) {
    // Show loading spinner while checking auth
    content = (
      <div className="login-container">
        <div className="login-background">
          <div className="login-gradient"></div>
        </div>
        <div className="loading-spinner" style={{ width: 40, height: 40 }}></div>
      </div>
    );
  } else if (!isAuthenticated) {
    // Show login if not authenticated
    content = <Login />;
  } else {
    // Show main app if authenticated
    content = <MainApp />;
  }

  return (
    <UIProvider>
      {content}
    </UIProvider>
  );
}

export default App;

